import {
  DestinationOption,
  ExportDestinationKey,
  ExportStatus,
  ExportType,
  ExportWritePreference,
  ScheduledType,
} from '@app/shared/types/exports-types';
import {
  AiConfigAgentGet,
  AiConfigAgentKey,
  AiConfigAgentPut,
  AiConfigGetResponse,
  AiConfigGlobalSettingPut,
  AiConfigOrgAgentGet,
  AiConfigPutRequest,
  AiConfigScope,
} from '@shared/types/ai-config.types';
import { ExchangeCodeResponse } from '@shared/types/auth.types';
import {
  AI_CONFIG_AGENT_CATALOG,
  AI_CONFIG_GLOBAL_BANNED_WORDS_INFO,
  AI_CONFIG_GLOBAL_SETTING_CATEGORIES,
  AI_CONFIG_ORG_BANNED_WORDS_INFO,
  AI_CONFIG_TENANT_BANNED_WORDS_INFO,
  cloneAiConfigGlobalSettingCategories,
} from '@shared/constants/ai-config.catalog';
import { userRoles } from '@shared/strings/common.strings';
import {
  ExportDetail,
  ExportDestinationDetail,
  ExportFrequency,
  JobStatus,
  ExportVersionJobsResponse,
} from '@shared/types/exports-manager.types';
import {
  LookalikeJob,
  LookalikeJobStatus,
  LookalikeResults,
  LookalikeSettings,
} from '@shared/types/lookalike-types';

// Helper function to calculate time ago from now
const getTimeAgo = (
  minutes: number = 0,
  hours: number = 0,
  days: number = 0,
  weeks: number = 0,
  months: number = 0,
) => {
  const now = new Date();
  const totalMinutes =
    minutes +
    hours * 60 +
    days * 24 * 60 +
    weeks * 7 * 24 * 60 +
    months * 30 * 24 * 60;
  const targetDate = new Date(now.getTime() - totalMinutes * 60 * 1000);
  return targetDate.toISOString();
};

export const MOCK_EXPORT_DESTINATION_OPTIONS: DestinationOption[] = [
  {
    id: '8acc167d-efce-4755-baf0-54dfb203c4c2',
    key: ExportDestinationKey.GOOGLE_BIGQUERY,
    name: 'Google BigQuery',
    description: 'Serverless data warehouse for analytics',
    selected: false,
    isDisabled: false,
    isTenantDisabled: true,
  },
  {
    id: 'b5c6dbf2-6af6-4e75-9d39-1b326b66f921',
    key: ExportDestinationKey.DSP,
    name: 'Stirista DSP+',
    description: 'Activate audiences into DSP segments',
    selected: false,
    isDisabled: false,
  },
  {
    id: 'e2f3a1b4-5c6d-4e7f-8a9b-0c1d2e3f4a5b',
    key: ExportDestinationKey.META,
    name: 'Meta',
    description: 'Sync audiences to Meta Custom Audiences',
    selected: false,
    isDisabled: false,
  },
  {
    id: 'f7006f90-c95a-4d8a-bf61-f8d885c5271b',
    key: ExportDestinationKey.AMAZON_S3,
    name: 'AmazonS3',
    description: 'Secure cloud storage with global availability',
    selected: false,
    isDisabled: true,
  },
  {
    id: 'd99586eb-dd98-4271-a64b-7df74f76331b',
    key: ExportDestinationKey.GOOGLE_CLOUD_STORAGE,
    name: 'GoogleCloudStorage',
    description: "Google's unified object storage service",
    selected: false,
    isDisabled: true,
  },
];

export const MOCK_AUDIENCES = [
  {
    edges: [
      {
        id: 'node-1c55bb94-5a57-40b2-9871-6c861839e86d -> node-0e455b0b-8bf9-429b-adf9-44058588153f',
        source: 'node-1c55bb94-5a57-40b2-9871-6c861839e86d',
        target: 'node-0e455b0b-8bf9-429b-adf9-44058588153f',
        selectedLogicNode: 'AND',
      },
    ],
    nodes: [
      {
        id: 'node-1c55bb94-5a57-40b2-9871-6c861839e86d',
        meta: {
          validation: {
            required: true,
          },
          default_value: true,
          operators: [],
          values: [
            {
              label: 'Yes',
              value: 'true',
            },
            {
              label: 'No',
              value: 'false',
            },
          ],
        },
        type: 'toggle',
        label: 'Multilingual',

        parentId: null,
        point: {
          x: 0,
          y: 0,
        },
        isDisabled: false,
        attributeId: 10093,
        sourceLabel: 'Stirista Data',
        incomingEdge: '',
        outgoingEdge: 'AND',
        categoryLabel: 'Personal',
        selectedValue: '"true"',
        selectedOperator: '',
      },
      {
        id: 'node-0e455b0b-8bf9-429b-adf9-44058588153f',
        meta: {
          operators: [
            { operator: 'between', value: '<>' },
            { operator: 'not_between', value: '><' },
          ],
          validation: {
            required: true,
          },
          placeholder: 'Enter bounced last 10 days range...',
        },
        type: 'range',
        label: 'Bounced Last 10 Days',
        parentId: null,
        point: {
          x: 580,
          y: 0,
        },
        isDisabled: false,
        attributeId: 20486,
        sourceLabel: 'Email Data',
        incomingEdge: 'AND',
        outgoingEdge: '',
        categoryLabel: 'Activity Data',
        selectedValue: '[34,100]',
        selectedOperator: 'not_between',
      },
    ],
    status: 'draft',
    audienceTitle: 'GG Untitled Audience',
    audienceSize: 23243434,
    sizeUpdatedAt: getTimeAgo(25), // 25 minutes ago - shows "25 min ago" (green)
    id: '19b61de3-b1c8-4ff9-8126-9cb4d36b0207',
    createdAt: '2025-09-11T07:21:08Z',
    updatedAt: '2025-09-11T07:21:08Z',
    audienceType: 'node',
  },
  {
    id: 'e5918b8a-e391-403f-b815-25a35a04db95',
    audienceTitle: '1234566666',
    status: 'draft',
    createdAt: '2025-09-11T07:21:08Z',
    updatedAt: '2025-09-11T07:21:08Z',
    audienceSize: 10892891,
    sizeUpdatedAt: getTimeAgo(0, 5), // 5 hours ago - shows "5 hours ago" (green)
    audienceType: 'node',
  },
  {
    id: 'ad993864-cd34-45da-8ef4-57d0f7f52bce',
    audienceTitle: 'Create new from audience',
    status: 'draft',
    createdAt: '2025-09-11T06:43:14Z',
    updatedAt: '2025-09-15T07:54:24Z',
    audienceSize: 88322002,
    sizeUpdatedAt: getTimeAgo(0, 0, 4), // 4 days ago - shows "4 days ago" (green)
    audienceType: 'audience',
  },
  {
    id: '4a10aace-ddbb-493d-b9de-b401c6825c74',
    audienceTitle: 'Last 30 Days Gaming',
    status: 'active',
    createdAt: '2025-09-03T14:41:30Z',
    updatedAt: '2025-09-08T14:28:56Z',
    audienceSize: 227937,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 1.5), // ~10-11 days ago - shows "1 week ago" (orange)
    audienceType: 'audience',
  },
  {
    id: 'a46d7290-a8bf-42f1-9c24-9f6437662c3b',
    audienceTitle: 'D- Sports Enthusiasts',
    status: 'active',
    createdAt: '2025-09-02T14:19:41Z',
    updatedAt: '2025-09-08T14:27:59Z',
    audienceSize: 34958141,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 2),
    audienceType: 'audience',
  },
  {
    id: 'b12c3d4e-5f6a-7b8c-9d0e-1f2a3b4c5d6e',
    audienceTitle: 'High-Value Customers',
    status: 'active',
    createdAt: '2025-08-01T09:00:00Z',
    updatedAt: '2025-09-10T11:00:00Z',
    audienceSize: 12543,
    sizeUpdatedAt: getTimeAgo(0, 2),
    audienceType: 'audience',
  },
  {
    id: 'c23d4e5f-6a7b-8c9d-0e1f-2a3b4c5d6e7f',
    audienceTitle: 'New Subscribers',
    status: 'active',
    createdAt: '2025-08-05T10:30:00Z',
    updatedAt: '2025-09-12T08:45:00Z',
    audienceSize: 8921,
    sizeUpdatedAt: getTimeAgo(0, 0, 1),
    audienceType: 'audience',
  },
  {
    id: 'd34e5f6a-7b8c-9d0e-1f2a-3b4c5d6e7f8a',
    audienceTitle: 'Churned Users Q3',
    status: 'active',
    createdAt: '2025-07-20T14:00:00Z',
    updatedAt: '2025-09-05T16:20:00Z',
    audienceSize: 3456,
    sizeUpdatedAt: getTimeAgo(0, 0, 3),
    audienceType: 'audience',
  },
  {
    id: 'e45f6a7b-8c9d-0e1f-2a3b-4c5d6e7f8a9b',
    audienceTitle: 'Email Engagement Segment',
    status: 'active',
    createdAt: '2025-07-15T08:00:00Z',
    updatedAt: '2025-09-09T09:30:00Z',
    audienceSize: 15678,
    sizeUpdatedAt: getTimeAgo(0, 0, 2),
    audienceType: 'audience',
  },
  {
    id: 'f56a7b8c-9d0e-1f2a-3b4c-5d6e7f8a9b0c',
    audienceTitle: 'Loyalty Program Members',
    status: 'active',
    createdAt: '2025-06-10T11:00:00Z',
    updatedAt: '2025-09-11T14:00:00Z',
    audienceSize: 22300,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 1),
    audienceType: 'audience',
  },
  {
    id: 'a67b8c9d-0e1f-2a3b-4c5d-6e7f8a9b0c1d',
    audienceTitle: 'Automotive Intenders',
    status: 'active',
    createdAt: '2025-06-20T13:00:00Z',
    updatedAt: '2025-09-07T10:00:00Z',
    audienceSize: 45230,
    sizeUpdatedAt: getTimeAgo(0, 0, 5),
    audienceType: 'audience',
  },
  {
    id: 'b78c9d0e-1f2a-3b4c-5d6e-7f8a9b0c1d2e',
    audienceTitle: 'Travel Enthusiasts',
    status: 'active',
    createdAt: '2025-05-18T09:30:00Z',
    updatedAt: '2025-09-06T12:45:00Z',
    audienceSize: 31200,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 2),
    audienceType: 'audience',
  },
  {
    id: 'c89d0e1f-2a3b-4c5d-6e7f-8a9b0c1d2e3f',
    audienceTitle: 'Mobile App Users',
    status: 'active',
    createdAt: '2025-05-25T07:00:00Z',
    updatedAt: '2025-09-13T08:00:00Z',
    audienceSize: 67890,
    sizeUpdatedAt: getTimeAgo(30),
    audienceType: 'audience',
  },
  {
    id: 'd90e1f2a-3b4c-5d6e-7f8a-9b0c1d2e3f4a',
    audienceTitle: 'Fashion Forward Shoppers',
    status: 'active',
    createdAt: '2025-04-12T10:00:00Z',
    updatedAt: '2025-09-04T15:30:00Z',
    audienceSize: 19450,
    sizeUpdatedAt: getTimeAgo(0, 0, 7),
    audienceType: 'audience',
  },
  {
    id: 'e01f2a3b-4c5d-6e7f-8a9b-0c1d2e3f4a5b',
    audienceTitle: 'Health & Wellness Seekers',
    status: 'active',
    createdAt: '2025-04-20T11:15:00Z',
    updatedAt: '2025-09-03T13:00:00Z',
    audienceSize: 28750,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 3),
    audienceType: 'audience',
  },
  {
    id: 'f12a3b4c-5d6e-7f8a-9b0c-1d2e3f4a5b6c',
    audienceTitle: 'Tech Early Adopters',
    status: 'active',
    createdAt: '2025-03-30T08:30:00Z',
    updatedAt: '2025-09-02T10:00:00Z',
    audienceSize: 11230,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 1),
    audienceType: 'audience',
  },
  {
    id: 'a23b4c5d-6e7f-8a9b-0c1d-2e3f4a5b6c7d',
    audienceTitle: 'Re-engagement Campaign Targets',
    status: 'active',
    createdAt: '2025-03-15T12:00:00Z',
    updatedAt: '2025-09-01T09:30:00Z',
    audienceSize: 5640,
    sizeUpdatedAt: getTimeAgo(0, 0, 10),
    audienceType: 'audience',
  },
  {
    id: 'b34c5d6e-7f8a-9b0c-1d2e-3f4a5b6c7d8e',
    audienceTitle: 'Premium Tier Subscribers',
    status: 'active',
    createdAt: '2025-02-10T09:00:00Z',
    updatedAt: '2025-08-30T14:00:00Z',
    audienceSize: 9870,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 2),
    audienceType: 'audience',
  },
  {
    id: 'c45d6e7f-8a9b-0c1d-2e3f-4a5b6c7d8e9f',
    audienceTitle: 'Weekend Shoppers',
    status: 'active',
    createdAt: '2025-02-01T10:30:00Z',
    updatedAt: '2025-08-28T11:00:00Z',
    audienceSize: 41200,
    sizeUpdatedAt: getTimeAgo(0, 0, 14),
    audienceType: 'audience',
  },
  {
    id: 'd56e7f8a-9b0c-1d2e-3f4a-5b6c7d8e9f0a',
    audienceTitle: 'B2B Decision Makers',
    status: 'active',
    createdAt: '2025-01-20T08:00:00Z',
    updatedAt: '2025-08-25T09:00:00Z',
    audienceSize: 7340,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 1),
    audienceType: 'audience',
  },
  {
    id: 'e67f8a9b-0c1d-2e3f-4a5b-6c7d8e9f0a1b',
    audienceTitle: 'Gen Z Content Consumers',
    status: 'active',
    createdAt: '2025-01-10T11:00:00Z',
    updatedAt: '2025-08-22T15:00:00Z',
    audienceSize: 53400,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 1),
    audienceType: 'audience',
  },
  {
    id: 'f78a9b0c-1d2e-3f4a-5b6c-7d8e9f0a1b2c',
    audienceTitle: 'Homeowners 35-55',
    status: 'active',
    createdAt: '2024-12-15T10:00:00Z',
    updatedAt: '2025-08-20T12:00:00Z',
    audienceSize: 24600,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 2),
    audienceType: 'audience',
  },
  {
    id: 'a89b0c1d-2e3f-4a5b-6c7d-8e9f0a1b2c3d',
    audienceTitle: 'Frequent Flyers',
    status: 'active',
    createdAt: '2024-12-01T09:30:00Z',
    updatedAt: '2025-08-18T13:30:00Z',
    audienceSize: 18200,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 3),
    audienceType: 'audience',
  },
  {
    id: 'b90c1d2e-3f4a-5b6c-7d8e-9f0a1b2c3d4e',
    audienceTitle: 'Pet Owners Segment',
    status: 'active',
    createdAt: '2024-11-20T08:45:00Z',
    updatedAt: '2025-08-15T10:00:00Z',
    audienceSize: 36700,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 3),
    audienceType: 'audience',
  },
  {
    id: 'c01d2e3f-4a5b-6c7d-8e9f-0a1b2c3d4e5f',
    audienceTitle: 'Cord-Cutters Streaming Users',
    status: 'active',
    createdAt: '2024-11-10T12:00:00Z',
    updatedAt: '2025-08-12T14:00:00Z',
    audienceSize: 29100,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 1),
    audienceType: 'audience',
  },
  {
    id: 'd12e3f4a-5b6c-7d8e-9f0a-1b2c3d4e5f6a',
    audienceTitle: 'Back-to-School Parents',
    status: 'active',
    createdAt: '2024-10-05T09:00:00Z',
    updatedAt: '2025-08-10T11:00:00Z',
    audienceSize: 14300,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 1),
    audienceType: 'audience',
  },
  {
    id: 'e23f4a5b-6c7d-8e9f-0a1b-2c3d4e5f6a7b',
    audienceTitle: 'Seasonal Holiday Buyers',
    status: 'draft',
    createdAt: '2025-09-01T10:00:00Z',
    updatedAt: '2025-09-14T09:00:00Z',
    audienceSize: 0,
    sizeUpdatedAt: getTimeAgo(0, 1),
    audienceType: 'audience',
  },
  {
    id: 'f34a5b6c-7d8e-9f0a-1b2c-3d4e5f6a7b8c',
    audienceTitle: 'Social Media Influencers',
    status: 'draft',
    createdAt: '2025-08-28T11:30:00Z',
    updatedAt: '2025-09-13T14:00:00Z',
    audienceSize: 0,
    sizeUpdatedAt: getTimeAgo(0, 2),
    audienceType: 'audience',
  },
  {
    id: 'a45b6c7d-8e9f-0a1b-2c3d-4e5f6a7b8c9d',
    audienceTitle: 'Q4 Campaign Prospectors',
    status: 'draft',
    createdAt: '2025-08-20T08:00:00Z',
    updatedAt: '2025-09-12T10:30:00Z',
    audienceSize: 0,
    sizeUpdatedAt: getTimeAgo(0, 0, 1),
    audienceType: 'audience',
  },
  {
    id: 'b56c7d8e-9f0a-1b2c-3d4e-5f6a7b8c9d0e',
    audienceTitle: 'Beta Program Candidates',
    status: 'draft',
    createdAt: '2025-08-10T13:00:00Z',
    updatedAt: '2025-09-10T09:00:00Z',
    audienceSize: 0,
    sizeUpdatedAt: getTimeAgo(0, 0, 2),
    audienceType: 'audience',
  },
  {
    id: 'c67d8e9f-0a1b-2c3d-4e5f-6a7b8c9d0e1f',
    audienceTitle: 'Untitled Segment Draft',
    status: 'draft',
    createdAt: '2025-09-13T15:00:00Z',
    updatedAt: '2025-09-14T08:00:00Z',
    audienceSize: 0,
    sizeUpdatedAt: getTimeAgo(0, 0, 0),
    audienceType: 'audience',
  },
  {
    id: 'd78e9f0a-1b2c-3d4e-5f6a-7b8c9d0e1f2a',
    audienceTitle: 'Holiday Gift Shoppers 2024',
    status: 'archived',
    createdAt: '2024-10-01T09:00:00Z',
    updatedAt: '2025-01-15T12:00:00Z',
    audienceSize: 33200,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 8),
    audienceType: 'audience',
  },
  {
    id: 'e89f0a1b-2c3d-4e5f-6a7b-8c9d0e1f2a3b',
    audienceTitle: 'Summer Sale Responders',
    status: 'archived',
    createdAt: '2024-05-01T10:00:00Z',
    updatedAt: '2024-09-01T11:00:00Z',
    audienceSize: 21800,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 12),
    audienceType: 'audience',
  },
  {
    id: 'f90a1b2c-3d4e-5f6a-7b8c-9d0e1f2a3b4c',
    audienceTitle: 'Black Friday 2024 Segment',
    status: 'archived',
    createdAt: '2024-11-01T08:00:00Z',
    updatedAt: '2024-12-15T10:00:00Z',
    audienceSize: 48900,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 5),
    audienceType: 'audience',
  },
  {
    id: 'a01b2c3d-4e5f-6a7b-8c9d-0e1f2a3b4c5d',
    audienceTitle: 'Legacy Loyalty Tier 1',
    status: 'archived',
    createdAt: '2023-08-20T09:00:00Z',
    updatedAt: '2024-06-01T12:00:00Z',
    audienceSize: 17600,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 18),
    audienceType: 'audience',
  },
  {
    id: 'b12c3d4e-6e7f-8a9b-0c1d-2e3f4a5b6c7d',
    audienceTitle: 'Deprecated Email List 2023',
    status: 'archived',
    createdAt: '2023-01-10T10:00:00Z',
    updatedAt: '2023-12-31T12:00:00Z',
    audienceSize: 8100,
    sizeUpdatedAt: getTimeAgo(0, 0, 0, 0, 24),
    audienceType: 'audience',
  },
];

export const MOCK_SOURCES = {
  sources: [
    {
      sourceName: 'Stirista Data',
      iconId: 'stirista-data',
      categories: [
        {
          id: 2,
          label: 'Personal',
        },
        {
          id: 4,
          label: 'Financial',
        },
        {
          id: 1,
          label: 'Geography',
        },
        {
          id: 3,
          label: 'Intent',
        },
        {
          id: 5,
          label: 'Interest',
        },
        {
          id: 6,
          label: 'Frequent Purchasers',
        },
        {
          id: 8,
          label: 'Automotive',
        },
        {
          id: 7,
          label: 'Political',
        },
      ],
    },
    {
      sourceName: 'Website Data',
      iconId: 'website-activity',
      categories: [
        {
          id: 11,
          label: 'Cart',
        },
        {
          id: 12,
          label: 'Checkout',
        },
        {
          id: 10,
          label: 'Pageviews',
        },
        {
          id: 9,
          label: 'Sessions',
        },
        {
          id: 18,
          label: 'VIG Attributes',
        },
      ],
    },
    {
      sourceName: 'Email Data',
      iconId: 'email-data',
      categories: [
        {
          id: 14,
          label: 'Activity Data',
        },
        {
          id: 13,
          label: 'Campaign Data',
        },
      ],
    },
    {
      sourceName: '1st Party Data',
      iconId: 'first-party-data',
      categories: [
        {
          id: 15,
          label: 'Transactional',
        },
      ],
    },
    {
      sourceName: 'Custom Audiences',
      iconId: 'custom-audiences',
      categories: [
        {
          id: 16,
          label: 'Audience Builder',
        },
        // Returned by the BE for tenants with lookalike entitlement.
        {
          id: 20,
          label: 'Lookalike',
        },
      ],
    },
  ],
};

export const MOCK_STIRISTA_DATA_PERSONAL_ATTRIBUTES = {
  attributes: [
    {
      id: 10084,
      name: 'Age',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Enter age...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"<>\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"><<\\"}],\\"permissions\\":{\\"permision\\":true},\\"rangeConfig\\":{\\"min\\":18,\\"max\\":100,\\"step\\":1}}"',
    },
    {
      id: 10087,
      name: 'Ancestry',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select ancestry/ancestries...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"value\\":\\"Aboriginal\\",\\"label\\":\\"Aboriginal\\"},{\\"value\\":\\"Afghanistan\\",\\"label\\":\\"Afghanistan\\"},{\\"value\\":\\"African-American\\",\\"label\\":\\"African-American\\"},{\\"value\\":\\"Afrikaan\\",\\"label\\":\\"Afrikaan\\"},{\\"value\\":\\"Albania\\",\\"label\\":\\"Albania\\"},{\\"value\\":\\"Algeria\\",\\"label\\":\\"Algeria\\"},{\\"value\\":\\"Andorra\\",\\"label\\":\\"Andorra\\"},{\\"value\\":\\"Angola\\",\\"label\\":\\"Angola\\"},{\\"value\\":\\"Antigua\\",\\"label\\":\\"Antigua\\"},{\\"value\\":\\"Arab\\",\\"label\\":\\"Arab\\"},{\\"value\\":\\"Argentina\\",\\"label\\":\\"Argentina\\"},{\\"value\\":\\"Armenia\\",\\"label\\":\\"Armenia\\"},{\\"value\\":\\"Aruba\\",\\"label\\":\\"Aruba\\"},{\\"value\\":\\"Assyria\\",\\"label\\":\\"Assyria\\"},{\\"value\\":\\"Austria\\",\\"label\\":\\"Austria\\"},{\\"value\\":\\"Azerbaijan\\",\\"label\\":\\"Azerbaijan\\"},{\\"value\\":\\"Bahamas\\",\\"label\\":\\"Bahamas\\"},{\\"value\\":\\"Bahrain\\",\\"label\\":\\"Bahrain\\"},{\\"value\\":\\"Bangladesh\\",\\"label\\":\\"Bangladesh\\"},{\\"value\\":\\"Barbados\\",\\"label\\":\\"Barbados\\"},{\\"value\\":\\"Basque\\",\\"label\\":\\"Basque\\"},{\\"value\\":\\"Belarus\\",\\"label\\":\\"Belarus\\"},{\\"value\\":\\"Belgium\\",\\"label\\":\\"Belgium\\"},{\\"value\\":\\"Benine\\",\\"label\\":\\"Benine\\"},{\\"value\\":\\"Bermuda\\",\\"label\\":\\"Bermuda\\"},{\\"value\\":\\"Bhutan\\",\\"label\\":\\"Bhutan\\"},{\\"value\\":\\"Bosnia\\",\\"label\\":\\"Bosnia\\"},{\\"value\\":\\"Brazil\\",\\"label\\":\\"Brazil\\"},{\\"value\\":\\"Bret\\",\\"label\\":\\"Bret\\"},{\\"value\\":\\"Britain\\",\\"label\\":\\"Britain\\"},{\\"value\\":\\"Brunei\\",\\"label\\":\\"Brunei\\"},{\\"value\\":\\"Bulgaria\\",\\"label\\":\\"Bulgaria\\"},{\\"value\\":\\"Burkhina Faso\\",\\"label\\":\\"Burkhina Faso\\"},{\\"value\\":\\"Burundi\\",\\"label\\":\\"Burundi\\"},{\\"value\\":\\"Cambodia\\",\\"label\\":\\"Cambodia\\"},{\\"value\\":\\"Cape Verde\\",\\"label\\":\\"Cape Verde\\"},{\\"value\\":\\"Caribbean\\",\\"label\\":\\"Caribbean\\"},{\\"value\\":\\"Caribbean Mixed Race\\",\\"label\\":\\"Caribbean Mixed Race\\"},{\\"value\\":\\"Carribean\\",\\"label\\":\\"Carribean\\"},{\\"value\\":\\"Catalan\\",\\"label\\":\\"Catalan\\"},{\\"value\\":\\"Cayman Islands\\",\\"label\\":\\"Cayman Islands\\"},{\\"value\\":\\"Chad\\",\\"label\\":\\"Chad\\"},{\\"value\\":\\"Chile\\",\\"label\\":\\"Chile\\"},{\\"value\\":\\"China\\",\\"label\\":\\"China\\"},{\\"value\\":\\"Colombia\\",\\"label\\":\\"Colombia\\"},{\\"value\\":\\"Comoros\\",\\"label\\":\\"Comoros\\"},{\\"value\\":\\"Congo\\",\\"label\\":\\"Congo\\"},{\\"value\\":\\"Cook Islands\\",\\"label\\":\\"Cook Islands\\"},{\\"value\\":\\"Cornish\\",\\"label\\":\\"Cornish\\"},{\\"value\\":\\"Costa Rica\\",\\"label\\":\\"Costa Rica\\"},{\\"value\\":\\"Cote Divoire\\",\\"label\\":\\"Cote Divoire\\"},{\\"value\\":\\"Cote dIvoire\\",\\"label\\":\\"Cote dIvoire\\"},{\\"value\\":\\"Croatia\\",\\"label\\":\\"Croatia\\"},{\\"value\\":\\"Cuba\\",\\"label\\":\\"Cuba\\"},{\\"value\\":\\"Cyprus\\",\\"label\\":\\"Cyprus\\"},{\\"value\\":\\"Czech Republic\\",\\"label\\":\\"Czech Republic\\"},{\\"value\\":\\"Demark\\",\\"label\\":\\"Demark\\"},{\\"value\\":\\"Djibouti\\",\\"label\\":\\"Djibouti\\"},{\\"value\\":\\"Dominica\\",\\"label\\":\\"Dominica\\"},{\\"value\\":\\"Dominican Republic\\",\\"label\\":\\"Dominican Republic\\"},{\\"value\\":\\"Ecuador\\",\\"label\\":\\"Ecuador\\"},{\\"value\\":\\"Egypt\\",\\"label\\":\\"Egypt\\"},{\\"value\\":\\"El Salvador\\",\\"label\\":\\"El Salvador\\"},{\\"value\\":\\"England\\",\\"label\\":\\"England\\"},{\\"value\\":\\"Eriteria\\",\\"label\\":\\"Eriteria\\"},{\\"value\\":\\"Estonia\\",\\"label\\":\\"Estonia\\"},{\\"value\\":\\"Ethiopia\\",\\"label\\":\\"Ethiopia\\"},{\\"value\\":\\"Faroe Islands\\",\\"label\\":\\"Faroe Islands\\"},{\\"value\\":\\"Fiji Islands\\",\\"label\\":\\"Fiji Islands\\"},{\\"value\\":\\"Finland\\",\\"label\\":\\"Finland\\"},{\\"value\\":\\"France\\",\\"label\\":\\"France\\"},{\\"value\\":\\"French (black)\\",\\"label\\":\\"French (black)\\"},{\\"value\\":\\"French Canadian\\",\\"label\\":\\"French Canadian\\"},{\\"value\\":\\"Frisian\\",\\"label\\":\\"Frisian\\"},{\\"value\\":\\"Gabon\\",\\"label\\":\\"Gabon\\"},{\\"value\\":\\"Galican\\",\\"label\\":\\"Galican\\"},{\\"value\\":\\"Gambia\\",\\"label\\":\\"Gambia\\"},{\\"value\\":\\"Georgia\\",\\"label\\":\\"Georgia\\"},{\\"value\\":\\"Germany\\",\\"label\\":\\"Germany\\"},{\\"value\\":\\"Ghana\\",\\"label\\":\\"Ghana\\"},{\\"value\\":\\"Gibraltar\\",\\"label\\":\\"Gibraltar\\"},{\\"value\\":\\"Greece\\",\\"label\\":\\"Greece\\"},{\\"value\\":\\"Guam\\",\\"label\\":\\"Guam\\"},{\\"value\\":\\"Guatemala\\",\\"label\\":\\"Guatemala\\"},{\\"value\\":\\"Guinea\\",\\"label\\":\\"Guinea\\"},{\\"value\\":\\"Guinea Bissau\\",\\"label\\":\\"Guinea Bissau\\"},{\\"value\\":\\"Guyana\\",\\"label\\":\\"Guyana\\"},{\\"value\\":\\"Haiti\\",\\"label\\":\\"Haiti\\"},{\\"value\\":\\"Hawaii\\",\\"label\\":\\"Hawaii\\"},{\\"value\\":\\"Hmong\\",\\"label\\":\\"Hmong\\"},{\\"value\\":\\"Honduras\\",\\"label\\":\\"Honduras\\"},{\\"value\\":\\"Hong Kong\\",\\"label\\":\\"Hong Kong\\"},{\\"value\\":\\"Hungary\\",\\"label\\":\\"Hungary\\"},{\\"value\\":\\"Iceland\\",\\"label\\":\\"Iceland\\"},{\\"value\\":\\"India\\",\\"label\\":\\"India\\"},{\\"value\\":\\"India (Bihar)\\",\\"label\\":\\"India (Bihar)\\"},{\\"value\\":\\"India (Jaat)\\",\\"label\\":\\"India (Jaat)\\"},{\\"value\\":\\"India (Kannad)\\",\\"label\\":\\"India (Kannad)\\"},{\\"value\\":\\"India (Malyalam)\\",\\"label\\":\\"India (Malyalam)\\"},{\\"value\\":\\"India (Marathi)\\",\\"label\\":\\"India (Marathi)\\"},{\\"value\\":\\"India (Muslim)\\",\\"label\\":\\"India (Muslim)\\"},{\\"value\\":\\"India (North)\\",\\"label\\":\\"India (North)\\"},{\\"value\\":\\"India (Orissa)\\",\\"label\\":\\"India (Orissa)\\"},{\\"value\\":\\"India (Rajasthan)\\",\\"label\\":\\"India (Rajasthan)\\"},{\\"value\\":\\"India (bihar)\\",\\"label\\":\\"India (bihar)\\"},{\\"value\\":\\"India (jaat)\\",\\"label\\":\\"India (jaat)\\"},{\\"value\\":\\"India (kannad)\\",\\"label\\":\\"India (kannad)\\"},{\\"value\\":\\"India (malyalam)\\",\\"label\\":\\"India (malyalam)\\"},{\\"value\\":\\"India (marathi)\\",\\"label\\":\\"India (marathi)\\"},{\\"value\\":\\"India (muslim)\\",\\"label\\":\\"India (muslim)\\"},{\\"value\\":\\"India (north)\\",\\"label\\":\\"India (north)\\"},{\\"value\\":\\"India (orissa)\\",\\"label\\":\\"India (orissa)\\"},{\\"value\\":\\"India (rajasthan)\\",\\"label\\":\\"India (rajasthan)\\"},{\\"value\\":\\"India Marwari\\",\\"label\\":\\"India Marwari\\"},{\\"value\\":\\"Indian (Gujrat)\\",\\"label\\":\\"Indian (Gujrat)\\"},{\\"value\\":\\"Indian (Iranian Heritage)\\",\\"label\\":\\"Indian (Iranian Heritage)\\"},{\\"value\\":\\"Indian (Punjab)\\",\\"label\\":\\"Indian (Punjab)\\"},{\\"value\\":\\"Indian (Tamilian)\\",\\"label\\":\\"Indian (Tamilian)\\"},{\\"value\\":\\"Indian (Telugu)\\",\\"label\\":\\"Indian (Telugu)\\"},{\\"value\\":\\"Indian (West Bengal)\\",\\"label\\":\\"Indian (West Bengal)\\"},{\\"value\\":\\"Indian (gujrat)\\",\\"label\\":\\"Indian (gujrat)\\"},{\\"value\\":\\"Indian (iranian Heritage)\\",\\"label\\":\\"Indian (iranian Heritage)\\"},{\\"value\\":\\"Indian (punjab)\\",\\"label\\":\\"Indian (punjab)\\"},{\\"value\\":\\"Indian (tamilian)\\",\\"label\\":\\"Indian (tamilian)\\"},{\\"value\\":\\"Indian (telugu)\\",\\"label\\":\\"Indian (telugu)\\"},{\\"value\\":\\"Indian (west Bengal)\\",\\"label\\":\\"Indian (west Bengal)\\"},{\\"value\\":\\"Indonesia\\",\\"label\\":\\"Indonesia\\"},{\\"value\\":\\"Iran\\",\\"label\\":\\"Iran\\"},{\\"value\\":\\"Iraq\\",\\"label\\":\\"Iraq\\"},{\\"value\\":\\"Ireland\\",\\"label\\":\\"Ireland\\"},{\\"value\\":\\"Italy\\",\\"label\\":\\"Italy\\"},{\\"value\\":\\"Ivory Coast\\",\\"label\\":\\"Ivory Coast\\"},{\\"value\\":\\"Jamaica\\",\\"label\\":\\"Jamaica\\"},{\\"value\\":\\"Japan\\",\\"label\\":\\"Japan\\"},{\\"value\\":\\"Jewish\\",\\"label\\":\\"Jewish\\"},{\\"value\\":\\"Jordan\\",\\"label\\":\\"Jordan\\"},{\\"value\\":\\"Kazakhstan\\",\\"label\\":\\"Kazakhstan\\"},{\\"value\\":\\"Kenya\\",\\"label\\":\\"Kenya\\"},{\\"value\\":\\"Kiribati\\",\\"label\\":\\"Kiribati\\"},{\\"value\\":\\"Korea\\",\\"label\\":\\"Korea\\"},{\\"value\\":\\"Kurd\\",\\"label\\":\\"Kurd\\"},{\\"value\\":\\"Kuwait\\",\\"label\\":\\"Kuwait\\"},{\\"value\\":\\"Kyrgyzstan\\",\\"label\\":\\"Kyrgyzstan\\"},{\\"value\\":\\"Laos\\",\\"label\\":\\"Laos\\"},{\\"value\\":\\"Latvia\\",\\"label\\":\\"Latvia\\"},{\\"value\\":\\"Lebanon\\",\\"label\\":\\"Lebanon\\"},{\\"value\\":\\"Lesotho\\",\\"label\\":\\"Lesotho\\"},{\\"value\\":\\"Liberia\\",\\"label\\":\\"Liberia\\"},{\\"value\\":\\"Liechtenstein\\",\\"label\\":\\"Liechtenstein\\"},{\\"value\\":\\"Lithuania\\",\\"label\\":\\"Lithuania\\"},{\\"value\\":\\"Luxembourg\\",\\"label\\":\\"Luxembourg\\"},{\\"value\\":\\"Macau\\",\\"label\\":\\"Macau\\"},{\\"value\\":\\"Macedonia\\",\\"label\\":\\"Macedonia\\"},{\\"value\\":\\"Madagascar\\",\\"label\\":\\"Madagascar\\"},{\\"value\\":\\"Malawi\\",\\"label\\":\\"Malawi\\"},{\\"value\\":\\"Malaysia\\",\\"label\\":\\"Malaysia\\"},{\\"value\\":\\"Maldives\\",\\"label\\":\\"Maldives\\"},{\\"value\\":\\"Mali\\",\\"label\\":\\"Mali\\"},{\\"value\\":\\"Malta\\",\\"label\\":\\"Malta\\"},{\\"value\\":\\"Manx\\",\\"label\\":\\"Manx\\"},{\\"value\\":\\"Maori\\",\\"label\\":\\"Maori\\"},{\\"value\\":\\"Marshall Island\\",\\"label\\":\\"Marshall Island\\"},{\\"value\\":\\"Martinique\\",\\"label\\":\\"Martinique\\"},{\\"value\\":\\"Mauritania\\",\\"label\\":\\"Mauritania\\"},{\\"value\\":\\"Mauritius\\",\\"label\\":\\"Mauritius\\"},{\\"value\\":\\"Mexico\\",\\"label\\":\\"Mexico\\"},{\\"value\\":\\"Micronesia\\",\\"label\\":\\"Micronesia\\"},{\\"value\\":\\"Moldova\\",\\"label\\":\\"Moldova\\"},{\\"value\\":\\"Monaco\\",\\"label\\":\\"Monaco\\"},{\\"value\\":\\"Mongolia\\",\\"label\\":\\"Mongolia\\"},{\\"value\\":\\"Montenegro\\",\\"label\\":\\"Montenegro\\"},{\\"value\\":\\"Morocco\\",\\"label\\":\\"Morocco\\"},{\\"value\\":\\"Mozambique\\",\\"label\\":\\"Mozambique\\"},{\\"value\\":\\"Myanmar\\",\\"label\\":\\"Myanmar\\"},{\\"value\\":\\"Namibia\\",\\"label\\":\\"Namibia\\"},{\\"value\\":\\"Native American\\",\\"label\\":\\"Native American\\"},{\\"value\\":\\"Nauru\\",\\"label\\":\\"Nauru\\"},{\\"value\\":\\"Nepal\\",\\"label\\":\\"Nepal\\"},{\\"value\\":\\"Netherlands\\",\\"label\\":\\"Netherlands\\"},{\\"value\\":\\"New Caledonia\\",\\"label\\":\\"New Caledonia\\"},{\\"value\\":\\"Nicaragua\\",\\"label\\":\\"Nicaragua\\"},{\\"value\\":\\"Nigeria\\",\\"label\\":\\"Nigeria\\"},{\\"value\\":\\"Niue Islands\\",\\"label\\":\\"Niue Islands\\"},{\\"value\\":\\"Norway\\",\\"label\\":\\"Norway\\"},{\\"value\\":\\"Oman\\",\\"label\\":\\"Oman\\"},{\\"value\\":\\"Pakistan\\",\\"label\\":\\"Pakistan\\"},{\\"value\\":\\"Pakistan (Baluchistan)\\",\\"label\\":\\"Pakistan (Baluchistan)\\"},{\\"value\\":\\"Pakistan (baluchistan)\\",\\"label\\":\\"Pakistan (baluchistan)\\"},{\\"value\\":\\"Palau\\",\\"label\\":\\"Palau\\"},{\\"value\\":\\"Palestine\\",\\"label\\":\\"Palestine\\"},{\\"value\\":\\"Panama\\",\\"label\\":\\"Panama\\"},{\\"value\\":\\"Papua New Guinea\\",\\"label\\":\\"Papua New Guinea\\"},{\\"value\\":\\"Paraguay\\",\\"label\\":\\"Paraguay\\"},{\\"value\\":\\"Peru\\",\\"label\\":\\"Peru\\"},{\\"value\\":\\"Philippines\\",\\"label\\":\\"Philippines\\"},{\\"value\\":\\"Poland\\",\\"label\\":\\"Poland\\"},{\\"value\\":\\"Polynesia\\",\\"label\\":\\"Polynesia\\"},{\\"value\\":\\"Portugal\\",\\"label\\":\\"Portugal\\"},{\\"value\\":\\"Puerto Rico\\",\\"label\\":\\"Puerto Rico\\"},{\\"value\\":\\"Qatar\\",\\"label\\":\\"Qatar\\"},{\\"value\\":\\"Reunion\\",\\"label\\":\\"Reunion\\"},{\\"value\\":\\"Romania\\",\\"label\\":\\"Romania\\"},{\\"value\\":\\"Russia\\",\\"label\\":\\"Russia\\"},{\\"value\\":\\"Rwanda\\",\\"label\\":\\"Rwanda\\"},{\\"value\\":\\"Samoa\\",\\"label\\":\\"Samoa\\"},{\\"value\\":\\"San Marino\\",\\"label\\":\\"San Marino\\"},{\\"value\\":\\"Saudi Arabia\\",\\"label\\":\\"Saudi Arabia\\"},{\\"value\\":\\"Scandinavia\\",\\"label\\":\\"Scandinavia\\"},{\\"value\\":\\"Scotland\\",\\"label\\":\\"Scotland\\"},{\\"value\\":\\"Senegal\\",\\"label\\":\\"Senegal\\"},{\\"value\\":\\"Serbia\\",\\"label\\":\\"Serbia\\"},{\\"value\\":\\"Seychelles\\",\\"label\\":\\"Seychelles\\"},{\\"value\\":\\"Sierra Leone\\",\\"label\\":\\"Sierra Leone\\"},{\\"value\\":\\"Sigapore\\",\\"label\\":\\"Sigapore\\"},{\\"value\\":\\"Slavic\\",\\"label\\":\\"Slavic\\"},{\\"value\\":\\"Slovakia\\",\\"label\\":\\"Slovakia\\"},{\\"value\\":\\"Slovenia\\",\\"label\\":\\"Slovenia\\"},{\\"value\\":\\"Solomon Islands\\",\\"label\\":\\"Solomon Islands\\"},{\\"value\\":\\"Somalia\\",\\"label\\":\\"Somalia\\"},{\\"value\\":\\"South Africa\\",\\"label\\":\\"South Africa\\"},{\\"value\\":\\"Spain\\",\\"label\\":\\"Spain\\"},{\\"value\\":\\"Sri Lanka\\",\\"label\\":\\"Sri Lanka\\"},{\\"value\\":\\"St. Kitts Island\\",\\"label\\":\\"St. Kitts Island\\"},{\\"value\\":\\"St. Lucia\\",\\"label\\":\\"St. Lucia\\"},{\\"value\\":\\"St. Vincente\\",\\"label\\":\\"St. Vincente\\"},{\\"value\\":\\"Sudan\\",\\"label\\":\\"Sudan\\"},{\\"value\\":\\"Suriname\\",\\"label\\":\\"Suriname\\"},{\\"value\\":\\"Swaziland\\",\\"label\\":\\"Swaziland\\"},{\\"value\\":\\"Sweden\\",\\"label\\":\\"Sweden\\"},{\\"value\\":\\"Switzerland\\",\\"label\\":\\"Switzerland\\"},{\\"value\\":\\"Syria\\",\\"label\\":\\"Syria\\"},{\\"value\\":\\"Tahiti\\",\\"label\\":\\"Tahiti\\"},{\\"value\\":\\"Taiwan\\",\\"label\\":\\"Taiwan\\"},{\\"value\\":\\"Tajikistan\\",\\"label\\":\\"Tajikistan\\"},{\\"value\\":\\"Tanzania\\",\\"label\\":\\"Tanzania\\"},{\\"value\\":\\"Thailand\\",\\"label\\":\\"Thailand\\"},{\\"value\\":\\"The Carribean\\",\\"label\\":\\"The Carribean\\"},{\\"value\\":\\"Tibet\\",\\"label\\":\\"Tibet\\"},{\\"value\\":\\"Timor\\",\\"label\\":\\"Timor\\"},{\\"value\\":\\"Togo\\",\\"label\\":\\"Togo\\"},{\\"value\\":\\"Tonga\\",\\"label\\":\\"Tonga\\"},{\\"value\\":\\"Trinidad\\",\\"label\\":\\"Trinidad\\"},{\\"value\\":\\"Tunisia\\",\\"label\\":\\"Tunisia\\"},{\\"value\\":\\"Turkey\\",\\"label\\":\\"Turkey\\"},{\\"value\\":\\"Turkmenistan\\",\\"label\\":\\"Turkmenistan\\"},{\\"value\\":\\"Turks And Caicos\\",\\"label\\":\\"Turks And Caicos\\"},{\\"value\\":\\"Turks and Caicos\\",\\"label\\":\\"Turks and Caicos\\"},{\\"value\\":\\"Tuvalu\\",\\"label\\":\\"Tuvalu\\"},{\\"value\\":\\"UAE\\",\\"label\\":\\"UAE\\"},{\\"value\\":\\"Uae\\",\\"label\\":\\"Uae\\"},{\\"value\\":\\"Uganda\\",\\"label\\":\\"Uganda\\"},{\\"value\\":\\"Ukraine\\",\\"label\\":\\"Ukraine\\"},{\\"value\\":\\"Uruguay\\",\\"label\\":\\"Uruguay\\"},{\\"value\\":\\"Uzbekistan\\",\\"label\\":\\"Uzbekistan\\"},{\\"value\\":\\"Vanuatu\\",\\"label\\":\\"Vanuatu\\"},{\\"value\\":\\"Venezuela\\",\\"label\\":\\"Venezuela\\"},{\\"value\\":\\"Vietnam\\",\\"label\\":\\"Vietnam\\"},{\\"value\\":\\"Virgin Islands\\",\\"label\\":\\"Virgin Islands\\"},{\\"value\\":\\"Wales\\",\\"label\\":\\"Wales\\"},{\\"value\\":\\"Western Africa\\",\\"label\\":\\"Western Africa\\"},{\\"value\\":\\"Yemen\\",\\"label\\":\\"Yemen\\"},{\\"value\\":\\"Zambia\\",\\"label\\":\\"Zambia\\"},{\\"value\\":\\"Zimbabwe\\",\\"label\\":\\"Zimbabwe\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10089,
      name: 'Assimilation Level',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more assimilation level options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"P\\",\\"value\\":\\"1st Generation in the US\\"},{\\"key\\":\\"M\\",\\"value\\":\\"2nd Generation in the US\\"},{\\"key\\":\\"C\\",\\"value\\":\\"3+ Generations in the US\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10098,
      name: 'Blue Collar',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more blue collar options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"1\\",\\"value\\":\\"Yes\\"},{\\"key\\":\\"0\\",\\"value\\":\\"No\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10094,
      name: 'Education',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more education options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"6\\",\\"value\\":\\"Some College\\"},{\\"key\\":\\"3\\",\\"value\\":\\"Completed Graduate School\\"},{\\"key\\":\\"5\\",\\"value\\":\\"Some HS\\"},{\\"key\\":\\"4\\",\\"value\\":\\"Attended Vocational/Technical\\"},{\\"key\\":\\"1\\",\\"value\\":\\"Completed HS\\"},{\\"key\\":\\"2\\",\\"value\\":\\"Completed College\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10086,
      name: 'Ethnicity',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select ethnicity/ethnicities...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"value\\":\\"African American 1\\",\\"label\\":\\"African American 1\\"},{\\"value\\":\\"Afrikaan\\",\\"label\\":\\"Afrikaan\\"},{\\"value\\":\\"Albanian\\",\\"label\\":\\"Albanian\\"},{\\"value\\":\\"Aleut\\",\\"label\\":\\"Aleut\\"},{\\"value\\":\\"Algerian\\",\\"label\\":\\"Algerian\\"},{\\"value\\":\\"Angola\\",\\"label\\":\\"Angola\\"},{\\"value\\":\\"Arabic\\",\\"label\\":\\"Arabic\\"},{\\"value\\":\\"Armenian\\",\\"label\\":\\"Armenian\\"},{\\"value\\":\\"Ashanti\\",\\"label\\":\\"Ashanti\\"},{\\"value\\":\\"Australia\\",\\"label\\":\\"Australia\\"},{\\"value\\":\\"Austrian\\",\\"label\\":\\"Austrian\\"},{\\"value\\":\\"Azerbejani\\",\\"label\\":\\"Azerbejani\\"},{\\"value\\":\\"Bahrain\\",\\"label\\":\\"Bahrain\\"},{\\"value\\":\\"Bangladesh\\",\\"label\\":\\"Bangladesh\\"},{\\"value\\":\\"Basotho\\",\\"label\\":\\"Basotho\\"},{\\"value\\":\\"Basque\\",\\"label\\":\\"Basque\\"},{\\"value\\":\\"Belgium\\",\\"label\\":\\"Belgium\\"},{\\"value\\":\\"Bengali\\",\\"label\\":\\"Bengali\\"},{\\"value\\":\\"Benin\\",\\"label\\":\\"Benin\\"},{\\"value\\":\\"Bhutanese\\",\\"label\\":\\"Bhutanese\\"},{\\"value\\":\\"Black African\\",\\"label\\":\\"Black African\\"},{\\"value\\":\\"Bosnian Muslim\\",\\"label\\":\\"Bosnian Muslim\\"},{\\"value\\":\\"Botswana\\",\\"label\\":\\"Botswana\\"},{\\"value\\":\\"Brazil\\",\\"label\\":\\"Brazil\\"},{\\"value\\":\\"Bulgarian\\",\\"label\\":\\"Bulgarian\\"},{\\"value\\":\\"Burkina  Faso\\",\\"label\\":\\"Burkina  Faso\\"},{\\"value\\":\\"Burundi\\",\\"label\\":\\"Burundi\\"},{\\"value\\":\\"Byelorus\\",\\"label\\":\\"Byelorus\\"},{\\"value\\":\\"Cameroon\\",\\"label\\":\\"Cameroon\\"},{\\"value\\":\\"Caribbean African American\\",\\"label\\":\\"Caribbean African American\\"},{\\"value\\":\\"Central African Republic\\",\\"label\\":\\"Central African Republic\\"},{\\"value\\":\\"Chad\\",\\"label\\":\\"Chad\\"},{\\"value\\":\\"Chechnian\\",\\"label\\":\\"Chechnian\\"},{\\"value\\":\\"Chinese\\",\\"label\\":\\"Chinese\\"},{\\"value\\":\\"Comoros Island\\",\\"label\\":\\"Comoros Island\\"},{\\"value\\":\\"Congo\\",\\"label\\":\\"Congo\\"},{\\"value\\":\\"Cook Islands\\",\\"label\\":\\"Cook Islands\\"},{\\"value\\":\\"Cote dIvoire\\",\\"label\\":\\"Cote dIvoire\\"},{\\"value\\":\\"Croatian\\",\\"label\\":\\"Croatian\\"},{\\"value\\":\\"Cuba\\",\\"label\\":\\"Cuba\\"},{\\"value\\":\\"Czech\\",\\"label\\":\\"Czech\\"},{\\"value\\":\\"Danish\\",\\"label\\":\\"Danish\\"},{\\"value\\":\\"Djibouti\\",\\"label\\":\\"Djibouti\\"},{\\"value\\":\\"Dutch\\",\\"label\\":\\"Dutch\\"},{\\"value\\":\\"Egyptian\\",\\"label\\":\\"Egyptian\\"},{\\"value\\":\\"English\\",\\"label\\":\\"English\\"},{\\"value\\":\\"Equatorial Guinea\\",\\"label\\":\\"Equatorial Guinea\\"},{\\"value\\":\\"Estonian\\",\\"label\\":\\"Estonian\\"},{\\"value\\":\\"Ethiopian\\",\\"label\\":\\"Ethiopian\\"},{\\"value\\":\\"Fiji\\",\\"label\\":\\"Fiji\\"},{\\"value\\":\\"Filipino\\",\\"label\\":\\"Filipino\\"},{\\"value\\":\\"Finn\\",\\"label\\":\\"Finn\\"},{\\"value\\":\\"French\\",\\"label\\":\\"French\\"},{\\"value\\":\\"Gabon\\",\\"label\\":\\"Gabon\\"},{\\"value\\":\\"Gambian\\",\\"label\\":\\"Gambian\\"},{\\"value\\":\\"Georgian\\",\\"label\\":\\"Georgian\\"},{\\"value\\":\\"German\\",\\"label\\":\\"German\\"},{\\"value\\":\\"Ghana\\",\\"label\\":\\"Ghana\\"},{\\"value\\":\\"Greek\\",\\"label\\":\\"Greek\\"},{\\"value\\":\\"Guinea\\",\\"label\\":\\"Guinea\\"},{\\"value\\":\\"Guinea-Bissau\\",\\"label\\":\\"Guinea-Bissau\\"},{\\"value\\":\\"Guyana\\",\\"label\\":\\"Guyana\\"},{\\"value\\":\\"Haiti\\",\\"label\\":\\"Haiti\\"},{\\"value\\":\\"Hausa\\",\\"label\\":\\"Hausa\\"},{\\"value\\":\\"Hawaii\\",\\"label\\":\\"Hawaii\\"},{\\"value\\":\\"Hindu Specific Indian\\",\\"label\\":\\"Hindu Specific Indian\\"},{\\"value\\":\\"Hispanic\\",\\"label\\":\\"Hispanic\\"},{\\"value\\":\\"Hmong\\",\\"label\\":\\"Hmong\\"},{\\"value\\":\\"Hungarian\\",\\"label\\":\\"Hungarian\\"},{\\"value\\":\\"Icelandic\\",\\"label\\":\\"Icelandic\\"},{\\"value\\":\\"India\\",\\"label\\":\\"India\\"},{\\"value\\":\\"Indian (Iranian Heritage)\\",\\"label\\":\\"Indian (Iranian Heritage)\\"},{\\"value\\":\\"Indonesia\\",\\"label\\":\\"Indonesia\\"},{\\"value\\":\\"Iranian\\",\\"label\\":\\"Iranian\\"},{\\"value\\":\\"Iraqi\\",\\"label\\":\\"Iraqi\\"},{\\"value\\":\\"Irish\\",\\"label\\":\\"Irish\\"},{\\"value\\":\\"Italian\\",\\"label\\":\\"Italian\\"},{\\"value\\":\\"Ivory Coast\\",\\"label\\":\\"Ivory Coast\\"},{\\"value\\":\\"Jamaica\\",\\"label\\":\\"Jamaica\\"},{\\"value\\":\\"Japanese\\",\\"label\\":\\"Japanese\\"},{\\"value\\":\\"Jewish\\",\\"label\\":\\"Jewish\\"},{\\"value\\":\\"Jewish - Hebrew Language Names\\",\\"label\\":\\"Jewish - Hebrew Language Names\\"},{\\"value\\":\\"Jordan\\",\\"label\\":\\"Jordan\\"},{\\"value\\":\\"Kampuchea\\",\\"label\\":\\"Kampuchea\\"},{\\"value\\":\\"Kenyan\\",\\"label\\":\\"Kenyan\\"},{\\"value\\":\\"Khazakstani\\",\\"label\\":\\"Khazakstani\\"},{\\"value\\":\\"Khmer\\",\\"label\\":\\"Khmer\\"},{\\"value\\":\\"Kirghizstan\\",\\"label\\":\\"Kirghizstan\\"},{\\"value\\":\\"Korean\\",\\"label\\":\\"Korean\\"},{\\"value\\":\\"Kurdish\\",\\"label\\":\\"Kurdish\\"},{\\"value\\":\\"Kuwaiti\\",\\"label\\":\\"Kuwaiti\\"},{\\"value\\":\\"Kyrgestan\\",\\"label\\":\\"Kyrgestan\\"},{\\"value\\":\\"Laotian\\",\\"label\\":\\"Laotian\\"},{\\"value\\":\\"Latvian\\",\\"label\\":\\"Latvian\\"},{\\"value\\":\\"Lebanon\\",\\"label\\":\\"Lebanon\\"},{\\"value\\":\\"Lesotho\\",\\"label\\":\\"Lesotho\\"},{\\"value\\":\\"Liberian\\",\\"label\\":\\"Liberian\\"},{\\"value\\":\\"Libyan\\",\\"label\\":\\"Libyan\\"},{\\"value\\":\\"Liechtenstein\\",\\"label\\":\\"Liechtenstein\\"},{\\"value\\":\\"Lithuanian\\",\\"label\\":\\"Lithuanian\\"},{\\"value\\":\\"Luxembourg\\",\\"label\\":\\"Luxembourg\\"},{\\"value\\":\\"Macedonian\\",\\"label\\":\\"Macedonian\\"},{\\"value\\":\\"Madagascar\\",\\"label\\":\\"Madagascar\\"},{\\"value\\":\\"Malawi\\",\\"label\\":\\"Malawi\\"},{\\"value\\":\\"Malay\\",\\"label\\":\\"Malay\\"},{\\"value\\":\\"Maldives\\",\\"label\\":\\"Maldives\\"},{\\"value\\":\\"Mali\\",\\"label\\":\\"Mali\\"},{\\"value\\":\\"Malta\\",\\"label\\":\\"Malta\\"},{\\"value\\":\\"Manx - Isle of Man\\",\\"label\\":\\"Manx - Isle of Man\\"},{\\"value\\":\\"Mauritania\\",\\"label\\":\\"Mauritania\\"},{\\"value\\":\\"Misc. South African\\",\\"label\\":\\"Misc. South African\\"},{\\"value\\":\\"Misc. South East Asian\\",\\"label\\":\\"Misc. South East Asian\\"},{\\"value\\":\\"Moldavian\\",\\"label\\":\\"Moldavian\\"},{\\"value\\":\\"Mongolian\\",\\"label\\":\\"Mongolian\\"},{\\"value\\":\\"Moroccan\\",\\"label\\":\\"Moroccan\\"},{\\"value\\":\\"Mozambique\\",\\"label\\":\\"Mozambique\\"},{\\"value\\":\\"Multi Ethnic\\",\\"label\\":\\"Multi Ethnic\\"},{\\"value\\":\\"Myanamar\\",\\"label\\":\\"Myanamar\\"},{\\"value\\":\\"Namibian\\",\\"label\\":\\"Namibian\\"},{\\"value\\":\\"Native American\\",\\"label\\":\\"Native American\\"},{\\"value\\":\\"Nauru\\",\\"label\\":\\"Nauru\\"},{\\"value\\":\\"Nepal\\",\\"label\\":\\"Nepal\\"},{\\"value\\":\\"New Zealander\\",\\"label\\":\\"New Zealander\\"},{\\"value\\":\\"Niger\\",\\"label\\":\\"Niger\\"},{\\"value\\":\\"Nigerian\\",\\"label\\":\\"Nigerian\\"},{\\"value\\":\\"Norwegian\\",\\"label\\":\\"Norwegian\\"},{\\"value\\":\\"Other Asian\\",\\"label\\":\\"Other Asian\\"},{\\"value\\":\\"Overt European Jewish Names\\",\\"label\\":\\"Overt European Jewish Names\\"},{\\"value\\":\\"Pakistan (Baluchistan)\\",\\"label\\":\\"Pakistan (Baluchistan)\\"},{\\"value\\":\\"Pakistani\\",\\"label\\":\\"Pakistani\\"},{\\"value\\":\\"Palau\\",\\"label\\":\\"Palau\\"},{\\"value\\":\\"Palestine\\",\\"label\\":\\"Palestine\\"},{\\"value\\":\\"Panama\\",\\"label\\":\\"Panama\\"},{\\"value\\":\\"Papau New Guinea\\",\\"label\\":\\"Papau New Guinea\\"},{\\"value\\":\\"Paraguay\\",\\"label\\":\\"Paraguay\\"},{\\"value\\":\\"Persian\\",\\"label\\":\\"Persian\\"},{\\"value\\":\\"Peru\\",\\"label\\":\\"Peru\\"},{\\"value\\":\\"Philippine\\",\\"label\\":\\"Philippine\\"},{\\"value\\":\\"Polish\\",\\"label\\":\\"Polish\\"},{\\"value\\":\\"Polynesia\\",\\"label\\":\\"Polynesia\\"},{\\"value\\":\\"Portuguese\\",\\"label\\":\\"Portuguese\\"},{\\"value\\":\\"Puerto Rico\\",\\"label\\":\\"Puerto Rico\\"},{\\"value\\":\\"Qatar\\",\\"label\\":\\"Qatar\\"},{\\"value\\":\\"Romanian\\",\\"label\\":\\"Romanian\\"},{\\"value\\":\\"Russian\\",\\"label\\":\\"Russian\\"},{\\"value\\":\\"Rwandan\\",\\"label\\":\\"Rwandan\\"},{\\"value\\":\\"Samoa\\",\\"label\\":\\"Samoa\\"},{\\"value\\":\\"Saudi\\",\\"label\\":\\"Saudi\\"},{\\"value\\":\\"Scottish\\",\\"label\\":\\"Scottish\\"},{\\"value\\":\\"Senegalese\\",\\"label\\":\\"Senegalese\\"},{\\"value\\":\\"Serbian\\",\\"label\\":\\"Serbian\\"},{\\"value\\":\\"Seychelles\\",\\"label\\":\\"Seychelles\\"},{\\"value\\":\\"Sierra Leone\\",\\"label\\":\\"Sierra Leone\\"},{\\"value\\":\\"Slovak\\",\\"label\\":\\"Slovak\\"},{\\"value\\":\\"Slovenian\\",\\"label\\":\\"Slovenian\\"},{\\"value\\":\\"Somalian\\",\\"label\\":\\"Somalian\\"},{\\"value\\":\\"South African\\",\\"label\\":\\"South African\\"},{\\"value\\":\\"Spanish\\",\\"label\\":\\"Spanish\\"},{\\"value\\":\\"Sri Lankan\\",\\"label\\":\\"Sri Lankan\\"},{\\"value\\":\\"Sudanese\\",\\"label\\":\\"Sudanese\\"},{\\"value\\":\\"Surinam\\",\\"label\\":\\"Surinam\\"},{\\"value\\":\\"Swahili\\",\\"label\\":\\"Swahili\\"},{\\"value\\":\\"Swaziland\\",\\"label\\":\\"Swaziland\\"},{\\"value\\":\\"Swedish\\",\\"label\\":\\"Swedish\\"},{\\"value\\":\\"Swiss\\",\\"label\\":\\"Swiss\\"},{\\"value\\":\\"Syrian\\",\\"label\\":\\"Syrian\\"},{\\"value\\":\\"Taiwan\\",\\"label\\":\\"Taiwan\\"},{\\"value\\":\\"Tajik\\",\\"label\\":\\"Tajik\\"},{\\"value\\":\\"Tanzanian\\",\\"label\\":\\"Tanzanian\\"},{\\"value\\":\\"Telugu\\",\\"label\\":\\"Telugu\\"},{\\"value\\":\\"Thai\\",\\"label\\":\\"Thai\\"},{\\"value\\":\\"Tibetan\\",\\"label\\":\\"Tibetan\\"},{\\"value\\":\\"Togo\\",\\"label\\":\\"Togo\\"},{\\"value\\":\\"Tonga\\",\\"label\\":\\"Tonga\\"},{\\"value\\":\\"Trinidad\\",\\"label\\":\\"Trinidad\\"},{\\"value\\":\\"Tunisian\\",\\"label\\":\\"Tunisian\\"},{\\"value\\":\\"Turkish\\",\\"label\\":\\"Turkish\\"},{\\"value\\":\\"Turkmenistan\\",\\"label\\":\\"Turkmenistan\\"},{\\"value\\":\\"Ugandan\\",\\"label\\":\\"Ugandan\\"},{\\"value\\":\\"Ukrainian\\",\\"label\\":\\"Ukrainian\\"},{\\"value\\":\\"Uniquely African\\",\\"label\\":\\"Uniquely African\\"},{\\"value\\":\\"Unknown\\",\\"label\\":\\"Unknown\\"},{\\"value\\":\\"Uzbek\\",\\"label\\":\\"Uzbek\\"},{\\"value\\":\\"Vanuatu\\",\\"label\\":\\"Vanuatu\\"},{\\"value\\":\\"Vietnamese\\",\\"label\\":\\"Vietnamese\\"},{\\"value\\":\\"Welsh\\",\\"label\\":\\"Welsh\\"},{\\"value\\":\\"Western Africa\\",\\"label\\":\\"Western Africa\\"},{\\"value\\":\\"Western Somoa\\",\\"label\\":\\"Western Somoa\\"},{\\"value\\":\\"Xhosa\\",\\"label\\":\\"Xhosa\\"},{\\"value\\":\\"Yemeni\\",\\"label\\":\\"Yemeni\\"},{\\"value\\":\\"Zaire\\",\\"label\\":\\"Zaire\\"},{\\"value\\":\\"Zambia\\",\\"label\\":\\"Zambia\\"},{\\"value\\":\\"Zimbabwe\\",\\"label\\":\\"Zimbabwe\\"},{\\"value\\":\\"Zulu\\",\\"label\\":\\"Zulu\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10085,
      name: 'Gender',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more gender options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"M\\",\\"value\\":\\"Male\\"},{\\"key\\":\\"F\\",\\"value\\":\\"Female\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10092,
      name: 'Language',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select language(s)...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"value\\":\\"Afar-Arabic-English\\",\\"label\\":\\"Afar-Arabic-English\\"},{\\"value\\":\\"Afrikaan-English\\",\\"label\\":\\"Afrikaan-English\\"},{\\"value\\":\\"Arabic-English\\",\\"label\\":\\"Arabic-English\\"},{\\"value\\":\\"Arabic-French-English\\",\\"label\\":\\"Arabic-French-English\\"},{\\"value\\":\\"Arabic-Italian-English\\",\\"label\\":\\"Arabic-Italian-English\\"},{\\"value\\":\\"Armenian-English\\",\\"label\\":\\"Armenian-English\\"},{\\"value\\":\\"Assyrian\\",\\"label\\":\\"Assyrian\\"},{\\"value\\":\\"Azerbaijani-English\\",\\"label\\":\\"Azerbaijani-English\\"},{\\"value\\":\\"Bahasa-English\\",\\"label\\":\\"Bahasa-English\\"},{\\"value\\":\\"Balochi-English\\",\\"label\\":\\"Balochi-English\\"},{\\"value\\":\\"Bambara-English\\",\\"label\\":\\"Bambara-English\\"},{\\"value\\":\\"Bangla-English\\",\\"label\\":\\"Bangla-English\\"},{\\"value\\":\\"Bantu\\",\\"label\\":\\"Bantu\\"},{\\"value\\":\\"Bengali-English\\",\\"label\\":\\"Bengali-English\\"},{\\"value\\":\\"Bislama-English\\",\\"label\\":\\"Bislama-English\\"},{\\"value\\":\\"Bosnian-English\\",\\"label\\":\\"Bosnian-English\\"},{\\"value\\":\\"Bulgarian-English\\",\\"label\\":\\"Bulgarian-English\\"},{\\"value\\":\\"Burmese-English\\",\\"label\\":\\"Burmese-English\\"},{\\"value\\":\\"Chichewa-English\\",\\"label\\":\\"Chichewa-English\\"},{\\"value\\":\\"Creole-English\\",\\"label\\":\\"Creole-English\\"},{\\"value\\":\\"Creole-French-English\\",\\"label\\":\\"Creole-French-English\\"},{\\"value\\":\\"Croatian-English\\",\\"label\\":\\"Croatian-English\\"},{\\"value\\":\\"Czech-English\\",\\"label\\":\\"Czech-English\\"},{\\"value\\":\\"Dhivehi-English\\",\\"label\\":\\"Dhivehi-English\\"},{\\"value\\":\\"Dutch\\",\\"label\\":\\"Dutch\\"},{\\"value\\":\\"Dutch-English\\",\\"label\\":\\"Dutch-English\\"},{\\"value\\":\\"Dutch-Frisian-English\\",\\"label\\":\\"Dutch-Frisian-English\\"},{\\"value\\":\\"Dzongkha-English\\",\\"label\\":\\"Dzongkha-English\\"},{\\"value\\":\\"English\\",\\"label\\":\\"English\\"},{\\"value\\":\\"English-Afrikaan\\",\\"label\\":\\"English-Afrikaan\\"},{\\"value\\":\\"English-Cantonese\\",\\"label\\":\\"English-Cantonese\\"},{\\"value\\":\\"English-Creole\\",\\"label\\":\\"English-Creole\\"},{\\"value\\":\\"English-Danish\\",\\"label\\":\\"English-Danish\\"},{\\"value\\":\\"English-French\\",\\"label\\":\\"English-French\\"},{\\"value\\":\\"English-IndigenousLanguage\\",\\"label\\":\\"English-IndigenousLanguage\\"},{\\"value\\":\\"English-Indigenouslanguage\\",\\"label\\":\\"English-Indigenouslanguage\\"},{\\"value\\":\\"English-Mende\\",\\"label\\":\\"English-Mende\\"},{\\"value\\":\\"English-Polynesian\\",\\"label\\":\\"English-Polynesian\\"},{\\"value\\":\\"English-Russian\\",\\"label\\":\\"English-Russian\\"},{\\"value\\":\\"English-Sesotho\\",\\"label\\":\\"English-Sesotho\\"},{\\"value\\":\\"English-Swahili\\",\\"label\\":\\"English-Swahili\\"},{\\"value\\":\\"English-Swati\\",\\"label\\":\\"English-Swati\\"},{\\"value\\":\\"English-Swedish\\",\\"label\\":\\"English-Swedish\\"},{\\"value\\":\\"English-Tibetian\\",\\"label\\":\\"English-Tibetian\\"},{\\"value\\":\\"English-Welsh\\",\\"label\\":\\"English-Welsh\\"},{\\"value\\":\\"Estonian-English\\",\\"label\\":\\"Estonian-English\\"},{\\"value\\":\\"Filipino-Tagalog-English\\",\\"label\\":\\"Filipino-Tagalog-English\\"},{\\"value\\":\\"Finnish-English\\",\\"label\\":\\"Finnish-English\\"},{\\"value\\":\\"French-Arabic-English\\",\\"label\\":\\"French-Arabic-English\\"},{\\"value\\":\\"French-English\\",\\"label\\":\\"French-English\\"},{\\"value\\":\\"French-German-English\\",\\"label\\":\\"French-German-English\\"},{\\"value\\":\\"French-Hausa-English\\",\\"label\\":\\"French-Hausa-English\\"},{\\"value\\":\\"GUJRATI-English\\",\\"label\\":\\"GUJRATI-English\\"},{\\"value\\":\\"Georgian-English\\",\\"label\\":\\"Georgian-English\\"},{\\"value\\":\\"German-English\\",\\"label\\":\\"German-English\\"},{\\"value\\":\\"Gilbertese-English\\",\\"label\\":\\"Gilbertese-English\\"},{\\"value\\":\\"Greek-English\\",\\"label\\":\\"Greek-English\\"},{\\"value\\":\\"Gujrati-English\\",\\"label\\":\\"Gujrati-English\\"},{\\"value\\":\\"Hawaiian-English\\",\\"label\\":\\"Hawaiian-English\\"},{\\"value\\":\\"Hebrew-English\\",\\"label\\":\\"Hebrew-English\\"},{\\"value\\":\\"Hindi-English\\",\\"label\\":\\"Hindi-English\\"},{\\"value\\":\\"Hmong-English\\",\\"label\\":\\"Hmong-English\\"},{\\"value\\":\\"Icelandic-English\\",\\"label\\":\\"Icelandic-English\\"},{\\"value\\":\\"Ijekaviandialect-English\\",\\"label\\":\\"Ijekaviandialect-English\\"},{\\"value\\":\\"Italian-English\\",\\"label\\":\\"Italian-English\\"},{\\"value\\":\\"Japanese-English\\",\\"label\\":\\"Japanese-English\\"},{\\"value\\":\\"Kannad-English\\",\\"label\\":\\"Kannad-English\\"},{\\"value\\":\\"Kazak-Russian-English\\",\\"label\\":\\"Kazak-Russian-English\\"},{\\"value\\":\\"Khmer-English\\",\\"label\\":\\"Khmer-English\\"},{\\"value\\":\\"Kinyarwanda-French-Andenglish\\",\\"label\\":\\"Kinyarwanda-French-Andenglish\\"},{\\"value\\":\\"Kinyarwanda-French-andEnglish\\",\\"label\\":\\"Kinyarwanda-French-andEnglish\\"},{\\"value\\":\\"Korean-English\\",\\"label\\":\\"Korean-English\\"},{\\"value\\":\\"Kyrgyz-Russian-English\\",\\"label\\":\\"Kyrgyz-Russian-English\\"},{\\"value\\":\\"Lao-English\\",\\"label\\":\\"Lao-English\\"},{\\"value\\":\\"Lao-French-English\\",\\"label\\":\\"Lao-French-English\\"},{\\"value\\":\\"Latvian-Russian-English\\",\\"label\\":\\"Latvian-Russian-English\\"},{\\"value\\":\\"Lithuanian-English\\",\\"label\\":\\"Lithuanian-English\\"},{\\"value\\":\\"Luganda-English\\",\\"label\\":\\"Luganda-English\\"},{\\"value\\":\\"Macedonian-English\\",\\"label\\":\\"Macedonian-English\\"},{\\"value\\":\\"Magyar-English\\",\\"label\\":\\"Magyar-English\\"},{\\"value\\":\\"Malagasy-English\\",\\"label\\":\\"Malagasy-English\\"},{\\"value\\":\\"Malay-English\\",\\"label\\":\\"Malay-English\\"},{\\"value\\":\\"Malayalam-English\\",\\"label\\":\\"Malayalam-English\\"},{\\"value\\":\\"Maltese-English\\",\\"label\\":\\"Maltese-English\\"},{\\"value\\":\\"Mandarin\\",\\"label\\":\\"Mandarin\\"},{\\"value\\":\\"Mandarin-English\\",\\"label\\":\\"Mandarin-English\\"},{\\"value\\":\\"Marathi-English\\",\\"label\\":\\"Marathi-English\\"},{\\"value\\":\\"Marshallese-English\\",\\"label\\":\\"Marshallese-English\\"},{\\"value\\":\\"Melayu-English\\",\\"label\\":\\"Melayu-English\\"},{\\"value\\":\\"Moldovan-English\\",\\"label\\":\\"Moldovan-English\\"},{\\"value\\":\\"Mongolian-English\\",\\"label\\":\\"Mongolian-English\\"},{\\"value\\":\\"Nauruan-English\\",\\"label\\":\\"Nauruan-English\\"},{\\"value\\":\\"Nepali-English\\",\\"label\\":\\"Nepali-English\\"},{\\"value\\":\\"Norwegian-English\\",\\"label\\":\\"Norwegian-English\\"},{\\"value\\":\\"Oriya-English\\",\\"label\\":\\"Oriya-English\\"},{\\"value\\":\\"Orominga-English\\",\\"label\\":\\"Orominga-English\\"},{\\"value\\":\\"PARSI-English\\",\\"label\\":\\"PARSI-English\\"},{\\"value\\":\\"PUNJABI-English\\",\\"label\\":\\"PUNJABI-English\\"},{\\"value\\":\\"Palauan-English\\",\\"label\\":\\"Palauan-English\\"},{\\"value\\":\\"Parsi-English\\",\\"label\\":\\"Parsi-English\\"},{\\"value\\":\\"Pashtu-English\\",\\"label\\":\\"Pashtu-English\\"},{\\"value\\":\\"Persian-English\\",\\"label\\":\\"Persian-English\\"},{\\"value\\":\\"Polish-English\\",\\"label\\":\\"Polish-English\\"},{\\"value\\":\\"Portuguese-English\\",\\"label\\":\\"Portuguese-English\\"},{\\"value\\":\\"Punjabi-English\\",\\"label\\":\\"Punjabi-English\\"},{\\"value\\":\\"RAJASTHANI-English\\",\\"label\\":\\"RAJASTHANI-English\\"},{\\"value\\":\\"Rajasthani-English\\",\\"label\\":\\"Rajasthani-English\\"},{\\"value\\":\\"Romanian-English\\",\\"label\\":\\"Romanian-English\\"},{\\"value\\":\\"Russian-English\\",\\"label\\":\\"Russian-English\\"},{\\"value\\":\\"Samoan-English\\",\\"label\\":\\"Samoan-English\\"},{\\"value\\":\\"Serbian-English\\",\\"label\\":\\"Serbian-English\\"},{\\"value\\":\\"Setswana-English\\",\\"label\\":\\"Setswana-English\\"},{\\"value\\":\\"Sinhala-English\\",\\"label\\":\\"Sinhala-English\\"},{\\"value\\":\\"Slovak-English\\",\\"label\\":\\"Slovak-English\\"},{\\"value\\":\\"Slovenian-English\\",\\"label\\":\\"Slovenian-English\\"},{\\"value\\":\\"Somali-English\\",\\"label\\":\\"Somali-English\\"},{\\"value\\":\\"Spanish-English\\",\\"label\\":\\"Spanish-English\\"},{\\"value\\":\\"Spanish-French-English\\",\\"label\\":\\"Spanish-French-English\\"},{\\"value\\":\\"Swahili-English\\",\\"label\\":\\"Swahili-English\\"},{\\"value\\":\\"Swedish-English\\",\\"label\\":\\"Swedish-English\\"},{\\"value\\":\\"TAMIL-English\\",\\"label\\":\\"TAMIL-English\\"},{\\"value\\":\\"Tajik-Russian-English\\",\\"label\\":\\"Tajik-Russian-English\\"},{\\"value\\":\\"Tamil-English\\",\\"label\\":\\"Tamil-English\\"},{\\"value\\":\\"Telugu-English\\",\\"label\\":\\"Telugu-English\\"},{\\"value\\":\\"Tetum-English\\",\\"label\\":\\"Tetum-English\\"},{\\"value\\":\\"Thai-English\\",\\"label\\":\\"Thai-English\\"},{\\"value\\":\\"Tok Pisin-English\\",\\"label\\":\\"Tok Pisin-English\\"},{\\"value\\":\\"Tongan-English\\",\\"label\\":\\"Tongan-English\\"},{\\"value\\":\\"Tosk-English\\",\\"label\\":\\"Tosk-English\\"},{\\"value\\":\\"Turkish-English\\",\\"label\\":\\"Turkish-English\\"},{\\"value\\":\\"Turkmen-English\\",\\"label\\":\\"Turkmen-English\\"},{\\"value\\":\\"Tuvaluan-English\\",\\"label\\":\\"Tuvaluan-English\\"},{\\"value\\":\\"Ukrainian-English\\",\\"label\\":\\"Ukrainian-English\\"},{\\"value\\":\\"Urdu-English\\",\\"label\\":\\"Urdu-English\\"},{\\"value\\":\\"Uzbek-English\\",\\"label\\":\\"Uzbek-English\\"},{\\"value\\":\\"Vietnamese-English\\",\\"label\\":\\"Vietnamese-English\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10090,
      name: 'Marital Status',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more marital status options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"M\\",\\"value\\":\\"Married\\"},{\\"key\\":\\"S\\",\\"value\\":\\"Single\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10093,
      name: 'Multilingual',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10101,
      name: 'Number Of Adults In HH',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Enter # of adults in h h...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"7\\",\\"value\\":\\"Seven Adults\\"},{\\"key\\":\\"8\\",\\"value\\":\\"Eight Adults\\"},{\\"key\\":\\"4\\",\\"value\\":\\"Four Adults\\"},{\\"key\\":\\"1\\",\\"value\\":\\"One Adult\\"},{\\"key\\":\\"6\\",\\"value\\":\\"Six Adults\\"},{\\"key\\":\\"5\\",\\"value\\":\\"Five Adults\\"},{\\"key\\":\\"3\\",\\"value\\":\\"Three Adults\\"},{\\"key\\":\\"2\\",\\"value\\":\\"Two Adults\\"},{\\"key\\":\\"9\\",\\"value\\":\\"Nine Adults\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10102,
      name: 'Number Of Children',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more number of children options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"1\\",\\"value\\":\\"One Child\\"},{\\"key\\":\\"8\\",\\"value\\":\\"Eight Children\\"},{\\"key\\":\\"5\\",\\"value\\":\\"Five Children\\"},{\\"key\\":\\"7\\",\\"value\\":\\"Seven Children\\"},{\\"key\\":\\"3\\",\\"value\\":\\"Three Children\\"},{\\"key\\":\\"6\\",\\"value\\":\\"Six Children\\"},{\\"key\\":\\"2\\",\\"value\\":\\"Two Children\\"},{\\"key\\":\\"9\\",\\"value\\":\\"Nine Children\\"},{\\"key\\":\\"10\\",\\"value\\":\\"Ten Children\\"},{\\"key\\":\\"4\\",\\"value\\":\\"Four Children\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10100,
      name: 'Number Of Generations In HH',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Enter # of generations in h h...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"1\\",\\"value\\":\\"One Generation\\"},{\\"key\\":\\"2\\",\\"value\\":\\"Two Generations\\"},{\\"key\\":\\"3\\",\\"value\\":\\"Three Generations\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10099,
      name: 'Number Of Persons In HH',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Enter # of persons in h h...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"10\\",\\"value\\":\\"10 Individuals in the Household\\"},{\\"key\\":\\"8\\",\\"value\\":\\"8 Individuals in the Household\\"},{\\"key\\":\\"7\\",\\"value\\":\\"7 Individuals in the Household\\"},{\\"key\\":\\"4\\",\\"value\\":\\"4 Individuals in the Household\\"},{\\"key\\":\\"1\\",\\"value\\":\\"1 Individual in the Household\\"},{\\"key\\":\\"2\\",\\"value\\":\\"2  Individuals in the Household\\"},{\\"key\\":\\"9\\",\\"value\\":\\"9 Individuals in the Household\\"},{\\"key\\":\\"3\\",\\"value\\":\\"3 individuals in the Household\\"},{\\"key\\":\\"6\\",\\"value\\":\\"6 Individuals in a Household\\"},{\\"key\\":\\"5\\",\\"value\\":\\"5 Individuals in the Household\\"},{\\"key\\":\\"10+\\",\\"value\\":\\"10 or more individuals in the Household\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10096,
      name: 'Occupation Category',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select occupation category...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"value\\":\\"Blue Collar\\",\\"label\\":\\"Blue Collar\\"},{\\"value\\":\\"Homemaker\\",\\"label\\":\\"Homemaker\\"},{\\"value\\":\\"Lower Management\\",\\"label\\":\\"Lower Management\\"},{\\"value\\":\\"Other\\",\\"label\\":\\"Other\\"},{\\"value\\":\\"Professional\\",\\"label\\":\\"Professional\\"},{\\"value\\":\\"Retired\\",\\"label\\":\\"Retired\\"},{\\"value\\":\\"Unknown\\",\\"label\\":\\"Unknown\\"},{\\"value\\":\\"Upper Management\\",\\"label\\":\\"Upper Management\\"},{\\"value\\":\\"White Collar Worker\\",\\"label\\":\\"White Collar Worker\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10095,
      name: 'Occupations',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more occupations...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"key\\":\\"A\\",\\"value\\":\\"Business Owner\\"},{\\"key\\":\\"B\\",\\"value\\":\\"Professional\\"},{\\"key\\":\\"C\\",\\"value\\":\\"Exec/Upper Mgmt\\"},{\\"key\\":\\"D\\",\\"value\\":\\"Middle Management\\"},{\\"key\\":\\"E\\",\\"value\\":\\"White Collar Worker\\"},{\\"key\\":\\"F\\",\\"value\\":\\"Blue Collar Worker\\"},{\\"key\\":\\"G\\",\\"value\\":\\"Health Services\\"},{\\"key\\":\\"H\\",\\"value\\":\\"Secretary\\"},{\\"key\\":\\"I\\",\\"value\\":\\"Homemaker\\"},{\\"key\\":\\"J\\",\\"value\\":\\"Retired\\"},{\\"key\\":\\"L\\",\\"value\\":\\"Teacher\\"},{\\"key\\":\\"M\\",\\"value\\":\\"Nurse\\"},{\\"key\\":\\"N\\",\\"value\\":\\"Computer\\"},{\\"key\\":\\"O\\",\\"value\\":\\"Civil Service\\"},{\\"key\\":\\"P\\",\\"value\\":\\"Part Time\\"},{\\"key\\":\\"Q\\",\\"value\\":\\"Student\\"},{\\"key\\":\\"S\\",\\"value\\":\\"Disabled\\"},{\\"key\\":\\"A220\\",\\"value\\":\\"Owner\\"},{\\"key\\":\\"A264\\",\\"value\\":\\"Self Employed\\"},{\\"key\\":\\"B020\\",\\"value\\":\\"Architect\\"},{\\"key\\":\\"B051\\",\\"value\\":\\"Chemist\\"},{\\"key\\":\\"B083\\",\\"value\\":\\"Curator\\"},{\\"key\\":\\"B107\\",\\"value\\":\\"Engineer\\"},{\\"key\\":\\"B108\\",\\"value\\":\\"Engineer/Aerospace\\"},{\\"key\\":\\"B109\\",\\"value\\":\\"Engineer/Chemical\\"},{\\"key\\":\\"B110\\",\\"value\\":\\"Engineer/Civil\\"},{\\"key\\":\\"B111\\",\\"value\\":\\"Engineer/Electrical/Electronic\\"},{\\"key\\":\\"B112\\",\\"value\\":\\"Engineer/Field\\"},{\\"key\\":\\"B113\\",\\"value\\":\\"Engineer/Industrial\\"},{\\"key\\":\\"B114\\",\\"value\\":\\"Engineer/Mechanical\\"},{\\"key\\":\\"B135\\",\\"value\\":\\"Geologist\\"},{\\"key\\":\\"B159\\",\\"value\\":\\"Legal/Attorney/Lawyer\\"},{\\"key\\":\\"B162\\",\\"value\\":\\"Librarian/Archivist\\"},{\\"key\\":\\"B196\\",\\"value\\":\\"Medical Doctor/Physician\\"},{\\"key\\":\\"B226\\",\\"value\\":\\"Pastor\\"},{\\"key\\":\\"B230\\",\\"value\\":\\"Pilot\\"},{\\"key\\":\\"B260\\",\\"value\\":\\"Scientist\\"},{\\"key\\":\\"B270\\",\\"value\\":\\"Statistician/Actuary\\"},{\\"key\\":\\"B295\\",\\"value\\":\\"Veterinarian\\"},{\\"key\\":\\"C048\\",\\"value\\":\\"Ceo/Cfo/Chairman/Corp Officer\\"},{\\"key\\":\\"C065\\",\\"value\\":\\"Comptroller\\"},{\\"key\\":\\"C236\\",\\"value\\":\\"Politician/Legisaltor/Diplomat\\"},{\\"key\\":\\"C238\\",\\"value\\":\\"President\\"},{\\"key\\":\\"C289\\",\\"value\\":\\"Treasurer\\"},{\\"key\\":\\"C296\\",\\"value\\":\\"Vice President\\"},{\\"key\\":\\"D003\\",\\"value\\":\\"Acct Executive\\"},{\\"key\\":\\"D095\\",\\"value\\":\\"Director/Art Director\\"},{\\"key\\":\\"D096\\",\\"value\\":\\"Director/Executive Director/Etc\\"},{\\"key\\":\\"D105\\",\\"value\\":\\"Editor\\"},{\\"key\\":\\"D173\\",\\"value\\":\\"Manager\\"},{\\"key\\":\\"D174\\",\\"value\\":\\"Manager/Assistant Manager\\"},{\\"key\\":\\"D175\\",\\"value\\":\\"Manager/Branch Manager\\"},{\\"key\\":\\"D176\\",\\"value\\":\\"Manager/Credit Manager\\"},{\\"key\\":\\"D177\\",\\"value\\":\\"Manager/District Manager\\"},{\\"key\\":\\"D178\\",\\"value\\":\\"Manager/Division Manager\\"},{\\"key\\":\\"D179\\",\\"value\\":\\"Manager/General Manager\\"},{\\"key\\":\\"D180\\",\\"value\\":\\"Manager/Marketing Manager\\"},{\\"key\\":\\"D181\\",\\"value\\":\\"Manager/Office Manager\\"},{\\"key\\":\\"D182\\",\\"value\\":\\"Manager/Plant Manager\\"},{\\"key\\":\\"D183\\",\\"value\\":\\"Manager/Product Manager\\"},{\\"key\\":\\"D184\\",\\"value\\":\\"Manager/Project Manager\\"},{\\"key\\":\\"D185\\",\\"value\\":\\"Manager/Property Manager\\"},{\\"key\\":\\"D186\\",\\"value\\":\\"Manager/Regional Manager\\"},{\\"key\\":\\"D187\\",\\"value\\":\\"Manager/Sales Manager\\"},{\\"key\\":\\"D188\\",\\"value\\":\\"Manager/Store Manager\\"},{\\"key\\":\\"D189\\",\\"value\\":\\"Manager/Traffic Manager\\"},{\\"key\\":\\"D190\\",\\"value\\":\\"Manager/Warehouse Manager\\"},{\\"key\\":\\"D232\\",\\"value\\":\\"Planner\\"},{\\"key\\":\\"D241\\",\\"value\\":\\"Principal/Dean/Educator\\"},{\\"key\\":\\"D272\\",\\"value\\":\\"Superintendent\\"},{\\"key\\":\\"D273\\",\\"value\\":\\"Supervisor\\"},{\\"key\\":\\"E002\\",\\"value\\":\\"Accounting/Biller/Billing Clerk\\"},{\\"key\\":\\"E004\\",\\"value\\":\\"Actor/Entertainer/Announcer/Bro\\"},{\\"key\\":\\"E005\\",\\"value\\":\\"Adjuster\\"},{\\"key\\":\\"E006\\",\\"value\\":\\"Administration/Management\\"},{\\"key\\":\\"E007\\",\\"value\\":\\"Advertising\\"},{\\"key\\":\\"E008\\",\\"value\\":\\"Agent\\"},{\\"key\\":\\"E009\\",\\"value\\":\\"Aide/Assistant\\"},{\\"key\\":\\"E010\\",\\"value\\":\\"Aide/Assistant/Executive\\"},{\\"key\\":\\"E011\\",\\"value\\":\\"Aide/Assistant/Office\\"},{\\"key\\":\\"E012\\",\\"value\\":\\"Aide/Assistant/School\\"},{\\"key\\":\\"E013\\",\\"value\\":\\"Aide/Assistant/Staff\\"},{\\"key\\":\\"E014\\",\\"value\\":\\"Aide/Assistant/Technical\\"},{\\"key\\":\\"E016\\",\\"value\\":\\"Analyst\\"},{\\"key\\":\\"E018\\",\\"value\\":\\"Appraiser\\"},{\\"key\\":\\"E022\\",\\"value\\":\\"Artist\\"},{\\"key\\":\\"E026\\",\\"value\\":\\"Auctioneer\\"},{\\"key\\":\\"E027\\",\\"value\\":\\"Auditor\\"},{\\"key\\":\\"E030\\",\\"value\\":\\"Banker\\"},{\\"key\\":\\"E031\\",\\"value\\":\\"Banker/Loan Officer\\"},{\\"key\\":\\"E032\\",\\"value\\":\\"Banker/Loan Processor\\"},{\\"key\\":\\"E037\\",\\"value\\":\\"Bookkeeper\\"},{\\"key\\":\\"E040\\",\\"value\\":\\"Broker\\"},{\\"key\\":\\"E041\\",\\"value\\":\\"Broker/Stock/Trader\\"},{\\"key\\":\\"E044\\",\\"value\\":\\"Buyer\\"},{\\"key\\":\\"E046\\",\\"value\\":\\"Cashier\\"},{\\"key\\":\\"E047\\",\\"value\\":\\"Caterer\\"},{\\"key\\":\\"E049\\",\\"value\\":\\"Checker\\"},{\\"key\\":\\"E055\\",\\"value\\":\\"Claims Examiner/Rep/Adjudicator\\"},{\\"key\\":\\"E057\\",\\"value\\":\\"Clerk\\"},{\\"key\\":\\"E059\\",\\"value\\":\\"Clerk/File\\"},{\\"key\\":\\"E063\\",\\"value\\":\\"Collector\\"},{\\"key\\":\\"E064\\",\\"value\\":\\"Communications\\"},{\\"key\\":\\"E070\\",\\"value\\":\\"Conservation/Enviroment\\"},{\\"key\\":\\"E072\\",\\"value\\":\\"Consultant/Advisor\\"},{\\"key\\":\\"E076\\",\\"value\\":\\"Coordinator\\"},{\\"key\\":\\"E085\\",\\"value\\":\\"Customer Service/Representative\\"},{\\"key\\":\\"E092\\",\\"value\\":\\"Designer\\"},{\\"key\\":\\"E093\\",\\"value\\":\\"Detective/Investigator\\"},{\\"key\\":\\"E098\\",\\"value\\":\\"Dispatcher\\"},{\\"key\\":\\"E101\\",\\"value\\":\\"Draftsman\\"},{\\"key\\":\\"E115\\",\\"value\\":\\"Estimator\\"},{\\"key\\":\\"E116\\",\\"value\\":\\"Expeditor\\"},{\\"key\\":\\"E120\\",\\"value\\":\\"Finance\\"},{\\"key\\":\\"E125\\",\\"value\\":\\"Flight Attendent/Steward/Ess\\"},{\\"key\\":\\"E126\\",\\"value\\":\\"Florist\\"},{\\"key\\":\\"E137\\",\\"value\\":\\"Graphic Designer/Commercial Art\\"},{\\"key\\":\\"E144\\",\\"value\\":\\"Hostess/Host/Usher\\"},{\\"key\\":\\"E149\\",\\"value\\":\\"Insurance/Agent\\"},{\\"key\\":\\"E150\\",\\"value\\":\\"Insurance/Underwriter\\"},{\\"key\\":\\"E151\\",\\"value\\":\\"Interior Designer\\"},{\\"key\\":\\"E154\\",\\"value\\":\\"Jeweler\\"},{\\"key\\":\\"E191\\",\\"value\\":\\"Marketing\\"},{\\"key\\":\\"E200\\",\\"value\\":\\"Merchandiser\\"},{\\"key\\":\\"E205\\",\\"value\\":\\"Model\\"},{\\"key\\":\\"E207\\",\\"value\\":\\"Musician/Music/Dance\\"},{\\"key\\":\\"E227\\",\\"value\\":\\"Personnel/Recruiter/Interviewer\\"},{\\"key\\":\\"E229\\",\\"value\\":\\"Photography\\"},{\\"key\\":\\"E246\\",\\"value\\":\\"Public Relations\\"},{\\"key\\":\\"E247\\",\\"value\\":\\"Publishing\\"},{\\"key\\":\\"E248\\",\\"value\\":\\"Purchasing\\"},{\\"key\\":\\"E249\\",\\"value\\":\\"Quality Control\\"},{\\"key\\":\\"E250\\",\\"value\\":\\"Real Estate/Realtor\\"},{\\"key\\":\\"E251\\",\\"value\\":\\"Receptionist\\"},{\\"key\\":\\"E253\\",\\"value\\":\\"Reporter\\"},{\\"key\\":\\"E254\\",\\"value\\":\\"Researcher\\"},{\\"key\\":\\"E257\\",\\"value\\":\\"Sales\\"},{\\"key\\":\\"E258\\",\\"value\\":\\"Sales Clerk/Counterman\\"},{\\"key\\":\\"E263\\",\\"value\\":\\"Security\\"},{\\"key\\":\\"E274\\",\\"value\\":\\"Surveyor\\"},{\\"key\\":\\"E276\\",\\"value\\":\\"Technician\\"},{\\"key\\":\\"E279\\",\\"value\\":\\"Telemarket/Telephone/Operator\\"},{\\"key\\":\\"E280\\",\\"value\\":\\"Teller/Bank Teller\\"},{\\"key\\":\\"E281\\",\\"value\\":\\"Tester\\"},{\\"key\\":\\"E286\\",\\"value\\":\\"Transcripter/Translator\\"},{\\"key\\":\\"E288\\",\\"value\\":\\"Travel Agent\\"},{\\"key\\":\\"E292\\",\\"value\\":\\"Union Member/Rep.\\"},{\\"key\\":\\"E299\\",\\"value\\":\\"Ward Clerk\\"},{\\"key\\":\\"E300\\",\\"value\\":\\"Water Treatment\\"},{\\"key\\":\\"E302\\",\\"value\\":\\"Writer\\"},{\\"key\\":\\"F017\\",\\"value\\":\\"Animal Technician/Groomer\\"},{\\"key\\":\\"F019\\",\\"value\\":\\"Apprentice\\"},{\\"key\\":\\"F023\\",\\"value\\":\\"Assembler\\"},{\\"key\\":\\"F024\\",\\"value\\":\\"Athlete/Professional\\"},{\\"key\\":\\"F025\\",\\"value\\":\\"Attendant\\"},{\\"key\\":\\"F028\\",\\"value\\":\\"Auto Mechanic\\"},{\\"key\\":\\"F029\\",\\"value\\":\\"Baker\\"},{\\"key\\":\\"F033\\",\\"value\\":\\"Barber/Hairstylist/Beautician\\"},{\\"key\\":\\"F034\\",\\"value\\":\\"Bartender\\"},{\\"key\\":\\"F035\\",\\"value\\":\\"Binder\\"},{\\"key\\":\\"F036\\",\\"value\\":\\"Bodyman\\"},{\\"key\\":\\"F038\\",\\"value\\":\\"Brakeman\\"},{\\"key\\":\\"F039\\",\\"value\\":\\"Brewer\\"},{\\"key\\":\\"F043\\",\\"value\\":\\"Butcher/Meat Cutter\\"},{\\"key\\":\\"F045\\",\\"value\\":\\"Carpenter/Furniture/Woodworking\\"},{\\"key\\":\\"F050\\",\\"value\\":\\"Chef/Butler\\"},{\\"key\\":\\"F052\\",\\"value\\":\\"Child Care/Day Care/Babysitter\\"},{\\"key\\":\\"F056\\",\\"value\\":\\"Cleaner/Laundry\\"},{\\"key\\":\\"F058\\",\\"value\\":\\"Clerk/Deli\\"},{\\"key\\":\\"F060\\",\\"value\\":\\"Clerk/Produce\\"},{\\"key\\":\\"F061\\",\\"value\\":\\"Clerk/Stock\\"},{\\"key\\":\\"F069\\",\\"value\\":\\"Conductor\\"},{\\"key\\":\\"F071\\",\\"value\\":\\"Construction\\"},{\\"key\\":\\"F075\\",\\"value\\":\\"Cook\\"},{\\"key\\":\\"F078\\",\\"value\\":\\"Cosmetologist\\"},{\\"key\\":\\"F080\\",\\"value\\":\\"Courier/Delivery/Messenger\\"},{\\"key\\":\\"F082\\",\\"value\\":\\"Crewman\\"},{\\"key\\":\\"F084\\",\\"value\\":\\"Custodian\\"},{\\"key\\":\\"F086\\",\\"value\\":\\"Cutter\\"},{\\"key\\":\\"F100\\",\\"value\\":\\"Dock Worker\\"},{\\"key\\":\\"F102\\",\\"value\\":\\"Driver\\"},{\\"key\\":\\"F103\\",\\"value\\":\\"Driver/Bus Driver\\"},{\\"key\\":\\"F104\\",\\"value\\":\\"Driver/Truck Driver\\"},{\\"key\\":\\"F106\\",\\"value\\":\\"Electrician\\"},{\\"key\\":\\"F117\\",\\"value\\":\\"Fabricator\\"},{\\"key\\":\\"F118\\",\\"value\\":\\"Factory Worker\\"},{\\"key\\":\\"F119\\",\\"value\\":\\"Farmer/Dairyman\\"},{\\"key\\":\\"F121\\",\\"value\\":\\"Finisher\\"},{\\"key\\":\\"F123\\",\\"value\\":\\"Fisherman/Seaman\\"},{\\"key\\":\\"F124\\",\\"value\\":\\"Fitter\\"},{\\"key\\":\\"F127\\",\\"value\\":\\"Food Service\\"},{\\"key\\":\\"F128\\",\\"value\\":\\"Foreman/Crewleader\\"},{\\"key\\":\\"F129\\",\\"value\\":\\"Foreman/Shop Foreman\\"},{\\"key\\":\\"F130\\",\\"value\\":\\"Forestry\\"},{\\"key\\":\\"F131\\",\\"value\\":\\"Foundry Worker\\"},{\\"key\\":\\"F133\\",\\"value\\":\\"Furrier\\"},{\\"key\\":\\"F134\\",\\"value\\":\\"Gardener/Landscaper\\"},{\\"key\\":\\"F136\\",\\"value\\":\\"Glazier\\"},{\\"key\\":\\"F138\\",\\"value\\":\\"Grinder\\"},{\\"key\\":\\"F139\\",\\"value\\":\\"Grocer\\"},{\\"key\\":\\"F141\\",\\"value\\":\\"Helper\\"},{\\"key\\":\\"F145\\",\\"value\\":\\"Housekeeper/Maid\\"},{\\"key\\":\\"F146\\",\\"value\\":\\"Inspector\\"},{\\"key\\":\\"F147\\",\\"value\\":\\"Installer\\"},{\\"key\\":\\"F152\\",\\"value\\":\\"Ironworker\\"},{\\"key\\":\\"F153\\",\\"value\\":\\"Janitor\\"},{\\"key\\":\\"F155\\",\\"value\\":\\"Journeyman\\"},{\\"key\\":\\"F157\\",\\"value\\":\\"Laborer\\"},{\\"key\\":\\"F163\\",\\"value\\":\\"Lineman\\"},{\\"key\\":\\"F164\\",\\"value\\":\\"Lithographer\\"},{\\"key\\":\\"F165\\",\\"value\\":\\"Loader\\"},{\\"key\\":\\"F166\\",\\"value\\":\\"Locksmith\\"},{\\"key\\":\\"F167\\",\\"value\\":\\"Machinist\\"},{\\"key\\":\\"F170\\",\\"value\\":\\"Maintenance\\"},{\\"key\\":\\"F171\\",\\"value\\":\\"Maintenance Superv\\"},{\\"key\\":\\"F192\\",\\"value\\":\\"Mason/Brick/Etc\\"},{\\"key\\":\\"F193\\",\\"value\\":\\"Material Handler\\"},{\\"key\\":\\"F194\\",\\"value\\":\\"Mechanic\\"},{\\"key\\":\\"F201\\",\\"value\\":\\"Meter Reading\\"},{\\"key\\":\\"F202\\",\\"value\\":\\"Millworker\\"},{\\"key\\":\\"F203\\",\\"value\\":\\"Millwright\\"},{\\"key\\":\\"F204\\",\\"value\\":\\"Miner\\"},{\\"key\\":\\"F206\\",\\"value\\":\\"Mold Maker/Molder/Injection Mol\\"},{\\"key\\":\\"F212\\",\\"value\\":\\"Oil Industry/Driller\\"},{\\"key\\":\\"F213\\",\\"value\\":\\"Operator\\"},{\\"key\\":\\"F214\\",\\"value\\":\\"Operator/Boilermaker\\"},{\\"key\\":\\"F215\\",\\"value\\":\\"Operator/Crane Operator\\"},{\\"key\\":\\"F216\\",\\"value\\":\\"Operator/Forklift Operator\\"},{\\"key\\":\\"F217\\",\\"value\\":\\"Operator/Machine Operator\\"},{\\"key\\":\\"F221\\",\\"value\\":\\"Packer\\"},{\\"key\\":\\"F222\\",\\"value\\":\\"Painter\\"},{\\"key\\":\\"F225\\",\\"value\\":\\"Parts (Auto Etc)\\"},{\\"key\\":\\"F231\\",\\"value\\":\\"Pipefitter\\"},{\\"key\\":\\"F233\\",\\"value\\":\\"Plumber\\"},{\\"key\\":\\"F235\\",\\"value\\":\\"Polisher\\"},{\\"key\\":\\"F237\\",\\"value\\":\\"Porter\\"},{\\"key\\":\\"F239\\",\\"value\\":\\"Press Operator\\"},{\\"key\\":\\"F240\\",\\"value\\":\\"Presser\\"},{\\"key\\":\\"F242\\",\\"value\\":\\"Printer\\"},{\\"key\\":\\"F243\\",\\"value\\":\\"Production\\"},{\\"key\\":\\"F252\\",\\"value\\":\\"Repairman\\"},{\\"key\\":\\"F256\\",\\"value\\":\\"Roofer\\"},{\\"key\\":\\"F259\\",\\"value\\":\\"Sanitation/Exterminator\\"},{\\"key\\":\\"F261\\",\\"value\\":\\"Seamstress/Tailor/Handicraft\\"},{\\"key\\":\\"F265\\",\\"value\\":\\"Setupman\\"},{\\"key\\":\\"F266\\",\\"value\\":\\"Sheet Metal Worker/Steel Worker\\"},{\\"key\\":\\"F267\\",\\"value\\":\\"Shipping/Import/Export/Custom/C\\"},{\\"key\\":\\"F269\\",\\"value\\":\\"Sorter\\"},{\\"key\\":\\"F284\\",\\"value\\":\\"Toolmaker\\"},{\\"key\\":\\"F287\\",\\"value\\":\\"Transportation\\"},{\\"key\\":\\"F290\\",\\"value\\":\\"Typesetter\\"},{\\"key\\":\\"F293\\",\\"value\\":\\"Upholstery\\"},{\\"key\\":\\"F294\\",\\"value\\":\\"Utility\\"},{\\"key\\":\\"F298\\",\\"value\\":\\"Waiter/Waitress\\"},{\\"key\\":\\"F301\\",\\"value\\":\\"Welder\\"},{\\"key\\":\\"G053\\",\\"value\\":\\"Chiropractor\\"},{\\"key\\":\\"G089\\",\\"value\\":\\"Dental Assistant\\"},{\\"key\\":\\"G090\\",\\"value\\":\\"Dental Hygenist\\"},{\\"key\\":\\"G091\\",\\"value\\":\\"Dentist\\"},{\\"key\\":\\"G094\\",\\"value\\":\\"Dietician\\"},{\\"key\\":\\"G140\\",\\"value\\":\\"Health Care\\"},{\\"key\\":\\"G195\\",\\"value\\":\\"Medical Assistant\\"},{\\"key\\":\\"G197\\",\\"value\\":\\"Medical Secretary\\"},{\\"key\\":\\"G198\\",\\"value\\":\\"Medical Technician\\"},{\\"key\\":\\"G199\\",\\"value\\":\\"Medical/Paramedic\\"},{\\"key\\":\\"G211\\",\\"value\\":\\"Nurses Aide/Orderly\\"},{\\"key\\":\\"G218\\",\\"value\\":\\"Optician\\"},{\\"key\\":\\"G219\\",\\"value\\":\\"Optometrist\\"},{\\"key\\":\\"G228\\",\\"value\\":\\"Pharmacist/Pharmacy\\"},{\\"key\\":\\"G245\\",\\"value\\":\\"Psychologist\\"},{\\"key\\":\\"G277\\",\\"value\\":\\"Technician/Lab\\"},{\\"key\\":\\"G278\\",\\"value\\":\\"Technician/Xray\\"},{\\"key\\":\\"G282\\",\\"value\\":\\"Therapist\\"},{\\"key\\":\\"G283\\",\\"value\\":\\"Therapist/Physical\\"},{\\"key\\":\\"H160\\",\\"value\\":\\"Legal/Paralegal/Assistant/Law C\\"},{\\"key\\":\\"H161\\",\\"value\\":\\"Legal/Secretary\\"},{\\"key\\":\\"H291\\",\\"value\\":\\"Typist\\"},{\\"key\\":\\"J255\\",\\"value\\":\\"Retired/Pensioner\\"},{\\"key\\":\\"K022\\",\\"value\\":\\"Army Credit Union Trades\\"},{\\"key\\":\\"K023\\",\\"value\\":\\"Navy Credit Union Trades\\"},{\\"key\\":\\"L062\\",\\"value\\":\\"Coach\\"},{\\"key\\":\\"L079\\",\\"value\\":\\"Counselor\\"},{\\"key\\":\\"L148\\",\\"value\\":\\"Instructor\\"},{\\"key\\":\\"L158\\",\\"value\\":\\"Lecturer\\"},{\\"key\\":\\"L244\\",\\"value\\":\\"Professor\\"},{\\"key\\":\\"L285\\",\\"value\\":\\"Trainer\\"},{\\"key\\":\\"M209\\",\\"value\\":\\"Nurse (Registered)\\"},{\\"key\\":\\"M210\\",\\"value\\":\\"Nurse/LPN\\"},{\\"key\\":\\"N066\\",\\"value\\":\\"Computer Operator\\"},{\\"key\\":\\"N067\\",\\"value\\":\\"Computer Programmer\\"},{\\"key\\":\\"N068\\",\\"value\\":\\"Computer/Systems Analyst\\"},{\\"key\\":\\"N087\\",\\"value\\":\\"Data Entry/Key Punch\\"},{\\"key\\":\\"O015\\",\\"value\\":\\"Air Traffic Control\\"},{\\"key\\":\\"O054\\",\\"value\\":\\"Civil Service/Govt\\"},{\\"key\\":\\"O077\\",\\"value\\":\\"Corrections/Probation/Parole/Ma\\"},{\\"key\\":\\"O081\\",\\"value\\":\\"Court Reporter\\"},{\\"key\\":\\"O122\\",\\"value\\":\\"Firefighter\\"},{\\"key\\":\\"O156\\",\\"value\\":\\"Judge/Referee\\"},{\\"key\\":\\"O168\\",\\"value\\":\\"Mailcarrier/Postal\\"},{\\"key\\":\\"O169\\",\\"value\\":\\"Mail/Postmaster\\"},{\\"key\\":\\"O234\\",\\"value\\":\\"Police/Trooper\\"},{\\"key\\":\\"O268\\",\\"value\\":\\"Social Worker/Case Worker\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10104,
      name: 'Parents of Children Age 0-3',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10107,
      name: 'Parents of Children Age 10-12',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10108,
      name: 'Parents of Children Age 13-18',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10105,
      name: 'Parents of Children Age 4-6',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10106,
      name: 'Parents of Children Age 7-9',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10103,
      name: 'Presence Of Children',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10088,
      name: 'Race',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more race options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"H\\",\\"value\\":\\"Hispanic\\"},{\\"key\\":\\"W\\",\\"value\\":\\"Caucasian\\"},{\\"key\\":\\"N\\",\\"value\\":\\"Native American\\"},{\\"key\\":\\"B\\",\\"value\\":\\"African-American\\"},{\\"key\\":\\"A\\",\\"value\\":\\"Asian\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10091,
      name: 'Religion',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select religion(s)...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"value\\":\\"BAPTIST\\",\\"label\\":\\"BAPTIST\\"},{\\"value\\":\\"BUDDHIST\\",\\"label\\":\\"BUDDHIST\\"},{\\"value\\":\\"CATHOLIC\\",\\"label\\":\\"CATHOLIC\\"},{\\"value\\":\\"CHRISTIAN\\",\\"label\\":\\"CHRISTIAN\\"},{\\"value\\":\\"CHRISTIAN ASSYRIAN\\",\\"label\\":\\"CHRISTIAN ASSYRIAN\\"},{\\"value\\":\\"CHRISTIAN MARONITE\\",\\"label\\":\\"CHRISTIAN MARONITE\\"},{\\"value\\":\\"CHRISTIAN ORTHODOX\\",\\"label\\":\\"CHRISTIAN ORTHODOX\\"},{\\"value\\":\\"HINDU\\",\\"label\\":\\"HINDU\\"},{\\"value\\":\\"JAIN\\",\\"label\\":\\"JAIN\\"},{\\"value\\":\\"JEWI\\",\\"label\\":\\"JEWI\\"},{\\"value\\":\\"JEWISH\\",\\"label\\":\\"JEWISH\\"},{\\"value\\":\\"MARONITE\\",\\"label\\":\\"MARONITE\\"},{\\"value\\":\\"MUSL\\",\\"label\\":\\"MUSL\\"},{\\"value\\":\\"MUSLIM\\",\\"label\\":\\"MUSLIM\\"},{\\"value\\":\\"MUSLIM SHIA\\",\\"label\\":\\"MUSLIM SHIA\\"},{\\"value\\":\\"MUSLIM SUNNI\\",\\"label\\":\\"MUSLIM SUNNI\\"},{\\"value\\":\\"ORTHODOX\\",\\"label\\":\\"ORTHODOX\\"},{\\"value\\":\\"PAKI\\",\\"label\\":\\"PAKI\\"},{\\"value\\":\\"PARSI\\",\\"label\\":\\"PARSI\\"},{\\"value\\":\\"PROTESTANT\\",\\"label\\":\\"PROTESTANT\\"},{\\"value\\":\\"PROTESTANT METHODIST\\",\\"label\\":\\"PROTESTANT METHODIST\\"},{\\"value\\":\\"PROTESTANT MOLOKAN\\",\\"label\\":\\"PROTESTANT MOLOKAN\\"},{\\"value\\":\\"REGIONAL\\",\\"label\\":\\"REGIONAL\\"},{\\"value\\":\\"SIKH\\",\\"label\\":\\"SIKH\\"},{\\"value\\":\\"X\\",\\"label\\":\\"X\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10097,
      name: 'White Collar',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more white collar options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"0\\",\\"value\\":\\"No\\"},{\\"key\\":\\"1\\",\\"value\\":\\"Yes\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
  ],
};

export const MOCK_STIRISTA_DATA_AUTOMOTIVE_ATTRIBUTES = {
  attributes: [
    {
      id: 10172,
      name: 'Auto Body Type Chassis',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10173,
      name: 'Auto Body Type Convertible',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10174,
      name: 'Auto Body Type Coupe',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10175,
      name: 'Auto Body Type Crossover',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10176,
      name: 'Auto Body Type Fullsize',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10177,
      name: 'Auto Body Type Hatchback',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10178,
      name: 'Auto Body Type Minivan',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10180,
      name: 'Auto Body Type SUV',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10179,
      name: 'Auto Body Type Sedan',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10181,
      name: 'Auto Body Type Truck',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10182,
      name: 'Auto Body Type Van',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10183,
      name: 'Auto Body Type Wagon',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10168,
      name: 'Auto Class Compact',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10169,
      name: 'Auto Class Exotic',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10170,
      name: 'Auto Class Luxury',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10171,
      name: 'Auto Class Mainstream',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10184,
      name: 'Auto Drive Type 4WD',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10185,
      name: 'Auto Drive Type 4X2',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10186,
      name: 'Auto Drive Type 4X4',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10187,
      name: 'Auto Drive Type AWD',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10188,
      name: 'Auto Drive Type FWD',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10189,
      name: 'Auto Drive Type RWD',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10163,
      name: 'Auto Fuel BioDiesel',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10164,
      name: 'Auto Fuel Diesel',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10165,
      name: 'Auto Fuel Flex Fuel',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10166,
      name: 'Auto Fuel Gasoline',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10167,
      name: 'Auto Fuel Hybrid',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10160,
      name: 'Auto Manufacturer Based Asia',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10161,
      name: 'Auto Manufacturer Based European',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10162,
      name: 'Auto Manufacturer Based USA',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10156,
      name: 'Auto Size Full Size',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10157,
      name: 'Auto Size Mid Size',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10158,
      name: 'Auto Size Small',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10159,
      name: 'Auto Size Sports',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10153,
      name: 'Auto Transmission Type Automatic',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10154,
      name: 'Auto Transmission Type CVT',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10155,
      name: 'Auto Transmission Type Manual',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10149,
      name: 'Auto Vehicle Type Car',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10150,
      name: 'Auto Vehicle Type SUV',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10151,
      name: 'Auto Vehicle Type Truck',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10152,
      name: 'Auto Vehicle Type Van',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"toggle\\",\\"default_value\\":true,\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10141,
      name: 'Make 1',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select vehicle make...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"value\\":\\"ACURA\\",\\"label\\":\\"ACURA\\"},{\\"value\\":\\"ALFA ROMEO\\",\\"label\\":\\"ALFA ROMEO\\"},{\\"value\\":\\"AM GENERAL\\",\\"label\\":\\"AM GENERAL\\"},{\\"value\\":\\"AMC\\",\\"label\\":\\"AMC\\"},{\\"value\\":\\"AMERICAN MOTORS\\",\\"label\\":\\"AMERICAN MOTORS\\"},{\\"value\\":\\"ASTON MARTIN\\",\\"label\\":\\"ASTON MARTIN\\"},{\\"value\\":\\"AUDI\\",\\"label\\":\\"AUDI\\"},{\\"value\\":\\"BENTLEY\\",\\"label\\":\\"BENTLEY\\"},{\\"value\\":\\"BMW\\",\\"label\\":\\"BMW\\"},{\\"value\\":\\"BUICK\\",\\"label\\":\\"BUICK\\"},{\\"value\\":\\"CADILLAC\\",\\"label\\":\\"CADILLAC\\"},{\\"value\\":\\"CHEVROLET\\",\\"label\\":\\"CHEVROLET\\"},{\\"value\\":\\"CHRYSLER\\",\\"label\\":\\"CHRYSLER\\"},{\\"value\\":\\"DAEWOO\\",\\"label\\":\\"DAEWOO\\"},{\\"value\\":\\"DAIHATSU\\",\\"label\\":\\"DAIHATSU\\"},{\\"value\\":\\"DATSUN\\",\\"label\\":\\"DATSUN\\"},{\\"value\\":\\"DELOREAN\\",\\"label\\":\\"DELOREAN\\"},{\\"value\\":\\"DODGE\\",\\"label\\":\\"DODGE\\"},{\\"value\\":\\"EAGLE\\",\\"label\\":\\"EAGLE\\"},{\\"value\\":\\"FERRARI\\",\\"label\\":\\"FERRARI\\"},{\\"value\\":\\"FIAT\\",\\"label\\":\\"FIAT\\"},{\\"value\\":\\"FISKER AUTOMOTIVE\\",\\"label\\":\\"FISKER AUTOMOTIVE\\"},{\\"value\\":\\"FORD\\",\\"label\\":\\"FORD\\"},{\\"value\\":\\"GENESIS\\",\\"label\\":\\"GENESIS\\"},{\\"value\\":\\"GEO\\",\\"label\\":\\"GEO\\"},{\\"value\\":\\"GMC\\",\\"label\\":\\"GMC\\"},{\\"value\\":\\"HONDA\\",\\"label\\":\\"HONDA\\"},{\\"value\\":\\"HUMMER\\",\\"label\\":\\"HUMMER\\"},{\\"value\\":\\"HYUNDAI\\",\\"label\\":\\"HYUNDAI\\"},{\\"value\\":\\"INFINITI\\",\\"label\\":\\"INFINITI\\"},{\\"value\\":\\"ISUZU\\",\\"label\\":\\"ISUZU\\"},{\\"value\\":\\"JAGUAR\\",\\"label\\":\\"JAGUAR\\"},{\\"value\\":\\"JEEP\\",\\"label\\":\\"JEEP\\"},{\\"value\\":\\"KARMA\\",\\"label\\":\\"KARMA\\"},{\\"value\\":\\"KIA\\",\\"label\\":\\"KIA\\"},{\\"value\\":\\"LAMBORGHINI\\",\\"label\\":\\"LAMBORGHINI\\"},{\\"value\\":\\"LAND ROVER\\",\\"label\\":\\"LAND ROVER\\"},{\\"value\\":\\"LEXUS\\",\\"label\\":\\"LEXUS\\"},{\\"value\\":\\"LINCOLN\\",\\"label\\":\\"LINCOLN\\"},{\\"value\\":\\"LOTUS\\",\\"label\\":\\"LOTUS\\"},{\\"value\\":\\"MASERATI\\",\\"label\\":\\"MASERATI\\"},{\\"value\\":\\"MAYBACH\\",\\"label\\":\\"MAYBACH\\"},{\\"value\\":\\"MAZDA\\",\\"label\\":\\"MAZDA\\"},{\\"value\\":\\"MCLAREN\\",\\"label\\":\\"MCLAREN\\"},{\\"value\\":\\"MERCEDES-BENZ\\",\\"label\\":\\"MERCEDES-BENZ\\"},{\\"value\\":\\"MERCURY\\",\\"label\\":\\"MERCURY\\"},{\\"value\\":\\"MERKUR\\",\\"label\\":\\"MERKUR\\"},{\\"value\\":\\"MINI\\",\\"label\\":\\"MINI\\"},{\\"value\\":\\"MITSUBISHI\\",\\"label\\":\\"MITSUBISHI\\"},{\\"value\\":\\"NISSAN\\",\\"label\\":\\"NISSAN\\"},{\\"value\\":\\"OLDSMOBILE\\",\\"label\\":\\"OLDSMOBILE\\"},{\\"value\\":\\"PANOZ\\",\\"label\\":\\"PANOZ\\"},{\\"value\\":\\"PININFARINA\\",\\"label\\":\\"PININFARINA\\"},{\\"value\\":\\"PLYMOUTH\\",\\"label\\":\\"PLYMOUTH\\"},{\\"value\\":\\"PONTIAC\\",\\"label\\":\\"PONTIAC\\"},{\\"value\\":\\"PORSCHE\\",\\"label\\":\\"PORSCHE\\"},{\\"value\\":\\"RAM\\",\\"label\\":\\"RAM\\"},{\\"value\\":\\"RENAULT\\",\\"label\\":\\"RENAULT\\"},{\\"value\\":\\"RIVIAN\\",\\"label\\":\\"RIVIAN\\"},{\\"value\\":\\"ROLLS-ROYCE\\",\\"label\\":\\"ROLLS-ROYCE\\"},{\\"value\\":\\"SAAB\\",\\"label\\":\\"SAAB\\"},{\\"value\\":\\"SATURN\\",\\"label\\":\\"SATURN\\"},{\\"value\\":\\"SCION\\",\\"label\\":\\"SCION\\"},{\\"value\\":\\"SMART\\",\\"label\\":\\"SMART\\"},{\\"value\\":\\"SUBARU\\",\\"label\\":\\"SUBARU\\"},{\\"value\\":\\"SUZUKI\\",\\"label\\":\\"SUZUKI\\"},{\\"value\\":\\"TESLA\\",\\"label\\":\\"TESLA\\"},{\\"value\\":\\"TOYOTA\\",\\"label\\":\\"TOYOTA\\"},{\\"value\\":\\"VOLKSWAGEN\\",\\"label\\":\\"VOLKSWAGEN\\"},{\\"value\\":\\"VOLVO\\",\\"label\\":\\"VOLVO\\"},{\\"value\\":\\"WAGONEER\\",\\"label\\":\\"WAGONEER\\"},{\\"value\\":\\"YUGO\\",\\"label\\":\\"YUGO\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10145,
      name: 'Make 2',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select vehicle make...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"value\\":\\"ACURA\\",\\"label\\":\\"ACURA\\"},{\\"value\\":\\"ALFA ROMEO\\",\\"label\\":\\"ALFA ROMEO\\"},{\\"value\\":\\"AM GENERAL\\",\\"label\\":\\"AM GENERAL\\"},{\\"value\\":\\"AMC\\",\\"label\\":\\"AMC\\"},{\\"value\\":\\"AMERICAN MOTORS\\",\\"label\\":\\"AMERICAN MOTORS\\"},{\\"value\\":\\"ASTON MARTIN\\",\\"label\\":\\"ASTON MARTIN\\"},{\\"value\\":\\"AUDI\\",\\"label\\":\\"AUDI\\"},{\\"value\\":\\"BENTLEY\\",\\"label\\":\\"BENTLEY\\"},{\\"value\\":\\"BMW\\",\\"label\\":\\"BMW\\"},{\\"value\\":\\"BUICK\\",\\"label\\":\\"BUICK\\"},{\\"value\\":\\"CADILLAC\\",\\"label\\":\\"CADILLAC\\"},{\\"value\\":\\"CHEVROLET\\",\\"label\\":\\"CHEVROLET\\"},{\\"value\\":\\"CHRYSLER\\",\\"label\\":\\"CHRYSLER\\"},{\\"value\\":\\"DAEWOO\\",\\"label\\":\\"DAEWOO\\"},{\\"value\\":\\"DAIHATSU\\",\\"label\\":\\"DAIHATSU\\"},{\\"value\\":\\"DATSUN\\",\\"label\\":\\"DATSUN\\"},{\\"value\\":\\"DELOREAN\\",\\"label\\":\\"DELOREAN\\"},{\\"value\\":\\"DODGE\\",\\"label\\":\\"DODGE\\"},{\\"value\\":\\"EAGLE\\",\\"label\\":\\"EAGLE\\"},{\\"value\\":\\"FERRARI\\",\\"label\\":\\"FERRARI\\"},{\\"value\\":\\"FIAT\\",\\"label\\":\\"FIAT\\"},{\\"value\\":\\"FISKER AUTOMOTIVE\\",\\"label\\":\\"FISKER AUTOMOTIVE\\"},{\\"value\\":\\"FORD\\",\\"label\\":\\"FORD\\"},{\\"value\\":\\"GENESIS\\",\\"label\\":\\"GENESIS\\"},{\\"value\\":\\"GEO\\",\\"label\\":\\"GEO\\"},{\\"value\\":\\"GMC\\",\\"label\\":\\"GMC\\"},{\\"value\\":\\"HONDA\\",\\"label\\":\\"HONDA\\"},{\\"value\\":\\"HUMMER\\",\\"label\\":\\"HUMMER\\"},{\\"value\\":\\"HYUNDAI\\",\\"label\\":\\"HYUNDAI\\"},{\\"value\\":\\"INFINITI\\",\\"label\\":\\"INFINITI\\"},{\\"value\\":\\"ISUZU\\",\\"label\\":\\"ISUZU\\"},{\\"value\\":\\"JAGUAR\\",\\"label\\":\\"JAGUAR\\"},{\\"value\\":\\"JEEP\\",\\"label\\":\\"JEEP\\"},{\\"value\\":\\"KARMA\\",\\"label\\":\\"KARMA\\"},{\\"value\\":\\"KIA\\",\\"label\\":\\"KIA\\"},{\\"value\\":\\"LAMBORGHINI\\",\\"label\\":\\"LAMBORGHINI\\"},{\\"value\\":\\"LAND ROVER\\",\\"label\\":\\"LAND ROVER\\"},{\\"value\\":\\"LEXUS\\",\\"label\\":\\"LEXUS\\"},{\\"value\\":\\"LINCOLN\\",\\"label\\":\\"LINCOLN\\"},{\\"value\\":\\"LOTUS\\",\\"label\\":\\"LOTUS\\"},{\\"value\\":\\"MASERATI\\",\\"label\\":\\"MASERATI\\"},{\\"value\\":\\"MAYBACH\\",\\"label\\":\\"MAYBACH\\"},{\\"value\\":\\"MAZDA\\",\\"label\\":\\"MAZDA\\"},{\\"value\\":\\"MCLAREN\\",\\"label\\":\\"MCLAREN\\"},{\\"value\\":\\"MERCEDES-BENZ\\",\\"label\\":\\"MERCEDES-BENZ\\"},{\\"value\\":\\"MERCURY\\",\\"label\\":\\"MERCURY\\"},{\\"value\\":\\"MERKUR\\",\\"label\\":\\"MERKUR\\"},{\\"value\\":\\"MINI\\",\\"label\\":\\"MINI\\"},{\\"value\\":\\"MITSUBISHI\\",\\"label\\":\\"MITSUBISHI\\"},{\\"value\\":\\"NISSAN\\",\\"label\\":\\"NISSAN\\"},{\\"value\\":\\"OLDSMOBILE\\",\\"label\\":\\"OLDSMOBILE\\"},{\\"value\\":\\"PANOZ\\",\\"label\\":\\"PANOZ\\"},{\\"value\\":\\"PININFARINA\\",\\"label\\":\\"PININFARINA\\"},{\\"value\\":\\"PLYMOUTH\\",\\"label\\":\\"PLYMOUTH\\"},{\\"value\\":\\"PONTIAC\\",\\"label\\":\\"PONTIAC\\"},{\\"value\\":\\"PORSCHE\\",\\"label\\":\\"PORSCHE\\"},{\\"value\\":\\"RAM\\",\\"label\\":\\"RAM\\"},{\\"value\\":\\"RENAULT\\",\\"label\\":\\"RENAULT\\"},{\\"value\\":\\"RIVIAN\\",\\"label\\":\\"RIVIAN\\"},{\\"value\\":\\"ROLLS-ROYCE\\",\\"label\\":\\"ROLLS-ROYCE\\"},{\\"value\\":\\"SAAB\\",\\"label\\":\\"SAAB\\"},{\\"value\\":\\"SATURN\\",\\"label\\":\\"SATURN\\"},{\\"value\\":\\"SCION\\",\\"label\\":\\"SCION\\"},{\\"value\\":\\"SMART\\",\\"label\\":\\"SMART\\"},{\\"value\\":\\"SUBARU\\",\\"label\\":\\"SUBARU\\"},{\\"value\\":\\"SUZUKI\\",\\"label\\":\\"SUZUKI\\"},{\\"value\\":\\"TESLA\\",\\"label\\":\\"TESLA\\"},{\\"value\\":\\"TOYOTA\\",\\"label\\":\\"TOYOTA\\"},{\\"value\\":\\"VOLKSWAGEN\\",\\"label\\":\\"VOLKSWAGEN\\"},{\\"value\\":\\"VOLVO\\",\\"label\\":\\"VOLVO\\"},{\\"value\\":\\"WAGONEER\\",\\"label\\":\\"WAGONEER\\"},{\\"value\\":\\"YUGO\\",\\"label\\":\\"YUGO\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10142,
      name: 'Model 1',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select vehicle model...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"value\\":\\"F-150\\",\\"label\\":\\"F-150\\"},{\\"value\\":\\"ACCORD\\",\\"label\\":\\"ACCORD\\"},{\\"value\\":\\"CAMRY\\",\\"label\\":\\"CAMRY\\"},{\\"value\\":\\"CIVIC\\",\\"label\\":\\"CIVIC\\"},{\\"value\\":\\"COROLLA\\",\\"label\\":\\"COROLLA\\"},{\\"value\\":\\"SILVERADO\\",\\"label\\":\\"SILVERADO\\"},{\\"value\\":\\"CR-V\\",\\"label\\":\\"CR-V\\"},{\\"value\\":\\"ALTIMA\\",\\"label\\":\\"ALTIMA\\"},{\\"value\\":\\"ESCAPE\\",\\"label\\":\\"ESCAPE\\"},{\\"value\\":\\"EXPLORER\\",\\"label\\":\\"EXPLORER\\"},{\\"value\\":\\"FOCUS\\",\\"label\\":\\"FOCUS\\"},{\\"value\\":\\"SILVERADO 1500\\",\\"label\\":\\"SILVERADO 1500\\"},{\\"value\\":\\"GRAND CHEROKEE\\",\\"label\\":\\"GRAND CHEROKEE\\"},{\\"value\\":\\"RAV4\\",\\"label\\":\\"RAV4\\"},{\\"value\\":\\"EQUINOX\\",\\"label\\":\\"EQUINOX\\"},{\\"value\\":\\"MALIBU\\",\\"label\\":\\"MALIBU\\"},{\\"value\\":\\"SONATA\\",\\"label\\":\\"SONATA\\"},{\\"value\\":\\"FUSION\\",\\"label\\":\\"FUSION\\"},{\\"value\\":\\"IMPALA\\",\\"label\\":\\"IMPALA\\"},{\\"value\\":\\"TAURUS\\",\\"label\\":\\"TAURUS\\"},{\\"value\\":\\"ELANTRA\\",\\"label\\":\\"ELANTRA\\"},{\\"value\\":\\"ODYSSEY\\",\\"label\\":\\"ODYSSEY\\"},{\\"value\\":\\"TACOMA\\",\\"label\\":\\"TACOMA\\"},{\\"value\\":\\"1500\\",\\"label\\":\\"1500\\"},{\\"value\\":\\"HIGHLANDER\\",\\"label\\":\\"HIGHLANDER\\"},{\\"value\\":\\"JETTA\\",\\"label\\":\\"JETTA\\"},{\\"value\\":\\"SENTRA\\",\\"label\\":\\"SENTRA\\"},{\\"value\\":\\"TAHOE\\",\\"label\\":\\"TAHOE\\"},{\\"value\\":\\"PILOT\\",\\"label\\":\\"PILOT\\"},{\\"value\\":\\"ROGUE\\",\\"label\\":\\"ROGUE\\"},{\\"value\\":\\"TRAILBLAZER\\",\\"label\\":\\"TRAILBLAZER\\"},{\\"value\\":\\"MUSTANG\\",\\"label\\":\\"MUSTANG\\"},{\\"value\\":\\"CRUZE\\",\\"label\\":\\"CRUZE\\"},{\\"value\\":\\"TOWN AND COUNTRY\\",\\"label\\":\\"TOWN AND COUNTRY\\"},{\\"value\\":\\"RANGER\\",\\"label\\":\\"RANGER\\"},{\\"value\\":\\"SIENNA\\",\\"label\\":\\"SIENNA\\"},{\\"value\\":\\"PRIUS\\",\\"label\\":\\"PRIUS\\"},{\\"value\\":\\"EDGE\\",\\"label\\":\\"EDGE\\"},{\\"value\\":\\"TUNDRA\\",\\"label\\":\\"TUNDRA\\"},{\\"value\\":\\"COLORADO\\",\\"label\\":\\"COLORADO\\"},{\\"value\\":\\"OUTBACK\\",\\"label\\":\\"OUTBACK\\"},{\\"value\\":\\"SONIC\\",\\"label\\":\\"SONIC\\"},{\\"value\\":\\"FORESTER\\",\\"label\\":\\"FORESTER\\"},{\\"value\\":\\"WRANGLER\\",\\"label\\":\\"WRANGLER\\"},{\\"value\\":\\"SIERRA\\",\\"label\\":\\"SIERRA\\"},{\\"value\\":\\"TRAVERSE\\",\\"label\\":\\"TRAVERSE\\"},{\\"value\\":\\"CHARGER\\",\\"label\\":\\"CHARGER\\"},{\\"value\\":\\"CARAVAN/GRAND CARAVAN\\",\\"label\\":\\"CARAVAN/GRAND CARAVAN\\"},{\\"value\\":\\"SORENTO\\",\\"label\\":\\"SORENTO\\"},{\\"value\\":\\"RAM\\",\\"label\\":\\"RAM\\"},{\\"value\\":\\"SIERRA 1500\\",\\"label\\":\\"SIERRA 1500\\"},{\\"value\\":\\"GRAND CARAVAN\\",\\"label\\":\\"GRAND CARAVAN\\"},{\\"value\\":\\"OPTIMA\\",\\"label\\":\\"OPTIMA\\"},{\\"value\\":\\"SUBURBAN\\",\\"label\\":\\"SUBURBAN\\"},{\\"value\\":\\"DURANGO\\",\\"label\\":\\"DURANGO\\"},{\\"value\\":\\"EXPEDITION\\",\\"label\\":\\"EXPEDITION\\"},{\\"value\\":\\"MAXIMA\\",\\"label\\":\\"MAXIMA\\"},{\\"value\\":\\"SANTA FE\\",\\"label\\":\\"SANTA FE\\"},{\\"value\\":\\"CHEROKEE\\",\\"label\\":\\"CHEROKEE\\"},{\\"value\\":\\"SOUL\\",\\"label\\":\\"SOUL\\"},{\\"value\\":\\"LUCERNE\\",\\"label\\":\\"LUCERNE\\"},{\\"value\\":\\"FORTE\\",\\"label\\":\\"FORTE\\"},{\\"value\\":\\"MAZDA3\\",\\"label\\":\\"MAZDA3\\"},{\\"value\\":\\"ACADIA\\",\\"label\\":\\"ACADIA\\"},{\\"value\\":\\"LIBERTY\\",\\"label\\":\\"LIBERTY\\"},{\\"value\\":\\"COBALT\\",\\"label\\":\\"COBALT\\"},{\\"value\\":\\"300\\",\\"label\\":\\"300\\"},{\\"value\\":\\"AVENGER\\",\\"label\\":\\"AVENGER\\"},{\\"value\\":\\"CAMARO\\",\\"label\\":\\"CAMARO\\"},{\\"value\\":\\"F-250\\",\\"label\\":\\"F-250\\"},{\\"value\\":\\"PASSAT\\",\\"label\\":\\"PASSAT\\"},{\\"value\\":\\"C-CLASS\\",\\"label\\":\\"C-CLASS\\"},{\\"value\\":\\"TERRAIN\\",\\"label\\":\\"TERRAIN\\"},{\\"value\\":\\"MURANO\\",\\"label\\":\\"MURANO\\"},{\\"value\\":\\"TUCSON\\",\\"label\\":\\"TUCSON\\"},{\\"value\\":\\"FRONTIER\\",\\"label\\":\\"FRONTIER\\"},{\\"value\\":\\"AVALON\\",\\"label\\":\\"AVALON\\"},{\\"value\\":\\"WRANGLER UNLIMITED\\",\\"label\\":\\"WRANGLER UNLIMITED\\"},{\\"value\\":\\"YUKON\\",\\"label\\":\\"YUKON\\"},{\\"value\\":\\"GRAND AM\\",\\"label\\":\\"GRAND AM\\"},{\\"value\\":\\"ACCENT\\",\\"label\\":\\"ACCENT\\"},{\\"value\\":\\"PATHFINDER\\",\\"label\\":\\"PATHFINDER\\"},{\\"value\\":\\"RX 350\\",\\"label\\":\\"RX 350\\"},{\\"value\\":\\"LEGACY\\",\\"label\\":\\"LEGACY\\"},{\\"value\\":\\"GRAND PRIX\\",\\"label\\":\\"GRAND PRIX\\"},{\\"value\\":\\"200\\",\\"label\\":\\"200\\"},{\\"value\\":\\"SEBRING\\",\\"label\\":\\"SEBRING\\"},{\\"value\\":\\"MDX\\",\\"label\\":\\"MDX\\"},{\\"value\\":\\"4RUNNER\\",\\"label\\":\\"4RUNNER\\"},{\\"value\\":\\"E-CLASS\\",\\"label\\":\\"E-CLASS\\"},{\\"value\\":\\"CHALLENGER\\",\\"label\\":\\"CHALLENGER\\"},{\\"value\\":\\"JOURNEY\\",\\"label\\":\\"JOURNEY\\"},{\\"value\\":\\"DAKOTA\\",\\"label\\":\\"DAKOTA\\"},{\\"value\\":\\"VERSA\\",\\"label\\":\\"VERSA\\"},{\\"value\\":\\"4-RUNNER\\",\\"label\\":\\"4-RUNNER\\"},{\\"value\\":\\"SPORTAGE\\",\\"label\\":\\"SPORTAGE\\"},{\\"value\\":\\"COMPASS\\",\\"label\\":\\"COMPASS\\"},{\\"value\\":\\"LACROSSE\\",\\"label\\":\\"LACROSSE\\"},{\\"value\\":\\"CX-5\\",\\"label\\":\\"CX-5\\"},{\\"value\\":\\"FREESTAR\\",\\"label\\":\\"FREESTAR\\"},{\\"value\\":\\"PT CRUISER\\",\\"label\\":\\"PT CRUISER\\"},{\\"value\\":\\"ENCLAVE\\",\\"label\\":\\"ENCLAVE\\"},{\\"value\\":\\"NEON\\",\\"label\\":\\"NEON\\"},{\\"value\\":\\"PACIFICA\\",\\"label\\":\\"PACIFICA\\"},{\\"value\\":\\"TL\\",\\"label\\":\\"TL\\"},{\\"value\\":\\"IMPREZA\\",\\"label\\":\\"IMPREZA\\"},{\\"value\\":\\"PATRIOT\\",\\"label\\":\\"PATRIOT\\"},{\\"value\\":\\"3 SERIES\\",\\"label\\":\\"3 SERIES\\"},{\\"value\\":\\"CENTURY\\",\\"label\\":\\"CENTURY\\"},{\\"value\\":\\"ENVOY\\",\\"label\\":\\"ENVOY\\"},{\\"value\\":\\"RAM CHASSIS CAB\\",\\"label\\":\\"RAM CHASSIS CAB\\"},{\\"value\\":\\"F-250 SUPER DUTY\\",\\"label\\":\\"F-250 SUPER DUTY\\"},{\\"value\\":\\"GRAND MARQUIS\\",\\"label\\":\\"GRAND MARQUIS\\"},{\\"value\\":\\"YUKON XL\\",\\"label\\":\\"YUKON XL\\"},{\\"value\\":\\"RX\\",\\"label\\":\\"RX\\"},{\\"value\\":\\"SILVERADO 2500HD\\",\\"label\\":\\"SILVERADO 2500HD\\"},{\\"value\\":\\"MAZDA6\\",\\"label\\":\\"MAZDA6\\"},{\\"value\\":\\"2500\\",\\"label\\":\\"2500\\"},{\\"value\\":\\"MONTE CARLO\\",\\"label\\":\\"MONTE CARLO\\"},{\\"value\\":\\"CT6\\",\\"label\\":\\"CT6\\"},{\\"value\\":\\"G6\\",\\"label\\":\\"G6\\"},{\\"value\\":\\"A4\\",\\"label\\":\\"A4\\"},{\\"value\\":\\"FIT\\",\\"label\\":\\"FIT\\"},{\\"value\\":\\"NITRO\\",\\"label\\":\\"NITRO\\"},{\\"value\\":\\"SRX\\",\\"label\\":\\"SRX\\"},{\\"value\\":\\"ES 350\\",\\"label\\":\\"ES 350\\"},{\\"value\\":\\"SABLE\\",\\"label\\":\\"SABLE\\"},{\\"value\\":\\"VUE\\",\\"label\\":\\"VUE\\"},{\\"value\\":\\"X5\\",\\"label\\":\\"X5\\"},{\\"value\\":\\"RIO\\",\\"label\\":\\"RIO\\"},{\\"value\\":\\"F-350\\",\\"label\\":\\"F-350\\"},{\\"value\\":\\"RDX\\",\\"label\\":\\"RDX\\"},{\\"value\\":\\"TOWN CAR\\",\\"label\\":\\"TOWN CAR\\"},{\\"value\\":\\"XTERRA\\",\\"label\\":\\"XTERRA\\"},{\\"value\\":\\"CTS\\",\\"label\\":\\"CTS\\"},{\\"value\\":\\"ESCALADE\\",\\"label\\":\\"ESCALADE\\"},{\\"value\\":\\"MALIBU CLASSIC\\",\\"label\\":\\"MALIBU CLASSIC\\"},{\\"value\\":\\"SEDONA\\",\\"label\\":\\"SEDONA\\"},{\\"value\\":\\"HHR\\",\\"label\\":\\"HHR\\"},{\\"value\\":\\"FIESTA\\",\\"label\\":\\"FIESTA\\"},{\\"value\\":\\"REGAL\\",\\"label\\":\\"REGAL\\"},{\\"value\\":\\"ESCORT\\",\\"label\\":\\"ESCORT\\"},{\\"value\\":\\"CANYON\\",\\"label\\":\\"CANYON\\"},{\\"value\\":\\"RAM 1500\\",\\"label\\":\\"RAM 1500\\"},{\\"value\\":\\"CROSSTREK\\",\\"label\\":\\"CROSSTREK\\"},{\\"value\\":\\"TITAN\\",\\"label\\":\\"TITAN\\"},{\\"value\\":\\"CALIBER\\",\\"label\\":\\"CALIBER\\"},{\\"value\\":\\"ES\\",\\"label\\":\\"ES\\"},{\\"value\\":\\"YARIS\\",\\"label\\":\\"YARIS\\"},{\\"value\\":\\"TSX\\",\\"label\\":\\"TSX\\"},{\\"value\\":\\"M-CLASS\\",\\"label\\":\\"M-CLASS\\"},{\\"value\\":\\"TIGUAN\\",\\"label\\":\\"TIGUAN\\"},{\\"value\\":\\"SANTA FE SPORT\\",\\"label\\":\\"SANTA FE SPORT\\"},{\\"value\\":\\"BEETLE\\",\\"label\\":\\"BEETLE\\"},{\\"value\\":\\"CORVETTE\\",\\"label\\":\\"CORVETTE\\"},{\\"value\\":\\"X3\\",\\"label\\":\\"X3\\"},{\\"value\\":\\"5 SERIES\\",\\"label\\":\\"5 SERIES\\"},{\\"value\\":\\"ALERO\\",\\"label\\":\\"ALERO\\"},{\\"value\\":\\"VENZA\\",\\"label\\":\\"VENZA\\"},{\\"value\\":\\"Q5\\",\\"label\\":\\"Q5\\"},{\\"value\\":\\"RIDGELINE\\",\\"label\\":\\"RIDGELINE\\"},{\\"value\\":\\"UPLANDER\\",\\"label\\":\\"UPLANDER\\"},{\\"value\\":\\"AVALANCHE\\",\\"label\\":\\"AVALANCHE\\"},{\\"value\\":\\"ENCORE\\",\\"label\\":\\"ENCORE\\"},{\\"value\\":\\"SEQUOIA\\",\\"label\\":\\"SEQUOIA\\"},{\\"value\\":\\"F-350 SUPER DUTY\\",\\"label\\":\\"F-350 SUPER DUTY\\"},{\\"value\\":\\"LS\\",\\"label\\":\\"LS\\"},{\\"value\\":\\"SUNFIRE\\",\\"label\\":\\"SUNFIRE\\"},{\\"value\\":\\"MKZ\\",\\"label\\":\\"MKZ\\"},{\\"value\\":\\"GALANT\\",\\"label\\":\\"GALANT\\"},{\\"value\\":\\"IMPALA LIMITED\\",\\"label\\":\\"IMPALA LIMITED\\"},{\\"value\\":\\"ARMADA\\",\\"label\\":\\"ARMADA\\"},{\\"value\\":\\"ION\\",\\"label\\":\\"ION\\"},{\\"value\\":\\"ELEMENT\\",\\"label\\":\\"ELEMENT\\"},{\\"value\\":\\"SIERRA 2500HD\\",\\"label\\":\\"SIERRA 2500HD\\"},{\\"value\\":\\"RENEGADE\\",\\"label\\":\\"RENEGADE\\"},{\\"value\\":\\"VIBE\\",\\"label\\":\\"VIBE\\"},{\\"value\\":\\"CX-9\\",\\"label\\":\\"CX-9\\"},{\\"value\\":\\"BONNEVILLE\\",\\"label\\":\\"BONNEVILLE\\"},{\\"value\\":\\"AVEO\\",\\"label\\":\\"AVEO\\"},{\\"value\\":\\"DART\\",\\"label\\":\\"DART\\"},{\\"value\\":\\"S60\\",\\"label\\":\\"S60\\"},{\\"value\\":\\"FLEX\\",\\"label\\":\\"FLEX\\"},{\\"value\\":\\"CROWN VICTORIA\\",\\"label\\":\\"CROWN VICTORIA\\"},{\\"value\\":\\"VOYAGER\\",\\"label\\":\\"VOYAGER\\"},{\\"value\\":\\"MKX\\",\\"label\\":\\"MKX\\"},{\\"value\\":\\"HR-V\\",\\"label\\":\\"HR-V\\"},{\\"value\\":\\"QUEST\\",\\"label\\":\\"QUEST\\"},{\\"value\\":\\"SL2\\",\\"label\\":\\"SL2\\"},{\\"value\\":\\"XC90\\",\\"label\\":\\"XC90\\"},{\\"value\\":\\"EXPLORER SPORT TRAC\\",\\"label\\":\\"EXPLORER SPORT TRAC\\"},{\\"value\\":\\"PROTEGE\\",\\"label\\":\\"PROTEGE\\"},{\\"value\\":\\"TRAX\\",\\"label\\":\\"TRAX\\"},{\\"value\\":\\"MOUNTAINEER\\",\\"label\\":\\"MOUNTAINEER\\"},{\\"value\\":\\"CT5\\",\\"label\\":\\"CT5\\"},{\\"value\\":\\"PARK AVENUE\\",\\"label\\":\\"PARK AVENUE\\"},{\\"value\\":\\"EXPRESS\\",\\"label\\":\\"EXPRESS\\"},{\\"value\\":\\"AURA\\",\\"label\\":\\"AURA\\"},{\\"value\\":\\"CAMRY HYBRID\\",\\"label\\":\\"CAMRY HYBRID\\"},{\\"value\\":\\"SL1\\",\\"label\\":\\"SL1\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10146,
      name: 'Model 2',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select vehicle model...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[{\\"value\\":\\"F-150\\",\\"label\\":\\"F-150\\"},{\\"value\\":\\"ACCORD\\",\\"label\\":\\"ACCORD\\"},{\\"value\\":\\"CAMRY\\",\\"label\\":\\"CAMRY\\"},{\\"value\\":\\"CIVIC\\",\\"label\\":\\"CIVIC\\"},{\\"value\\":\\"COROLLA\\",\\"label\\":\\"COROLLA\\"},{\\"value\\":\\"SILVERADO\\",\\"label\\":\\"SILVERADO\\"},{\\"value\\":\\"CR-V\\",\\"label\\":\\"CR-V\\"},{\\"value\\":\\"ALTIMA\\",\\"label\\":\\"ALTIMA\\"},{\\"value\\":\\"ESCAPE\\",\\"label\\":\\"ESCAPE\\"},{\\"value\\":\\"EXPLORER\\",\\"label\\":\\"EXPLORER\\"},{\\"value\\":\\"FOCUS\\",\\"label\\":\\"FOCUS\\"},{\\"value\\":\\"SILVERADO 1500\\",\\"label\\":\\"SILVERADO 1500\\"},{\\"value\\":\\"GRAND CHEROKEE\\",\\"label\\":\\"GRAND CHEROKEE\\"},{\\"value\\":\\"RAV4\\",\\"label\\":\\"RAV4\\"},{\\"value\\":\\"EQUINOX\\",\\"label\\":\\"EQUINOX\\"},{\\"value\\":\\"MALIBU\\",\\"label\\":\\"MALIBU\\"},{\\"value\\":\\"SONATA\\",\\"label\\":\\"SONATA\\"},{\\"value\\":\\"FUSION\\",\\"label\\":\\"FUSION\\"},{\\"value\\":\\"IMPALA\\",\\"label\\":\\"IMPALA\\"},{\\"value\\":\\"TAURUS\\",\\"label\\":\\"TAURUS\\"},{\\"value\\":\\"ELANTRA\\",\\"label\\":\\"ELANTRA\\"},{\\"value\\":\\"ODYSSEY\\",\\"label\\":\\"ODYSSEY\\"},{\\"value\\":\\"TACOMA\\",\\"label\\":\\"TACOMA\\"},{\\"value\\":\\"1500\\",\\"label\\":\\"1500\\"},{\\"value\\":\\"HIGHLANDER\\",\\"label\\":\\"HIGHLANDER\\"},{\\"value\\":\\"JETTA\\",\\"label\\":\\"JETTA\\"},{\\"value\\":\\"SENTRA\\",\\"label\\":\\"SENTRA\\"},{\\"value\\":\\"TAHOE\\",\\"label\\":\\"TAHOE\\"},{\\"value\\":\\"PILOT\\",\\"label\\":\\"PILOT\\"},{\\"value\\":\\"ROGUE\\",\\"label\\":\\"ROGUE\\"},{\\"value\\":\\"TRAILBLAZER\\",\\"label\\":\\"TRAILBLAZER\\"},{\\"value\\":\\"MUSTANG\\",\\"label\\":\\"MUSTANG\\"},{\\"value\\":\\"CRUZE\\",\\"label\\":\\"CRUZE\\"},{\\"value\\":\\"TOWN AND COUNTRY\\",\\"label\\":\\"TOWN AND COUNTRY\\"},{\\"value\\":\\"RANGER\\",\\"label\\":\\"RANGER\\"},{\\"value\\":\\"SIENNA\\",\\"label\\":\\"SIENNA\\"},{\\"value\\":\\"PRIUS\\",\\"label\\":\\"PRIUS\\"},{\\"value\\":\\"EDGE\\",\\"label\\":\\"EDGE\\"},{\\"value\\":\\"TUNDRA\\",\\"label\\":\\"TUNDRA\\"},{\\"value\\":\\"COLORADO\\",\\"label\\":\\"COLORADO\\"},{\\"value\\":\\"OUTBACK\\",\\"label\\":\\"OUTBACK\\"},{\\"value\\":\\"SONIC\\",\\"label\\":\\"SONIC\\"},{\\"value\\":\\"FORESTER\\",\\"label\\":\\"FORESTER\\"},{\\"value\\":\\"WRANGLER\\",\\"label\\":\\"WRANGLER\\"},{\\"value\\":\\"SIERRA\\",\\"label\\":\\"SIERRA\\"},{\\"value\\":\\"TRAVERSE\\",\\"label\\":\\"TRAVERSE\\"},{\\"value\\":\\"CHARGER\\",\\"label\\":\\"CHARGER\\"},{\\"value\\":\\"CARAVAN/GRAND CARAVAN\\",\\"label\\":\\"CARAVAN/GRAND CARAVAN\\"},{\\"value\\":\\"SORENTO\\",\\"label\\":\\"SORENTO\\"},{\\"value\\":\\"RAM\\",\\"label\\":\\"RAM\\"},{\\"value\\":\\"SIERRA 1500\\",\\"label\\":\\"SIERRA 1500\\"},{\\"value\\":\\"GRAND CARAVAN\\",\\"label\\":\\"GRAND CARAVAN\\"},{\\"value\\":\\"OPTIMA\\",\\"label\\":\\"OPTIMA\\"},{\\"value\\":\\"SUBURBAN\\",\\"label\\":\\"SUBURBAN\\"},{\\"value\\":\\"DURANGO\\",\\"label\\":\\"DURANGO\\"},{\\"value\\":\\"EXPEDITION\\",\\"label\\":\\"EXPEDITION\\"},{\\"value\\":\\"MAXIMA\\",\\"label\\":\\"MAXIMA\\"},{\\"value\\":\\"SANTA FE\\",\\"label\\":\\"SANTA FE\\"},{\\"value\\":\\"CHEROKEE\\",\\"label\\":\\"CHEROKEE\\"},{\\"value\\":\\"SOUL\\",\\"label\\":\\"SOUL\\"},{\\"value\\":\\"LUCERNE\\",\\"label\\":\\"LUCERNE\\"},{\\"value\\":\\"FORTE\\",\\"label\\":\\"FORTE\\"},{\\"value\\":\\"MAZDA3\\",\\"label\\":\\"MAZDA3\\"},{\\"value\\":\\"ACADIA\\",\\"label\\":\\"ACADIA\\"},{\\"value\\":\\"LIBERTY\\",\\"label\\":\\"LIBERTY\\"},{\\"value\\":\\"COBALT\\",\\"label\\":\\"COBALT\\"},{\\"value\\":\\"300\\",\\"label\\":\\"300\\"},{\\"value\\":\\"AVENGER\\",\\"label\\":\\"AVENGER\\"},{\\"value\\":\\"CAMARO\\",\\"label\\":\\"CAMARO\\"},{\\"value\\":\\"F-250\\",\\"label\\":\\"F-250\\"},{\\"value\\":\\"PASSAT\\",\\"label\\":\\"PASSAT\\"},{\\"value\\":\\"C-CLASS\\",\\"label\\":\\"C-CLASS\\"},{\\"value\\":\\"TERRAIN\\",\\"label\\":\\"TERRAIN\\"},{\\"value\\":\\"MURANO\\",\\"label\\":\\"MURANO\\"},{\\"value\\":\\"TUCSON\\",\\"label\\":\\"TUCSON\\"},{\\"value\\":\\"FRONTIER\\",\\"label\\":\\"FRONTIER\\"},{\\"value\\":\\"AVALON\\",\\"label\\":\\"AVALON\\"},{\\"value\\":\\"WRANGLER UNLIMITED\\",\\"label\\":\\"WRANGLER UNLIMITED\\"},{\\"value\\":\\"YUKON\\",\\"label\\":\\"YUKON\\"},{\\"value\\":\\"GRAND AM\\",\\"label\\":\\"GRAND AM\\"},{\\"value\\":\\"ACCENT\\",\\"label\\":\\"ACCENT\\"},{\\"value\\":\\"PATHFINDER\\",\\"label\\":\\"PATHFINDER\\"},{\\"value\\":\\"RX 350\\",\\"label\\":\\"RX 350\\"},{\\"value\\":\\"LEGACY\\",\\"label\\":\\"LEGACY\\"},{\\"value\\":\\"GRAND PRIX\\",\\"label\\":\\"GRAND PRIX\\"},{\\"value\\":\\"200\\",\\"label\\":\\"200\\"},{\\"value\\":\\"SEBRING\\",\\"label\\":\\"SEBRING\\"},{\\"value\\":\\"MDX\\",\\"label\\":\\"MDX\\"},{\\"value\\":\\"4RUNNER\\",\\"label\\":\\"4RUNNER\\"},{\\"value\\":\\"E-CLASS\\",\\"label\\":\\"E-CLASS\\"},{\\"value\\":\\"CHALLENGER\\",\\"label\\":\\"CHALLENGER\\"},{\\"value\\":\\"JOURNEY\\",\\"label\\":\\"JOURNEY\\"},{\\"value\\":\\"DAKOTA\\",\\"label\\":\\"DAKOTA\\"},{\\"value\\":\\"VERSA\\",\\"label\\":\\"VERSA\\"},{\\"value\\":\\"4-RUNNER\\",\\"label\\":\\"4-RUNNER\\"},{\\"value\\":\\"SPORTAGE\\",\\"label\\":\\"SPORTAGE\\"},{\\"value\\":\\"COMPASS\\",\\"label\\":\\"COMPASS\\"},{\\"value\\":\\"LACROSSE\\",\\"label\\":\\"LACROSSE\\"},{\\"value\\":\\"CX-5\\",\\"label\\":\\"CX-5\\"},{\\"value\\":\\"FREESTAR\\",\\"label\\":\\"FREESTAR\\"},{\\"value\\":\\"PT CRUISER\\",\\"label\\":\\"PT CRUISER\\"},{\\"value\\":\\"ENCLAVE\\",\\"label\\":\\"ENCLAVE\\"},{\\"value\\":\\"NEON\\",\\"label\\":\\"NEON\\"},{\\"value\\":\\"PACIFICA\\",\\"label\\":\\"PACIFICA\\"},{\\"value\\":\\"TL\\",\\"label\\":\\"TL\\"},{\\"value\\":\\"IMPREZA\\",\\"label\\":\\"IMPREZA\\"},{\\"value\\":\\"PATRIOT\\",\\"label\\":\\"PATRIOT\\"},{\\"value\\":\\"3 SERIES\\",\\"label\\":\\"3 SERIES\\"},{\\"value\\":\\"CENTURY\\",\\"label\\":\\"CENTURY\\"},{\\"value\\":\\"ENVOY\\",\\"label\\":\\"ENVOY\\"},{\\"value\\":\\"RAM CHASSIS CAB\\",\\"label\\":\\"RAM CHASSIS CAB\\"},{\\"value\\":\\"F-250 SUPER DUTY\\",\\"label\\":\\"F-250 SUPER DUTY\\"},{\\"value\\":\\"GRAND MARQUIS\\",\\"label\\":\\"GRAND MARQUIS\\"},{\\"value\\":\\"YUKON XL\\",\\"label\\":\\"YUKON XL\\"},{\\"value\\":\\"RX\\",\\"label\\":\\"RX\\"},{\\"value\\":\\"SILVERADO 2500HD\\",\\"label\\":\\"SILVERADO 2500HD\\"},{\\"value\\":\\"MAZDA6\\",\\"label\\":\\"MAZDA6\\"},{\\"value\\":\\"2500\\",\\"label\\":\\"2500\\"},{\\"value\\":\\"MONTE CARLO\\",\\"label\\":\\"MONTE CARLO\\"},{\\"value\\":\\"CT6\\",\\"label\\":\\"CT6\\"},{\\"value\\":\\"G6\\",\\"label\\":\\"G6\\"},{\\"value\\":\\"A4\\",\\"label\\":\\"A4\\"},{\\"value\\":\\"FIT\\",\\"label\\":\\"FIT\\"},{\\"value\\":\\"NITRO\\",\\"label\\":\\"NITRO\\"},{\\"value\\":\\"SRX\\",\\"label\\":\\"SRX\\"},{\\"value\\":\\"ES 350\\",\\"label\\":\\"ES 350\\"},{\\"value\\":\\"SABLE\\",\\"label\\":\\"SABLE\\"},{\\"value\\":\\"VUE\\",\\"label\\":\\"VUE\\"},{\\"value\\":\\"X5\\",\\"label\\":\\"X5\\"},{\\"value\\":\\"RIO\\",\\"label\\":\\"RIO\\"},{\\"value\\":\\"F-350\\",\\"label\\":\\"F-350\\"},{\\"value\\":\\"RDX\\",\\"label\\":\\"RDX\\"},{\\"value\\":\\"TOWN CAR\\",\\"label\\":\\"TOWN CAR\\"},{\\"value\\":\\"XTERRA\\",\\"label\\":\\"XTERRA\\"},{\\"value\\":\\"CTS\\",\\"label\\":\\"CTS\\"},{\\"value\\":\\"ESCALADE\\",\\"label\\":\\"ESCALADE\\"},{\\"value\\":\\"MALIBU CLASSIC\\",\\"label\\":\\"MALIBU CLASSIC\\"},{\\"value\\":\\"SEDONA\\",\\"label\\":\\"SEDONA\\"},{\\"value\\":\\"HHR\\",\\"label\\":\\"HHR\\"},{\\"value\\":\\"FIESTA\\",\\"label\\":\\"FIESTA\\"},{\\"value\\":\\"REGAL\\",\\"label\\":\\"REGAL\\"},{\\"value\\":\\"ESCORT\\",\\"label\\":\\"ESCORT\\"},{\\"value\\":\\"CANYON\\",\\"label\\":\\"CANYON\\"},{\\"value\\":\\"RAM 1500\\",\\"label\\":\\"RAM 1500\\"},{\\"value\\":\\"CROSSTREK\\",\\"label\\":\\"CROSSTREK\\"},{\\"value\\":\\"TITAN\\",\\"label\\":\\"TITAN\\"},{\\"value\\":\\"CALIBER\\",\\"label\\":\\"CALIBER\\"},{\\"value\\":\\"ES\\",\\"label\\":\\"ES\\"},{\\"value\\":\\"YARIS\\",\\"label\\":\\"YARIS\\"},{\\"value\\":\\"TSX\\",\\"label\\":\\"TSX\\"},{\\"value\\":\\"M-CLASS\\",\\"label\\":\\"M-CLASS\\"},{\\"value\\":\\"TIGUAN\\",\\"label\\":\\"TIGUAN\\"},{\\"value\\":\\"SANTA FE SPORT\\",\\"label\\":\\"SANTA FE SPORT\\"},{\\"value\\":\\"BEETLE\\",\\"label\\":\\"BEETLE\\"},{\\"value\\":\\"CORVETTE\\",\\"label\\":\\"CORVETTE\\"},{\\"value\\":\\"X3\\",\\"label\\":\\"X3\\"},{\\"value\\":\\"5 SERIES\\",\\"label\\":\\"5 SERIES\\"},{\\"value\\":\\"ALERO\\",\\"label\\":\\"ALERO\\"},{\\"value\\":\\"VENZA\\",\\"label\\":\\"VENZA\\"},{\\"value\\":\\"Q5\\",\\"label\\":\\"Q5\\"},{\\"value\\":\\"RIDGELINE\\",\\"label\\":\\"RIDGELINE\\"},{\\"value\\":\\"UPLANDER\\",\\"label\\":\\"UPLANDER\\"},{\\"value\\":\\"AVALANCHE\\",\\"label\\":\\"AVALANCHE\\"},{\\"value\\":\\"ENCORE\\",\\"label\\":\\"ENCORE\\"},{\\"value\\":\\"SEQUOIA\\",\\"label\\":\\"SEQUOIA\\"},{\\"value\\":\\"F-350 SUPER DUTY\\",\\"label\\":\\"F-350 SUPER DUTY\\"},{\\"value\\":\\"LS\\",\\"label\\":\\"LS\\"},{\\"value\\":\\"SUNFIRE\\",\\"label\\":\\"SUNFIRE\\"},{\\"value\\":\\"MKZ\\",\\"label\\":\\"MKZ\\"},{\\"value\\":\\"GALANT\\",\\"label\\":\\"GALANT\\"},{\\"value\\":\\"IMPALA LIMITED\\",\\"label\\":\\"IMPALA LIMITED\\"},{\\"value\\":\\"ARMADA\\",\\"label\\":\\"ARMADA\\"},{\\"value\\":\\"ION\\",\\"label\\":\\"ION\\"},{\\"value\\":\\"ELEMENT\\",\\"label\\":\\"ELEMENT\\"},{\\"value\\":\\"SIERRA 2500HD\\",\\"label\\":\\"SIERRA 2500HD\\"},{\\"value\\":\\"RENEGADE\\",\\"label\\":\\"RENEGADE\\"},{\\"value\\":\\"VIBE\\",\\"label\\":\\"VIBE\\"},{\\"value\\":\\"CX-9\\",\\"label\\":\\"CX-9\\"},{\\"value\\":\\"BONNEVILLE\\",\\"label\\":\\"BONNEVILLE\\"},{\\"value\\":\\"AVEO\\",\\"label\\":\\"AVEO\\"},{\\"value\\":\\"DART\\",\\"label\\":\\"DART\\"},{\\"value\\":\\"S60\\",\\"label\\":\\"S60\\"},{\\"value\\":\\"FLEX\\",\\"label\\":\\"FLEX\\"},{\\"value\\":\\"CROWN VICTORIA\\",\\"label\\":\\"CROWN VICTORIA\\"},{\\"value\\":\\"VOYAGER\\",\\"label\\":\\"VOYAGER\\"},{\\"value\\":\\"MKX\\",\\"label\\":\\"MKX\\"},{\\"value\\":\\"HR-V\\",\\"label\\":\\"HR-V\\"},{\\"value\\":\\"QUEST\\",\\"label\\":\\"QUEST\\"},{\\"value\\":\\"SL2\\",\\"label\\":\\"SL2\\"},{\\"value\\":\\"XC90\\",\\"label\\":\\"XC90\\"},{\\"value\\":\\"EXPLORER SPORT TRAC\\",\\"label\\":\\"EXPLORER SPORT TRAC\\"},{\\"value\\":\\"PROTEGE\\",\\"label\\":\\"PROTEGE\\"},{\\"value\\":\\"TRAX\\",\\"label\\":\\"TRAX\\"},{\\"value\\":\\"MOUNTAINEER\\",\\"label\\":\\"MOUNTAINEER\\"},{\\"value\\":\\"CT5\\",\\"label\\":\\"CT5\\"},{\\"value\\":\\"PARK AVENUE\\",\\"label\\":\\"PARK AVENUE\\"},{\\"value\\":\\"EXPRESS\\",\\"label\\":\\"EXPRESS\\"},{\\"value\\":\\"AURA\\",\\"label\\":\\"AURA\\"},{\\"value\\":\\"CAMRY HYBRID\\",\\"label\\":\\"CAMRY HYBRID\\"},{\\"value\\":\\"SL1\\",\\"label\\":\\"SL1\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10148,
      name: 'Number of Vehicles In The Household',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Select number of vehicles...\\",\\"validation\\":{\\"required\\":false},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"\u003c\u003e\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"\u003e\u003c\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10140,
      name: 'Vin 1',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"text\\",\\"placeholder\\":\\"Enter VIN...\\",\\"validation\\":{\\"required\\":true,\\"regex\\":\\"^[A-HJ-NPR-Z0-9]{17}$\\"},\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"is_empty\\",\\"symbol\\":\\"is_null\\"},{\\"operator\\":\\"is_not_empty\\",\\"symbol\\":\\"is_not_null\\"},{\\"operator\\":\\"like\\",\\"symbol\\":\\"like\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"},{\\"operator\\":\\"not_like\\",\\"symbol\\":\\"not_like\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10144,
      name: 'Vin 2',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"text\\",\\"placeholder\\":\\"Enter 17-character VIN (no I, O, Q)\\",\\"validation\\":{\\"required\\":true,\\"regex\\":\\"^[A-HJ-NPR-Z0-9]{17}$\\"},\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"is_empty\\",\\"symbol\\":\\"is_null\\"},{\\"operator\\":\\"is_not_empty\\",\\"symbol\\":\\"is_not_null\\"},{\\"operator\\":\\"like\\",\\"symbol\\":\\"like\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"},{\\"operator\\":\\"not_like\\",\\"symbol\\":\\"not_like\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10143,
      name: 'Year 1',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more year 1 options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"2025\\",\\"value\\":\\"2025\\"},{\\"key\\":\\"2024\\",\\"value\\":\\"2024\\"},{\\"key\\":\\"2023\\",\\"value\\":\\"2023\\"},{\\"key\\":\\"2022\\",\\"value\\":\\"2022\\"},{\\"key\\":\\"2021\\",\\"value\\":\\"2021\\"},{\\"key\\":\\"2020\\",\\"value\\":\\"2020\\"},{\\"key\\":\\"2019\\",\\"value\\":\\"2019\\"},{\\"key\\":\\"2018\\",\\"value\\":\\"2018\\"},{\\"key\\":\\"2017\\",\\"value\\":\\"2017\\"},{\\"key\\":\\"2016\\",\\"value\\":\\"2016\\"},{\\"key\\":\\"2015\\",\\"value\\":\\"2015\\"},{\\"key\\":\\"2014\\",\\"value\\":\\"2014\\"},{\\"key\\":\\"2013\\",\\"value\\":\\"2013\\"},{\\"key\\":\\"2012\\",\\"value\\":\\"2012\\"},{\\"key\\":\\"2011\\",\\"value\\":\\"2011\\"},{\\"key\\":\\"2010\\",\\"value\\":\\"2010\\"},{\\"key\\":\\"2009\\",\\"value\\":\\"2009\\"},{\\"key\\":\\"2008\\",\\"value\\":\\"2008\\"},{\\"key\\":\\"2007\\",\\"value\\":\\"2007\\"},{\\"key\\":\\"2006\\",\\"value\\":\\"2006\\"},{\\"key\\":\\"2005\\",\\"value\\":\\"2005\\"},{\\"key\\":\\"2004\\",\\"value\\":\\"2004\\"},{\\"key\\":\\"2003\\",\\"value\\":\\"2003\\"},{\\"key\\":\\"2002\\",\\"value\\":\\"2002\\"},{\\"key\\":\\"2001\\",\\"value\\":\\"2001\\"},{\\"key\\":\\"2000\\",\\"value\\":\\"2000\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 10147,
      name: 'Year 2',
      nodeType: 'attribute',
      meta: '"{\\"type\\":\\"select\\",\\"placeholder\\":\\"Select one or more year 2 options...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[{\\"key\\":\\"2025\\",\\"value\\":\\"2025\\"},{\\"key\\":\\"2024\\",\\"value\\":\\"2024\\"},{\\"key\\":\\"2023\\",\\"value\\":\\"2023\\"},{\\"key\\":\\"2022\\",\\"value\\":\\"2022\\"},{\\"key\\":\\"2021\\",\\"value\\":\\"2021\\"},{\\"key\\":\\"2020\\",\\"value\\":\\"2020\\"},{\\"key\\":\\"2019\\",\\"value\\":\\"2019\\"},{\\"key\\":\\"2018\\",\\"value\\":\\"2018\\"},{\\"key\\":\\"2017\\",\\"value\\":\\"2017\\"},{\\"key\\":\\"2016\\",\\"value\\":\\"2016\\"},{\\"key\\":\\"2015\\",\\"value\\":\\"2015\\"},{\\"key\\":\\"2014\\",\\"value\\":\\"2014\\"},{\\"key\\":\\"2013\\",\\"value\\":\\"2013\\"},{\\"key\\":\\"2012\\",\\"value\\":\\"2012\\"},{\\"key\\":\\"2011\\",\\"value\\":\\"2011\\"},{\\"key\\":\\"2010\\",\\"value\\":\\"2010\\"},{\\"key\\":\\"2009\\",\\"value\\":\\"2009\\"},{\\"key\\":\\"2008\\",\\"value\\":\\"2008\\"},{\\"key\\":\\"2007\\",\\"value\\":\\"2007\\"},{\\"key\\":\\"2006\\",\\"value\\":\\"2006\\"},{\\"key\\":\\"2005\\",\\"value\\":\\"2005\\"},{\\"key\\":\\"2004\\",\\"value\\":\\"2004\\"},{\\"key\\":\\"2003\\",\\"value\\":\\"2003\\"},{\\"key\\":\\"2002\\",\\"value\\":\\"2002\\"},{\\"key\\":\\"2001\\",\\"value\\":\\"2001\\"},{\\"key\\":\\"2000\\",\\"value\\":\\"2000\\"}],\\"operators\\":[{\\"operator\\":\\"equal_to\\",\\"symbol\\":\\"=\\"},{\\"operator\\":\\"not_equal_to\\",\\"symbol\\":\\"!=\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
  ],
};

export const MOCK_CUSTOM_AUDIENCES_DATA = {
  audiences: [
    {
      id: 'aud-19b61de3-b1c8-4ff9-8126-9cb4d36b0207',
      name: 'High-Value Shoppers',
      size: 2450000,
      nodeType: 'audience',
      description:
        'Customers with purchase history over $1000 in the last 6 months',
    },
    {
      id: 'aud-e5918b8a-e391-403f-b815-25a35a04db95',
      name: 'Active Email Subscribers',
      size: 8920500,
      nodeType: 'audience',
      description: 'Users who have opened emails in the last 30 days',
    },
    {
      id: 'aud-ad993864-cd34-45da-8ef4-57d0f7f52bce',
      name: 'Mobile App Users',
      size: 1567800,
      nodeType: 'audience',
      description: 'Users who have installed and used the mobile app',
    },
    {
      id: 'aud-4a10aace-ddbb-493d-b9de-b401c6825c74',
      name: 'Last 30 Days Gaming Enthusiasts',
      size: 3450000,
      nodeType: 'audience',
      description: 'Active gamers in the last 30 days',
      includes: 'Gaming category interest, Recent app activity',
    },
    {
      id: 'aud-a46d7290-a8bf-42f1-9c24-9f6437662c3b',
      name: 'Premium Tier Customers',
      size: 567000,
      nodeType: 'audience',
      description: 'Users with premium membership or subscription',
      includes: 'Active subscription, Premium features usage',
    },
    {
      id: 'aud-7c8f9a2b-1d4e-4b3c-9e1f-8a5b6c7d8e9f',
      name: 'Abandoned Cart - Last 7 Days',
      size: 892000,
      nodeType: 'audience',
      description:
        'Users who added items to cart but did not complete purchase',
      includes: 'Cart activity, No recent purchase',
    },
    {
      id: 'aud-f1e2d3c4-b5a6-4978-8c1b-2d3e4f5g6h7i',
      name: 'Fitness & Wellness Segment',
      size: 4200000,
      nodeType: 'audience',
      description: 'Users interested in fitness, health, and wellness products',
      includes: 'Fitness category views, Health app usage',
    },
    {
      id: 'aud-9a8b7c6d-5e4f-3g2h-1i0j-k9l8m7n6o5p4',
      name: 'Luxury Brand Shoppers',
      size: 780000,
      nodeType: 'audience',
      description: 'Customers who purchase luxury and premium brands',
      includes: 'High-end product purchases, Premium brand engagement',
      excludes: 'Budget category shoppers',
    },
    {
      id: 'aud-q1w2e3r4-t5y6-u7i8-o9p0-a1s2d3f4g5h6',
      name: 'Re-engagement Needed',
      size: 6540000,
      nodeType: 'audience',
      description: 'Previously active users who have not engaged in 90+ days',
      excludes: 'Recent activity, Active subscribers',
    },
    {
      id: 'aud-j7k8l9m0-n1o2-p3q4-r5s6-t7u8v9w0x1y2',
      name: 'First-Time Buyers',
      size: 1234500,
      nodeType: 'audience',
      description:
        'Customers who made their first purchase in the last 30 days',
      includes: 'Single purchase record, Recent registration',
    },
  ],
};

export const MOCK_1ST_PARTY_DATA_PERSONAL_TRANSACTIONAL = {
  attributes: [
    {
      id: 20458,
      name: 'Average Order Lag Last 365 Days',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Enter average order lag last 365 days range...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"\u003c\u003e\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"\u003e\u003c\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 20454,
      name: 'Average Order Lag Lifetime',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Enter average order lag lifetime range...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"\u003c\u003e\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"\u003e\u003c\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 20449,
      name: 'First Order Date',
      meta: '"{\\"type\\":\\"date\\",\\"placeholder\\":\\"Select a date...\\",\\"validation\\":{\\"required\\":false},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 20450,
      name: 'Last Order Date',
      meta: '"{\\"type\\":\\"date\\",\\"placeholder\\":\\"Select a date...\\",\\"validation\\":{\\"required\\":true},\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 20455,
      name: 'Orders Last 365 Days',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Enter orders last 365 days range...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"\u003c\u003e\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"\u003e\u003c\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 20451,
      name: 'Orders Lifetime',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Enter orders lifetime range...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"\u003c\u003e\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"\u003e\u003c\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 20456,
      name: 'Revenue Last 365 Days',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Enter revenue last 365 days range...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"\u003c\u003e\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"\u003e\u003c\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 20452,
      name: 'Revenue Lifetime',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Enter revenue lifetime range...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"\u003c\u003e\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"\u003e\u003c\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 20457,
      name: 'Units Last 365 Days',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Enter units last 365 days range...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"\u003c\u003e\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"\u003e\u003c\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
    {
      id: 20453,
      name: 'Units Lifetime',
      meta: '"{\\"type\\":\\"range\\",\\"placeholder\\":\\"Enter units lifetime range...\\",\\"validation\\":{\\"required\\":true},\\"values\\":[],\\"operators\\":[{\\"operator\\":\\"between\\",\\"value\\":\\"\u003c\u003e\\"},{\\"operator\\":\\"not_between\\",\\"value\\":\\"\u003e\u003c\\"}],\\"permissions\\":{\\"spyglass\\":true}}"',
    },
  ],
};

export const MOCK_USERS = {
  users: [
    {
      id: '1',
      email: 'cristiano.ronaldo@example.com',
      firstName: 'Cristiano',
      lastName: 'Ronaldo',
      createdAt: '2025-09-16T11:42:55Z',
      updatedAt: '2025-09-11T06:43:14Z',
      status: 'active',
      role: userRoles.superAdmin.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '2',
      email: 'emily.smith@example.com',
      firstName: 'Emily',
      lastName: 'Smith',
      createdAt: '2025-09-15T09:30:11Z',
      updatedAt: '2025-09-12T10:20:44Z',
      status: 'active',
      role: userRoles.orgAdmin.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '3',
      email: 'daniel.johnson@example.com',
      firstName: 'Daniel',
      lastName: 'Johnson',
      createdAt: '2025-09-14T14:22:01Z',
      updatedAt: '2025-09-13T08:11:27Z',
      status: 'pending',
      role: userRoles.user.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '4',
      email: 'olivia.brown@example.com',
      firstName: 'Olivia',
      lastName: 'Brown',
      createdAt: '2025-09-13T16:42:09Z',
      updatedAt: '2025-09-13T16:50:00Z',
      status: 'active',
      role: userRoles.orgAdmin.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '5',
      email: 'liam.miller@example.com',
      firstName: 'Liam',
      lastName: 'Miller',
      createdAt: '2025-09-12T10:10:10Z',
      updatedAt: '2025-09-13T09:55:33Z',
      status: 'inactive',
      role: userRoles.user.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '6',
      email: 'ava.wilson@example.com',
      firstName: 'Ava',
      lastName: 'Wilson',
      createdAt: '2025-09-11T08:45:19Z',
      updatedAt: '2025-09-11T09:11:57Z',
      status: 'active',
      role: userRoles.orgAdmin.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '7',
      email: 'ethan.moore@example.com',
      firstName: 'Ethan',
      lastName: 'Moore',
      createdAt: '2025-09-10T15:33:42Z',
      updatedAt: '2025-09-11T12:25:22Z',
      status: 'pending',
      role: userRoles.user.id,
    },
    {
      id: '8',
      email: 'sophia.taylor@example.com',
      firstName: 'Sophia',
      lastName: 'Taylor',
      createdAt: '2025-09-09T13:22:41Z',
      updatedAt: '2025-09-10T07:44:19Z',
      status: 'active',
      role: userRoles.orgAdmin.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '9',
      email: 'noah.anderson@example.com',
      firstName: 'Noah',
      lastName: 'Anderson',
      createdAt: '2025-09-08T09:12:35Z',
      updatedAt: '2025-09-08T10:00:00Z',
      status: 'active',
      role: userRoles.user.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '10',
      email: 'isabella.jackson@example.com',
      firstName: 'Isabella',
      lastName: 'Jackson',
      createdAt: '2025-09-07T11:00:00Z',
      updatedAt: '2025-09-07T13:33:33Z',
      status: 'inactive',
      role: userRoles.user.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '11',
      email: 'mason.white@example.com',
      firstName: 'Mason',
      lastName: 'White',
      createdAt: '2025-09-06T14:22:00Z',
      updatedAt: '2025-09-07T10:45:10Z',
      status: 'active',
      role: userRoles.orgAdmin.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '12',
      email: 'mia.harris@example.com',
      firstName: 'Mia',
      lastName: 'Harris',
      createdAt: '2025-09-05T10:40:00Z',
      updatedAt: '2025-09-05T12:12:12Z',
      status: 'pending',
      role: userRoles.user.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '13',
      email: 'james.clark@example.com',
      firstName: 'James',
      lastName: 'Clark',
      createdAt: '2025-09-04T08:00:00Z',
      updatedAt: '2025-09-04T08:45:30Z',
      status: 'active',
      role: userRoles.orgAdmin.id,
      tenantName: 'LG Electronics',
    },
    {
      id: '14',
      email: 'amelia.lewis@example.com',
      firstName: 'Amelia',
      lastName: 'Lewis',
      createdAt: '2025-09-03T07:55:12Z',
      updatedAt: '2025-09-03T09:25:49Z',
      status: 'active',
      role: userRoles.user.id,
      tenantName: 'LG Electronics',
    },
  ],
  pagination: {
    page: 1,
    pageSize: 10,
    totalItems: 35,
    totalPages: 4,
    sortBy: 'email', // column name eg email/createdAt/updatedAt/etc
    sortOrder: 'asc', // asc/desc
  },
};

export const MOCK_USER_1_PROFILE = {
  id: '1',
  firstName: 'Cristiano',
  lastName: 'Ronaldo',
  email: 'cristiano.ronaldo@example.com',
  createdAt: '2025-09-16T11:42:55Z',
  updatedAt: '2025-09-11T06:43:14Z',
  role: userRoles.superAdmin.id,
  status: 'active',
};

export const MOCK_CLIENTS = [
  {
    id: '1',
    name: 'Acme Corporation',
    createdAt: '2024-01-15',
    updatedAt: '2024-09-10',
    totalUsers: 25,
    gcpProjectId: 'acme-tenant-project',
    clientId: 'acm-a1b2',
    provisioningStatus: 'ready',
    steps: [
      'Creating GCP project',
      'Enabling GCP services',
      'Provisioning resources',
      'Cloning repository',
      'Creating Melissa BigQuery objects via Dataform',
      'Creating Scheduler job',
      'Registering control plane config',
      'Ready',
    ].map(label => ({
      label,
      status: 'success',
      message: 'Completed',
    })),
    vigIDs: [] as string[],
    config: {
      sourcesAndCategories: [
        {
          id: 'stiristaData',
          categories: [
            { id: 'personal' },
            { id: 'intent' },
          ],
        },
        {
          id: 'emailData',
        },
      ],
      misc: [
        { id: 'allowDataExports' },
      ],
    },
  },
  {
    id: '2',
    name: 'TechStart Solutions',
    createdAt: '2024-02-20',
    updatedAt: '2024-09-08',
    totalUsers: 12,
    gcpProjectId: 'techstart-tenant-project',
    clientId: 'tst-b2c3',
    provisioningStatus: 'provisioning',
    steps: [
      {
        label: 'Creating GCP project',
        status: 'success',
        message: 'Project created, billing attached, and admin owner set.',
      },
      {
        label: 'Enabling GCP services',
        status: 'success',
        message: 'Enabled 14 APIs.',
      },
    ],
    config: {
      sourcesAndCategories: [],
      misc: [
        { id: 'allowDataExports' },
      ],
    },
  },
  {
    id: '3',
    name: 'Global Enterprises',
    createdAt: '2024-03-10',
    updatedAt: '2024-08-30',
    totalUsers: 45,
    gcpProjectId: 'global-tenant-project',
    clientId: 'glb-c3d4',
    provisioningStatus: 'failed',
    steps: [
      {
        label: 'Creating GCP project',
        status: 'success',
        message: 'Project created, billing attached, and admin owner set.',
      },
      {
        label: 'Enabling GCP services',
        status: 'success',
        message: 'Enabled 14 APIs.',
      },
      {
        label: 'Provisioning resources',
        status: 'success',
        message: 'Resources returned.',
      },
      {
        label: 'Cloning repository',
        status: 'success',
        message: 'Repository cloned and linked.',
      },
      {
        label: 'Creating Melissa BigQuery objects via Dataform',
        status: 'failed',
        message: 'Dataform compilation failed: ambiguous action name.',
      },
    ],
    config: {
      sourcesAndCategories: [],
      misc: [],
    },
  },
  {
    id: '4',
    name: 'Innovation Labs',
    createdAt: '2024-04-05',
    updatedAt: '2024-09-11',
    totalUsers: 8,
    gcpProjectId: null,
    clientId: 'inn-d4e5',
    provisioningStatus: 'pending',
    steps: [] as { label: string; status: string; message: string }[],
    config: {
      sourcesAndCategories: [],
      misc: [],
    },
  },
  {
    id: '5',
    name: 'Retail Masters',
    createdAt: '2024-05-18',
    updatedAt: '2024-09-09',
    totalUsers: 33,
    config: {
      sourcesAndCategories: [],
      misc: [],
    },
  },
  {
    id: '6',
    name: 'Digital Dynamics',
    createdAt: '2024-06-22',
    updatedAt: '2024-09-07',
    totalUsers: 19,
    config: {
      sourcesAndCategories: [],
      misc: [],
    },
  },
  {
    id: '7',
    name: 'Acme Corporation #2',
    createdAt: '2024-01-15',
    updatedAt: '2024-09-10',
    totalUsers: 25,
    config: {
      sourcesAndCategories: [
        {
          id: 'stiristaData',
          categories: [
            { id: 'personal' },
            { id: 'intent' },
          ],
        },
        {
          id: 'emailData',
        },
      ],
      misc: [
        { id: 'allowDataExports' },
      ],
    },
  },
  {
    id: '8',
    name: 'TechStart Solutions #2',
    createdAt: '2024-02-20',
    updatedAt: '2024-09-08',
    totalUsers: 12,
    config: {
      sourcesAndCategories: [],
      misc: [
        { id: 'allowDataExports' },
      ],
    },
  },
  {
    id: '9',
    name: 'Global Enterprises #2',
    createdAt: '2024-03-10',
    updatedAt: '2024-08-30',
    totalUsers: 45,
    config: {
      sourcesAndCategories: [],
      misc: [],
    },
  },
  {
    id: '10',
    name: 'Innovation Labs #2',
    createdAt: '2024-04-05',
    updatedAt: '2024-09-11',
    totalUsers: 8,
    config: {
      sourcesAndCategories: [],
      misc: [],
    },
  },
  {
    id: '11',
    name: 'Retail Masters #2',
    createdAt: '2024-05-18',
    updatedAt: '2024-09-09',
    totalUsers: 33,
    config: {
      sourcesAndCategories: [],
      misc: [],
    },
  },
  {
    id: '12',
    name: 'Digital Dynamics #2',
    createdAt: '2024-06-22',
    updatedAt: '2024-09-07',
    totalUsers: 19,
    config: {
      sourcesAndCategories: [],
      misc: [],
    },
  },
];

export const MOCK_ORGS = [
  {
    id: '1',
    name: 'Acme Organization',
    createdAt: '2024-01-15',
    updatedAt: '2024-09-10',
  },
  {
    id: '2',
    name: 'TechStart Solutions Organization',
    createdAt: '2024-02-20',
    updatedAt: '2024-09-08',
  },
  {
    id: '3',
    name: 'Global Enterprises Organization',
    createdAt: '2024-03-10',
    updatedAt: '2024-08-30',
  },
  {
    id: '4',
    name: 'Innovation Labs Organization',
    createdAt: '2024-04-05',
    updatedAt: '2024-09-11',
  },
  {
    id: '5',
    name: 'Retail Masters Organization',
    createdAt: '2024-05-18',
    updatedAt: '2024-09-09',
  },
  {
    id: '6',
    name: 'Digital Dynamics Organization',
    createdAt: '2024-06-22',
    updatedAt: '2024-09-07',
  },
  {
    id: '7',
    name: 'Acme Corporation Organization #2',
    createdAt: '2024-01-15',
    updatedAt: '2024-09-10',
  },
  {
    id: '8',
    name: 'TechStart Solutions Organization #2',
    createdAt: '2024-02-20',
    updatedAt: '2024-09-08',
  },
  {
    id: '9',
    name: 'Global Enterprises Organization #2',
    createdAt: '2024-03-10',
    updatedAt: '2024-08-30',
  },
  {
    id: '10',
    name: 'Innovation Labs Organization #2',
    createdAt: '2024-04-05',
    updatedAt: '2024-09-11',
  },
  {
    id: '11',
    name: 'Retail Masters Organization #2',
    createdAt: '2024-05-18',
    updatedAt: '2024-09-09',
  },
  {
    id: '12',
    name: 'Digital Dynamics Organization #2',
    createdAt: '2024-06-22',
    updatedAt: '2024-09-07',
  },
];

const VIG_TEXT_OPERATORS = [
  { operator: 'like', symbol: 'like' },
  { operator: 'equal_to', symbol: '=' },
  { operator: 'not_equal_to', symbol: '!=' },
  { operator: 'not_like', symbol: 'not_like' },
  { operator: 'is_empty', symbol: 'is_null' },
  { operator: 'is_not_empty', symbol: 'is_not_null' },
];

const VIG_SELECT_OPERATORS = [
  { operator: 'equal_to', symbol: '=' },
  { operator: 'not_equal_to', symbol: '!=' },
];

const VIG_SITE_CATEGORY_VALUES = [
  {
    label: 'Air Conditioners',
    value: [
      'air-conditioners',
      'window-air-conditioners',
      'portable-air-conditioners',
      'wall-air-conditioners',
    ],
  },
  {
    label: 'Air Purifiers',
    value: ['air-purifiers', 'air-care-accessories', 'air-care-solutions'],
  },
  {
    label: 'Appliance Accessories',
    value: [
      'appliances-accessories',
      'appliance-accessories',
      'home-appliance-accessories',
      'kitchen-accessories',
    ],
  },
  { label: 'Blu-ray & DVD Players', value: ['blu-ray-dvd-players'] },
  {
    label: 'Bottom Freezer Refrigerators',
    value: ['bottom-freezer-refrigerators'],
  },
  { label: 'Burners & Drives', value: ['burners-drives'] },
  { label: 'Cell Phones', value: ['cell-phones', 'mobile-phones'] },
  {
    label: 'Cooking Appliances',
    value: [
      'cooking-appliances',
      'ranges',
      'gas-ranges',
      'electric-ranges',
      'induction-ranges',
      'dual-fuel-ranges',
      'range-hoods',
      'microwave-ovens',
      'over-the-range-microwave',
      'counter-top-microwave',
      'built-in-microwaves',
      'wall-ovens',
      'single-wall-ovens',
      'double-wall-ovens',
      'combination-wall-ovens',
      'cooktops',
      'gas-cooktops',
      'electric-cooktops',
      'induction-cooktops',
      'cooktops-hoods',
      'cooking-accessories',
      'replacement-oven-knobs',
      'lg-signature-cooking-appliances',
      'ranges-ovens',
    ],
  },
  {
    label: 'Cooktops',
    value: [
      'cooktops',
      'gas-cooktops',
      'electric-cooktops',
      'induction-cooktops',
      'cooktops-hoods',
    ],
  },
  {
    label: 'Counter-Depth Refrigerators',
    value: ['counter-depth-refrigerators'],
  },
  { label: 'Countertop Microwave', value: ['counter-top-microwave'] },
  { label: 'Craft Ice Refrigerators', value: ['craft-ice-refrigerators'] },
  { label: 'Dehumidifiers', value: ['dehumidifiers'] },
  {
    label: 'Dishwashers',
    value: [
      'dishwashers',
      'top-control-dishwashers',
      'front-control-dishwashers',
      'dishwasher-accessories',
      'lg-signature-dishwashers',
    ],
  },
  {
    label: 'Dryers',
    value: [
      'dryers',
      'electric-dryers',
      'gas-dryers',
      'dryer-racks',
      'dryer-lint-filters',
    ],
  },
  { label: 'Electric Dryers', value: ['electric-dryers'] },
  { label: 'Electric Ranges', value: ['electric-ranges'] },
  { label: 'French Door Refrigerators', value: ['french-door-refrigerators'] },
  { label: 'Front Control Dishwashers', value: ['front-control-dishwashers'] },
  { label: 'Front Load Washers', value: ['front-load-washers'] },
  {
    label: 'Gaming Monitors',
    value: ['gaming-monitors', 'ultragear-oled-gaming-monitors'],
  },
  { label: 'Gas Dryers', value: ['gas-dryers'] },
  { label: 'Gas Ranges', value: ['gas-ranges'] },
  {
    label: 'Home Audio',
    value: ['home-audio', 'home-theater-systems', 'home-theater-packages'],
  },
  { label: 'Induction Ranges', value: ['induction-ranges'] },
  {
    label: 'Kitchen Appliance Packages',
    value: ['kitchen-appliance-packages', 'kitchen-packages'],
  },
  {
    label: 'Laptops',
    value: [
      'laptops',
      'lg-gram-notebook-laptops',
      'lg-gram-pro-laptops',
      'lg-gram-pro-2-in-1-laptops',
      'lg-gram-copilot-plus-pc-laptops',
      'lg-gram-book-laptops',
      'lg-gram-superslim-laptops',
      'lg-gram-2in1-laptops',
      'lg-gram-style-notebook-laptops',
      'lg-ultrapc-notebook-laptops',
      'computer-accessories',
      'wireless-keyboards',
      'computing-packages',
    ],
  },
  {
    label: 'Laundry Appliance Sets',
    value: ['laundry-appliance-sets', 'laundry-packages'],
  },
  {
    label: 'LG Gram Laptops',
    value: [
      'lg-gram-notebook-laptops',
      'lg-gram-pro-laptops',
      'lg-gram-pro-2-in-1-laptops',
      'lg-gram-copilot-plus-pc-laptops',
      'lg-gram-book-laptops',
      'lg-gram-superslim-laptops',
      'lg-gram-2in1-laptops',
      'lg-gram-style-notebook-laptops',
    ],
  },
  {
    label: 'LG Gram Pro Laptops',
    value: ['lg-gram-pro-laptops', 'lg-gram-pro-2-in-1-laptops'],
  },
  { label: 'LG Partner Store', value: ['lg-partner-store'] },
  { label: 'LG Studio Appliances', value: ['lg-studio-appliances'] },
  { label: 'Lifestyle Products', value: ['lifestyle-products'] },
  {
    label: 'Microwave Ovens',
    value: [
      'microwave-ovens',
      'over-the-range-microwave',
      'counter-top-microwave',
      'built-in-microwaves',
    ],
  },
  {
    label: 'Mobile Accessories',
    value: [
      'mobile-accessories',
      'cell-phone-chargers',
      'cell-phone-batteries',
    ],
  },
  {
    label: 'Monitors',
    value: [
      'monitors',
      'ultragear-oled-gaming-monitors',
      'gaming-monitors',
      'ultrawide-monitors',
      'smart-monitors',
      'lifestyle-monitors',
      'fhd-monitors',
      'qhd-monitors',
      'monitor-tvs',
      'ultrafine-monitors',
    ],
  },
  { label: 'OLED Evo TVs', value: ['oled-evo-tvs'] },
  { label: 'OLED TVs', value: ['oled-tvs', 'oled-evo-tvs'] },
  { label: 'Over-the-Range Microwave', value: ['over-the-range-microwave'] },
  { label: 'Portable Air Conditioners', value: ['portable-air-conditioners'] },
  { label: 'Projectors', value: ['projectors'] },
  { label: 'QNED Evo AI TVs', value: ['qned-evo-ai-tv'] },
  { label: 'QNED TVs', value: ['qned-tvs', 'qned-evo-ai-tv'] },
  {
    label: 'Ranges',
    value: [
      'ranges',
      'gas-ranges',
      'electric-ranges',
      'induction-ranges',
      'dual-fuel-ranges',
      'range-hoods',
      'ranges-ovens',
    ],
  },
  { label: 'Refrigerator Filters', value: ['refrigerator-filters'] },
  {
    label: 'Refrigerators',
    value: [
      'refrigerators',
      'french-door-refrigerators',
      'side-by-side-refrigerators',
      'counter-depth-refrigerators',
      'top-freezer-refrigerators',
      'bottom-freezer-refrigerators',
      'specialty-food-refrigerators',
      'craft-ice-refrigerators',
      'instaview-refrigerators',
      'door-in-door-refrigerators',
      'refrigerator-filters',
      'refrigerator-accessories',
      'lg-signature-refrigerators',
    ],
  },
  {
    label: 'Side-by-Side Refrigerators',
    value: ['side-by-side-refrigerators'],
  },
  { label: 'Smart Monitors', value: ['smart-monitors'] },
  { label: 'Smart Watches', value: ['smart-watches'] },
  { label: 'Soundbars', value: ['sound-bars', 'soundbars'] },
  {
    label: 'Speakers',
    value: [
      'speakers',
      'wireless-bluetooth-speakers',
      'party-speakers',
      'subwoofers',
    ],
  },
  {
    label: 'Specialty Food Refrigerators',
    value: ['specialty-food-refrigerators'],
  },
  { label: 'Styler / Steam Closet', value: ['styler-steam-closet', 'styler'] },
  { label: 'Tablets', value: ['tablets'] },
  { label: 'Top Control Dishwashers', value: ['top-control-dishwashers'] },
  { label: 'Top Freezer Refrigerators', value: ['top-freezer-refrigerators'] },
  { label: 'Top Load Washers', value: ['top-load-washers'] },
  {
    label: 'TV & Home Theater Accessories',
    value: [
      'tv-home-theater-accessories',
      'tv-audio-video-accessories',
      'magic-remote',
    ],
  },
  {
    label: 'TVs',
    value: [
      'tvs',
      'oled-tvs',
      'qned-evo-ai-tv',
      'qned-tvs',
      'oled-evo-tvs',
      'uhd-4k-tvs',
      'nanocell-tvs',
      'home-video',
    ],
  },
  { label: 'UHD 4K TVs', value: ['uhd-4k-tvs'] },
  {
    label: 'UltraGear OLED Gaming Monitors',
    value: ['ultragear-oled-gaming-monitors'],
  },
  { label: 'Ultrawide Monitors', value: ['ultrawide-monitors'] },
  {
    label: 'Vacuum Cleaners',
    value: [
      'vacuum-cleaners',
      'stick-vacuums',
      'robot-vacuums',
      'vacuum-accessories',
    ],
  },
  {
    label: 'Wall Ovens',
    value: [
      'wall-ovens',
      'single-wall-ovens',
      'double-wall-ovens',
      'combination-wall-ovens',
    ],
  },
  {
    label: 'Washer-Dryer Combos',
    value: ['washer-dryer-combos', 'washers-dryer-combos'],
  },
  {
    label: 'Washers',
    value: [
      'washers',
      'front-load-washers',
      'top-load-washers',
      'pedestal-washers',
    ],
  },
  {
    label: 'Washers & Dryers',
    value: [
      'washers-dryers',
      'washers',
      'washer-dryer-combos',
      'washers-dryer-combos',
      'front-load-washers',
      'top-load-washers',
      'dryers',
      'electric-dryers',
      'gas-dryers',
      'washtowers',
      'styler-steam-closet',
      'styler',
      'pedestal-washers',
      'laundry-appliance-sets',
      'laundry-accessories',
      'laundry-pedestals',
      'laundry-hoses',
      'washer-dryer-stacking-kits',
      'dryer-racks',
      'dryer-lint-filters',
      'lg-signature-washers-dryers',
      'laundry-packages',
    ],
  },
  { label: 'WashTowers', value: ['washtowers'] },
  { label: 'Window Air Conditioners', value: ['window-air-conditioners'] },
  { label: 'Wireless Earbuds', value: ['wireless-earbuds'] },
  {
    label: 'Wireless Headphones',
    value: ['wireless-headphones', 'headphones', 'neckbands'],
  },
];

const toDoubleEncodedMeta = (meta: Record<string, unknown>): string =>
  JSON.stringify(JSON.stringify(meta));

const createVigTextMeta = (columnName: string) =>
  toDoubleEncodedMeta({
    type: 'text',
    operators: VIG_TEXT_OPERATORS,
    permissions: { spyglass: true },
    isVig: true,
    columnName,
    periodDays: { min: 7, max: 120, default: 7 },
  });

const createVigAttributes = () => ({
  attributes: [
    {
      id: -1,
      name: 'VIG URL',
      nodeType: 'attribute',
      meta: createVigTextMeta('url'),
    },
    {
      id: -2,
      name: 'VIG Event',
      nodeType: 'attribute',
      meta: createVigTextMeta('event'),
    },
    {
      id: -3,
      name: 'VIG Site Category',
      nodeType: 'attribute',
      meta: createVigSiteCategorySelectMeta(),
    },
    {
      id: -4,
      name: 'VIG UTM Source',
      nodeType: 'attribute',
      meta: createVigTextMeta('param_utm_source'),
    },
    {
      id: -5,
      name: 'VIG UTM Medium',
      nodeType: 'attribute',
      meta: createVigTextMeta('param_utm_medium'),
    },
    {
      id: -6,
      name: 'VIG UTM Campaign',
      nodeType: 'attribute',
      meta: createVigTextMeta('param_utm_campaign'),
    },
  ],
});

const createVigSiteCategorySelectMeta = () =>
  toDoubleEncodedMeta({
    type: 'select',
    placeholder: 'Select a VIG Site Category...',
    validation: { required: true },
    values: VIG_SITE_CATEGORY_VALUES,
    operators: VIG_SELECT_OPERATORS,
    permissions: { spyglass: true },
    isVig: true,
    columnName: 'site_category',
    periodDays: { min: 0, max: 120, default: 7 },
  });

export const MOCK_WEBSITE_DATA_VIG_ATTRIBUTES = createVigAttributes();

export const CLIENT_DATA_SOURCES = [
  {
    label: 'Stirista Data',
    categories: [
      {
        id: 2,
        label: 'Personal',
      },
      {
        id: 4,
        label: 'Financial',
      },
      {
        id: 1,
        label: 'Geography',
      },
      {
        id: 3,
        label: 'Intent',
      },
      {
        id: 5,
        label: 'Interest',
      },
      {
        id: 6,
        label: 'Frequent Purchasers',
      },
      {
        id: 8,
        label: 'Automotive',
      },
      {
        id: 7,
        label: 'Political',
      },
    ],
  },
  {
    label: 'Website Data',
    categories: [
      {
        id: 11,
        label: 'Cart',
      },
      {
        id: 12,
        label: 'Checkout',
      },
      {
        id: 10,
        label: 'Pageviews',
      },
      {
        id: 9,
        label: 'Sessions',
      },
      {
        id: 18,
        label: 'VIG Attributes',
      },
    ],
  },
  {
    label: 'Email Data',
    categories: [
      {
        id: 14,
        label: 'Activity Data',
      },
      {
        id: 13,
        label: 'Campaign Data',
      },
    ],
  },
  {
    label: '1st Party Data',
    categories: [
      {
        id: 17,
        label: 'CRM',
      },
      {
        id: 15,
        label: 'Transactional',
      },
    ],
  },
];

export const CLIENT_MISC_PERMISSIONS = {
  allowUserManagement: {
    id: '550e8400-e29b-41d4-a716-446655440003',
    label: 'Allow User Management',
  },
  emailAnalytics: null,
  // {
  //   id: '550e8400-e29b-41d4-a716-446655440004',
  //   label: 'Email Analytics',
  // },
  emailDashboard: {
    id: '550e8400-e29b-41d4-a716-446655440006',
    label: 'Email Dashboard',
  },
  websiteAnalytics: {
    id: '550e8400-e29b-41d4-a716-446655440005',
    label: 'Website Analytics',
  },
  allowDataExports: null,
  // {
  //   id: '550e8400-e29b-41d4-a716-446655440001',
  //   label: 'Allow Data Exports',
  // },
  uploadData: {
    id: '0ab45217-17ba-4e78-95ef-f5298818817a',
    label: 'Upload Data',
  },
  dataExplorer: {
    id: '8f828062-817c-4740-8451-f7615ab3bc3a',
    label: 'Data Explorer',
  },
  googleBigQuery: {
    id: '7f97a552-32a2-4a7b-a192-3bc8ff89c8fa',
    label: 'Google Big Query',
  },
  stiristaDSP: {
    id: 'bf88ef05-1a8c-4a37-8de5-1abcbefdcd6c',
    label: 'Stirista DSP+',
  },
  meta: {
    id: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
    label: 'Meta Export',
  },
};

export const ONBOARDING_STAGES = [
  {
    label: 'Creating GCP project',
    status: 'success',
    message: 'Completed',
  },
  {
    label: 'Enabling GCP services',
    status: 'error',
    message: 'Error',
  },
  {
    label: 'Provisioning resources',
    status: 'inProgress',
    message: 'In Progress',
  },
  {
    label: 'Creating organization',
    status: 'inProgress',
    message: 'In Progress',
  },
  {
    label: 'Ready',
    status: 'inProgress',
    message: 'In Progress',
  },
];

export const MOCK_USER_ROLES = {
  roles: Object.values(userRoles).map(role => ({
    value: role.id,
    label: role.label,
  })),
};

export const MOCK_EXPORTS = {
  exports: [
    {
      id: '1',
      exportName: 'High Value Customers',
      audienceName: 'Audience 1',
      exportType: ExportType.IMMEDIATE,
      status: ExportStatus.COMPLETED,
      scheduledType: null,
      startedDate: new Date('2025-01-01'),
      lastRun: new Date('2025-01-01'),
      currentRuns: 1,
      records: 100,
      initiatedBy: 'John Doe',
      destinations: 2,
    },
    {
      id: '2',
      exportName: 'Weekly Newsletter',
      audienceName: 'Audience 2',
      exportType: ExportType.SCHEDULED,
      status: ExportStatus.RUNNING,
      scheduledType: ScheduledType.ONE_TIME,
      startedDate: new Date('2025-01-02'),
      lastRun: new Date('2025-01-02'),
      currentRuns: 1,
      records: 100,
      initiatedBy: 'Marry Doe',
      destinations: 0,
    },
    {
      id: '3',
      exportName: 'Monthly Newsletter',
      audienceName: 'Audience 3',
      exportType: ExportType.SCHEDULED,
      status: ExportStatus.CANCELLED,
      scheduledType: ScheduledType.RECURRING,
      startedDate: new Date('2025-01-03'),
      lastRun: new Date('2025-01-03'),
      currentRuns: 1,
      records: 100,
      initiatedBy: 'Nick Doe',
      destinations: 3,
    },
    {
      id: '4',
      exportName: 'Seasonal Sales',
      audienceName: 'Audience 4',
      exportType: ExportType.SCHEDULED,
      status: ExportStatus.FAILED,
      scheduledType: ScheduledType.RECURRING,
      startedDate: new Date('2025-01-04'),
      lastRun: new Date('2025-01-04'),
      currentRuns: 1,
      records: 100,
      initiatedBy: 'John Doe',
      destinations: 1,
    },
    {
      id: '5',
      exportName: 'Gender Distribution',
      audienceName: 'Audience 5',
      exportType: ExportType.SCHEDULED,
      status: ExportStatus.SCHEDULED,
      scheduledType: ScheduledType.ONE_TIME,
      startedDate: new Date('2025-01-05'),
      lastRun: new Date('2025-01-05'),
      currentRuns: 1,
      records: 100,
      initiatedBy: 'John Doe',
      destinations: 4,
    },
  ],
  pagination: {
    page: 1,
    pageSize: 10,
    totalItems: 35,
    totalPages: 4,
    sortBy: 'started_date',
    sortOrder: 'desc',
  },
};

/**
 * In-memory store of lookalike jobs. Mutated by the mock POST handler when a
 * new job is started; new rows land in RUNNING to demo the async lifecycle
 * without needing a real timer, and existing rows exercise all four statuses
 * so the list view has real distribution to render.
 */
export const MOCK_LOOKALIKE_JOBS: LookalikeJob[] = [
  {
    jobId: 'll-9a3d1b',
    jobName: 'High-Value Prospects Q4',
    seedAudienceId: 'b12c3d4e-5f6a-7b8c-9d0e-1f2a3b4c5d6e',
    seedAudienceTitle: 'High-Value Customers',
    status: LookalikeJobStatus.COMPLETED,
    targetSize: 500000,
    createdAt: '2026-07-18T14:22:04Z',
    updatedAt: '2026-07-18T14:41:37Z',
    completedAt: '2026-07-18T14:41:37Z',
    initiatedBy: 'Ryan Brown',
    errorReason: null,
    resultAudienceId: 'll-result-9a3d1b',
    seedSize: 74213,
    expandedCount: 748902,
    audienceSize: 412337,
    minAffinity: 0.82,
    overallSimilarity: 87.4,
  },
  {
    jobId: 'll-7c22ee',
    jobName: 'SUV Buyers - New England Homeowners',
    seedAudienceId: 'f78a9b0c-1d2e-3f4a-5b6c-7d8e9f0a1b2c',
    seedAudienceTitle: 'Homeowners 35-55',
    status: LookalikeJobStatus.RUNNING,
    targetSize: 250000,
    createdAt: '2026-07-21T09:14:11Z',
    updatedAt: '2026-07-21T09:14:11Z',
    completedAt: null,
    initiatedBy: 'Ryan Brown',
    seedSize: 74213,
    expandedCount: 748902,
    audienceSize: 412337,
    minAffinity: 0.82,
    overallSimilarity: 87.4,
  },
  {
    jobId: 'll-4f80c2',
    jobName: 'Luxury Travel Q3',
    seedAudienceId: 'b78c9d0e-1f2a-3b4c-5d6e-7f8a9b0c1d2e',
    seedAudienceTitle: 'Travel Enthusiasts',
    status: LookalikeJobStatus.PENDING,
    targetSize: 1000000,
    createdAt: '2026-07-21T09:31:02Z',
    updatedAt: '2026-07-21T09:31:02Z',
    completedAt: null,
    initiatedBy: 'Ryan Brown',
    seedSize: 74213,
    expandedCount: 748902,
    audienceSize: 412337,
    minAffinity: 0.82,
    overallSimilarity: 87.4,
  },
  {
    jobId: 'll-1e0007',
    jobName: 'Failed test run',
    seedAudienceId: '4a10aace-ddbb-493d-b9de-b401c6825c74',
    seedAudienceTitle: 'Last 30 Days Gaming',
    status: LookalikeJobStatus.FAILED,
    targetSize: 100000,
    createdAt: '2026-07-15T13:03:29Z',
    updatedAt: '2026-07-15T13:05:14Z',
    completedAt: null,
    initiatedBy: 'Ryan Brown',
    errorReason: 'Seed materialization failed: 0 rows resolved in universe.',
    seedSize: 74213,
    expandedCount: 748902,
    audienceSize: 412337,
    minAffinity: 0.82,
    overallSimilarity: 87.4,
  },
];

/**
 * Deterministic mock of a completed job's three artifacts. Only jobs in
 * COMPLETED status have results; other statuses return 404 from the mock.
 * The shape mirrors the SPYG-613 contract: cluster metadata is the headline,
 * validation is audience-level (0-100), per-ID affinity is [-1, 1], and
 * optional `affinity` is the BE cutoff that seeds the results slider.
 */
const MOCK_LOOKALIKE_RESULTS_BY_JOB: Record<string, LookalikeResults> = {
  'll-9a3d1b': {
    clusters: [
      {
        clusterId: 0,
        name: 'Affluent Suburban Families',
        description:
          'Married homeowners in outer-ring suburbs with high household income and multi-vehicle households. The dominant cohort in the seed.',
        seedCount: 48210,
        expandedCount: 214300,
      },
      {
        clusterId: 1,
        name: 'Young Professional Urbanites',
        description:
          'Renters and condo owners in dense metros, above-median income, strong credit, single-vehicle households leaning toward CUVs and small SUVs.',
        seedCount: 26440,
        expandedCount: 118200,
      },
      {
        clusterId: 2,
        name: 'Established Empty Nesters',
        description:
          'Age 55+ homeowners with high net worth in mid-sized metros. Historically loyal SUV buyers refreshing their fleet.',
        seedCount: 22105,
        expandedCount: 91700,
      },
      {
        clusterId: 3,
        name: 'Rural High-Earners',
        description:
          'Non-metro homeowners with above-median income; strong preference for larger SUVs and trucks.',
        seedCount: 12830,
        expandedCount: 51600,
      },
      {
        clusterId: 4,
        name: 'Emerging Suburban Buyers',
        description:
          'Younger families in growing suburbs with mid-tier credit; entry-level SUV audience.',
        seedCount: 4017,
        expandedCount: 24200,
      },
    ],
    validation: {
      overallSimilarity: 87.2,
      perAttribute: {
        fin_household_income: 91.4,
        fin_net_worth: 88.1,
        fin_credit_score: 84.3,
        geo_urbanicity: 92.6,
        geo_region: 86.9,
        geo_metro_area: 79.5,
      },
      attributesCompared: [
        'fin_household_income',
        'fin_net_worth',
        'fin_credit_score',
        'geo_urbanicity',
        'geo_region',
        'geo_metro_area',
      ],
    },
    expandedSample: [
      { stiristaId: '100000123', clusterId: 0, affinity: 0.94 },
      { stiristaId: '100000456', clusterId: 0, affinity: 0.91 },
      { stiristaId: '100000789', clusterId: 1, affinity: 0.88 },
      { stiristaId: '100001012', clusterId: 2, affinity: 0.86 },
      { stiristaId: '100001345', clusterId: 0, affinity: 0.83 },
      { stiristaId: '100001678', clusterId: 3, affinity: 0.8 },
      { stiristaId: '100002011', clusterId: 1, affinity: 0.78 },
      { stiristaId: '100002344', clusterId: 0, affinity: 0.75 },
      { stiristaId: '100002677', clusterId: 2, affinity: 0.72 },
      { stiristaId: '100003010', clusterId: 4, affinity: 0.69 },
      { stiristaId: '100003343', clusterId: 1, affinity: 0.66 },
      { stiristaId: '100003676', clusterId: 0, affinity: 0.63 },
      { stiristaId: '100004009', clusterId: 3, affinity: 0.6 },
      { stiristaId: '100004342', clusterId: 2, affinity: 0.57 },
      { stiristaId: '100004675', clusterId: 0, affinity: 0.53 },
      { stiristaId: '100005008', clusterId: 1, affinity: 0.5 },
      { stiristaId: '100005341', clusterId: 4, affinity: 0.46 },
      { stiristaId: '100005674', clusterId: 0, affinity: 0.42 },
      { stiristaId: '100006007', clusterId: 2, affinity: 0.38 },
      { stiristaId: '100006340', clusterId: 1, affinity: 0.33 },
      { stiristaId: '100006673', clusterId: 3, affinity: 0.28 },
      { stiristaId: '100007006', clusterId: 4, affinity: 0.21 },
      { stiristaId: '100007339', clusterId: 0, affinity: 0.15 },
      { stiristaId: '100007672', clusterId: 1, affinity: 0.08 },
      { stiristaId: '100008005', clusterId: 2, affinity: 0.02 },
      { stiristaId: '100008338', clusterId: 4, affinity: -0.05 },
      { stiristaId: '100008671', clusterId: 3, affinity: -0.14 },
      { stiristaId: '100009004', clusterId: 0, affinity: -0.22 },
      { stiristaId: '100009337', clusterId: 1, affinity: -0.31 },
      { stiristaId: '100009670', clusterId: 4, affinity: -0.42 },
    ],
  },
};

/**
 * Read-only accessor. Returns `null` when the job isn't completed or has no
 * mock results seeded — the mock handler translates that into a 404.
 */
export function getMockLookalikeResults(
  jobId: string,
): LookalikeResults | null {
  return MOCK_LOOKALIKE_RESULTS_BY_JOB[jobId] ?? null;
}

/** Default lookalike sample size returned when an org has not overridden it. */
export const DEFAULT_LOOKALIKE_SAMPLE_SIZE = 1000;

/** In-memory org lookalike settings. Missing keys mean the BE default. */
export const MOCK_LOOKALIKE_SETTINGS_BY_ORG = new Map<
  string,
  LookalikeSettings
>();

/**
 * Returns org lookalike settings, falling back to the default sample size.
 * @param organizationId - Organization whose settings are requested.
 * @returns Current settings for the org.
 */
export function getMockLookalikeSettings(
  organizationId: string,
): LookalikeSettings {
  return (
    MOCK_LOOKALIKE_SETTINGS_BY_ORG.get(organizationId) ?? {
      sampleSize: DEFAULT_LOOKALIKE_SAMPLE_SIZE,
      isDefault: true,
    }
  );
}

/**
 * Stores a custom lookalike sample size for an org.
 * @param organizationId - Organization whose settings are updated.
 * @param sampleSize - New sample size.
 * @returns Saved settings with `isDefault: false`.
 */
export function setMockLookalikeSettings(
  organizationId: string,
  sampleSize: number,
): LookalikeSettings {
  const next: LookalikeSettings = {
    sampleSize,
    isDefault: false,
  };
  MOCK_LOOKALIKE_SETTINGS_BY_ORG.set(organizationId, next);
  return next;
}

/**
 * Produces an AttributesResponse-shaped payload for the Audience Builder's
 * Lookalike source (Story 3). Only COMPLETED jobs surface as attributes —
 * PENDING / RUNNING don't have score tables yet, and FAILED never will. The
 * meta contract mirrors the existing "Age" range attribute:
 *   - type: range, greater_than as default operator (matches epic example)
 *   - rangeConfig: [-1, 1], step 0.01, consistent with the Results-view slider
 *   - meta is doubly-JSON-encoded to match how the BE serves it (see comment
 *     in ApiAttribute; parseNodeField / normalizeNodeMeta un-nests it).
 *
 * In production the BE joins tenant_lookalike_attributes to the caller's
 * tenant and only returns models the tenant owns; the mock is tenant-agnostic
 * like every other attributes mock — see in-memory-data.service comments.
 */
export function getMockLookalikeAttributes() {
  const completedJobs = MOCK_LOOKALIKE_JOBS.filter(
    j => j.status === LookalikeJobStatus.COMPLETED,
  );
  const rangeMeta = {
    type: 'range',
    placeholder: 'Enter minimum affinity (-1 to 1)...',
    validation: { required: true },
    values: [],
    operators: [
      { operator: 'greater_than', value: '>' },
      { operator: 'greater_than_or_equal', value: '>=' },
      { operator: 'between', value: '<>' },
      { operator: 'less_than', value: '<' },
      { operator: 'less_than_or_equal', value: '<=' },
    ],
    permissions: { spyglass: true },
    rangeConfig: { min: -1, max: 1, step: 0.01 },
  };
  return {
    attributes: completedJobs.map(job => ({
      id: job.jobId,
      name: `Lookalike: ${job.jobName}`,
      nodeType: 'attribute',
      meta: JSON.stringify(JSON.stringify(rangeMeta)),
    })),
  };
}

export const MOCK_FIELDS = {
  categories: [
    {
      id: 1,
      label: 'All Fields',
    },
    {
      id: 2,
      label: 'Identity Fields',
    },
    {
      id: 3,
      label: 'Contact Fields',
    },
  ],
  fields: [
    {
      id: 1,
      label: 'Stirista ID',
      categoryId: 2,
      type: ['identity', 'required'],
      description: 'Unique identifier for each record',
      required: true,
    },
    {
      id: 2,
      label: 'Email Address',
      categoryId: 3,
      type: ['contact', 'email'],
      description: 'Primary email address',
      required: false,
    },
    {
      id: 3,
      label: 'First Name',
      categoryId: 3,
      type: ['contact'],
      description: 'First Name',
      required: false,
    },
    {
      id: 4,
      label: 'Last Name',
      categoryId: 3,
      type: ['contact'],
      description: 'Last Name',
      required: false,
    },
    {
      id: 5,
      label: 'Phone Number',
      categoryId: 3,
      type: ['contact', 'phone'],
      description: 'Primary phone number',
      required: false,
    },
  ],
};

export const MOCK_REPORTS_STATISTICS = {
  total_reports: 0,
  unique_campaigns_count: 0,
  most_recent_report_date: '12/8/2025',
};

export const MOCK_WEBSITE_ANALYTICS_REPORT = {
  reportId: null,
  generatedAt: '2026-03-23T14:00:00Z',
  prompt:
    'Show me visitors to the TV pages who added to cart in the last 30 days',
  dateRange: 'Last 30 Days',
  summaryText:
    'During Last 30 Days, your website received 2,975,199 total visits from 2,678,334 unique visitors, with a 17.61% identification rate. The top behavioral events were Product View (214,856) and Session Abandoned (84,312). Among identified visitors, Male visitors represented 51.1% of the identified audience.',
  insights: [
    {
      title: 'Traffic & Identification',
      subsections: [
        {
          title: 'Visitor Volume & Match Rate',
          bullet_points: [
            '2.68M unique visitors recorded over the period with a 17.6% identification match rate.',
            'Identified visitors skew toward the 35-54 age bracket, which accounts for the largest share of matched profiles.',
            'Mobile traffic remains dominant, so audience targeting should prioritize mobile-first experiences.',
          ],
        },
        {
          title: 'Traffic Sources',
          bullet_points: [
            'Organic and direct channels drive the largest volume of identified visitors in this window.',
            'Lower-identification referral traffic can be improved by pairing campaign links with first-party audience capture.',
          ],
        },
      ],
    },
    {
      title: 'Conversion & Events',
      subsections: [
        {
          title: 'Behavioral Event Summary',
          bullet_points: [
            'Product View is the dominant behavior, followed by cart-related actions and checkout completion.',
            'A meaningful drop-off appears between product views and later funnel actions, indicating conversion friction.',
            'Session and cart abandonment cohorts are strong candidates for fast retargeting workflows.',
          ],
        },
        {
          title: 'Funnel Opportunity',
          bullet_points: [
            'Checkout and cart abandonment events identify high-intent audiences that can be re-engaged with short-delay campaigns.',
            'Behavior-triggered messaging should focus on product revisit and completion nudges.',
          ],
        },
      ],
    },
    {
      title: 'Leads & Opportunities',
      subsections: [
        {
          title: 'Lead Pipeline Overview',
          bullet_points: [
            'High-intent and pre-activation audiences indicate a healthy opportunity pool for nurture and conversion programs.',
            'Potential opportunities should be segmented by recency and depth of behavioral intent.',
          ],
        },
        {
          title: 'Recommended Actions',
          bullet_points: [
            'Activate retargeting for cart and checkout-abandoner segments within 24-48 hours.',
            'Create dedicated nurture journeys for return visitors and product browsers with high revisit frequency.',
            'Align lead-priority scoring with conversion propensity signals from segment profiles.',
          ],
        },
      ],
    },
  ],
  metrics: {
    appliedFilters: {
      pageFilter: 'tv',
      urlPattern: '/tvs',
      eventFilter: 'cart_add',
      startDate: '2026-03-01',
      endDate: '2026-03-24',
    },
    engagement: {
      totalVisits: 2975199,
      totalVisitors: 2678334,
      identifiedVisits: 509149,
      identifiedVisitors: 471603,
      matchRate: 17.61,
    },
    eventTracking: {
      abandonedSessions: 84312,
      abandonedCarts: 31204,
      productViews: 214856,
      brandComparisons: 555,
      purchaseConversions: 19847,
      loyaltySignups: 444,
      events: [
        {
          eventId: 1,
          eventName: 'Product View',
          eventTag: 'product_view',
          count: 214856,
          percentage: 100.0,
        },
        {
          eventId: 2,
          eventName: 'Cart Add',
          eventTag: 'cart_add',
          count: 51051,
          percentage: 23.7,
        },
        {
          eventId: 3,
          eventName: 'Checkout Completion',
          eventTag: 'checkout',
          count: 19847,
          percentage: 9.2,
        },
        {
          eventId: 4,
          eventName: 'Identified Session',
          eventTag: 'identified_session',
          count: 509149,
          percentage: 237.0,
        },
      ],
    },
    visitorProfiling: {
      gender: [
        {
          label: 'Male',
          count: 241050,
          percentage: 51.1,
        },
        {
          label: 'Female',
          count: 221740,
          percentage: 47.0,
        },
        {
          label: 'Unknown',
          count: 8813,
          percentage: 1.9,
        },
      ],
      race: [
        {
          label: 'White',
          count: 290860,
          percentage: 61.7,
        },
        {
          label: 'Hispanic',
          count: 72715,
          percentage: 15.4,
        },
        {
          label: 'Black / African American',
          count: 47163,
          percentage: 10.0,
        },
        {
          label: 'Asian',
          count: 28297,
          percentage: 6.0,
        },
        {
          label: 'Other',
          count: 32568,
          percentage: 6.9,
        },
      ],
      maritalStatus: [
        {
          label: 'Married',
          count: 235310,
          percentage: 49.9,
        },
        {
          label: 'Single',
          count: 188062,
          percentage: 39.9,
        },
        {
          label: 'Other',
          count: 48231,
          percentage: 10.2,
        },
      ],
      ageBuckets: [
        {
          label: '18–24',
          count: 42853,
          percentage: 9.1,
        },
        {
          label: '25–34',
          count: 99302,
          percentage: 21.1,
        },
        {
          label: '35–44',
          count: 113590,
          percentage: 24.1,
        },
        {
          label: '45–54',
          count: 98741,
          percentage: 20.9,
        },
        {
          label: '55–64',
          count: 78234,
          percentage: 16.6,
        },
        {
          label: '65+',
          count: 38883,
          percentage: 8.2,
        },
      ],
      incomeLevels: [
        {
          label: 'Under $30K',
          count: 37497,
          percentage: 7.9,
        },
        {
          label: '$30K–$50K',
          count: 75921,
          percentage: 16.1,
        },
        {
          label: '$50K–$75K',
          count: 110562,
          percentage: 23.4,
        },
        {
          label: '$75K–$100K',
          count: 98327,
          percentage: 20.9,
        },
        {
          label: '$100K–$150K',
          count: 84110,
          percentage: 17.8,
        },
        {
          label: '$150K+',
          count: 65186,
          percentage: 13.9,
        },
      ],
      creditRanges: [
        {
          label: '300–579 (Poor)',
          count: 37969,
          percentage: 8.1,
        },
        {
          label: '580–669 (Fair)',
          count: 70661,
          percentage: 15.0,
        },
        {
          label: '670–739 (Good)',
          count: 118925,
          percentage: 25.2,
        },
        {
          label: '740–799 (Very Good)',
          count: 132266,
          percentage: 28.1,
        },
        {
          label: '800–850 (Exceptional)',
          count: 111782,
          percentage: 23.7,
        },
      ],
      presenceOfChildren: [
        {
          hasChildren: true,
          label: 'Has Children',
          count: 179450,
          percentage: 38.1,
        },
        {
          hasChildren: false,
          label: 'No Children',
          count: 291153,
          percentage: 61.9,
        },
      ],
      occupationCategories: [
        {
          label: 'Professional / Technical',
          count: 128240,
          percentage: 27.2,
        },
        {
          label: 'Management / Executive',
          count: 84755,
          percentage: 18.0,
        },
        {
          label: 'Retired',
          count: 35640,
          percentage: 7.6,
        },
        {
          label: 'Other',
          count: 222968,
          percentage: 47.2,
        },
      ],
      household: {
        men: 241050,
        women: 221740,
        adults: 471603,
        children: 179450,
        totalHouseholds: 287604,
      },
    },
    segmentProfiles: [
      {
        segmentId: 'seg-001',
        segmentName: 'Active Purchasers',
        description: 'Visitors who completed a purchase during the period.',
        visitCount: 19847,
        conversionRate: 4.21,
        attributeIds: [],
      },
      {
        segmentId: 'seg-002',
        segmentName: 'Cart Abandoners',
        description: 'Visitors who added items to cart but did not check out.',
        visitCount: 31204,
        conversionRate: 6.62,
        attributeIds: [],
      },
      {
        segmentId: 'seg-003',
        segmentName: 'Product Browsers',
        description: 'Visitors who viewed products without adding to cart.',
        visitCount: 214856,
        conversionRate: 45.56,
        attributeIds: [],
      },
      {
        segmentId: 'seg-004',
        segmentName: 'Return Visitors',
        description: 'Visitors with 3 or more lifetime sessions.',
        visitCount: 198340,
        conversionRate: 42.06,
        attributeIds: [],
      },
      {
        segmentId: 'seg-005',
        segmentName: 'New Visitors',
        description: 'First-time visitors with a single session.',
        visitCount: 7356,
        conversionRate: 1.56,
        attributeIds: [],
      },
    ],
    geographicDistribution: [
      {
        stateCode: 'CA',
        stateName: 'California',
        count: 341209,
        percentage: 12.7,
      },
      { stateCode: 'TX', stateName: 'Texas', count: 287634, percentage: 10.7 },
      { stateCode: 'FL', stateName: 'Florida', count: 214870, percentage: 8.0 },
      {
        stateCode: 'NY',
        stateName: 'New York',
        count: 198450,
        percentage: 7.4,
      },
      {
        stateCode: 'IL',
        stateName: 'Illinois',
        count: 142310,
        percentage: 5.3,
      },
      {
        stateCode: 'PA',
        stateName: 'Pennsylvania',
        count: 128740,
        percentage: 4.8,
      },
      { stateCode: 'OH', stateName: 'Ohio', count: 112560, percentage: 4.2 },
      { stateCode: 'GA', stateName: 'Georgia', count: 109830, percentage: 4.1 },
      {
        stateCode: 'NC',
        stateName: 'North Carolina',
        count: 98450,
        percentage: 3.7,
      },
      { stateCode: 'MI', stateName: 'Michigan', count: 94210, percentage: 3.5 },
      {
        stateCode: 'NJ',
        stateName: 'New Jersey',
        count: 87640,
        percentage: 3.3,
      },
      { stateCode: 'VA', stateName: 'Virginia', count: 82310, percentage: 3.1 },
      {
        stateCode: 'WA',
        stateName: 'Washington',
        count: 78920,
        percentage: 2.9,
      },
      { stateCode: 'AZ', stateName: 'Arizona', count: 74580, percentage: 2.8 },
      {
        stateCode: 'MA',
        stateName: 'Massachusetts',
        count: 68940,
        percentage: 2.6,
      },
      {
        stateCode: 'TN',
        stateName: 'Tennessee',
        count: 62310,
        percentage: 2.3,
      },
      { stateCode: 'IN', stateName: 'Indiana', count: 58470, percentage: 2.2 },
      { stateCode: 'MO', stateName: 'Missouri', count: 54230, percentage: 2.0 },
      { stateCode: 'MD', stateName: 'Maryland', count: 51890, percentage: 1.9 },
      {
        stateCode: 'WI',
        stateName: 'Wisconsin',
        count: 48320,
        percentage: 1.8,
      },
      { stateCode: 'CO', stateName: 'Colorado', count: 47810, percentage: 1.8 },
      {
        stateCode: 'MN',
        stateName: 'Minnesota',
        count: 44670,
        percentage: 1.7,
      },
      {
        stateCode: 'SC',
        stateName: 'South Carolina',
        count: 41230,
        percentage: 1.5,
      },
      { stateCode: 'AL', stateName: 'Alabama', count: 38940, percentage: 1.5 },
      {
        stateCode: 'LA',
        stateName: 'Louisiana',
        count: 36780,
        percentage: 1.4,
      },
      { stateCode: 'KY', stateName: 'Kentucky', count: 34120, percentage: 1.3 },
      { stateCode: 'OR', stateName: 'Oregon', count: 32780, percentage: 1.2 },
      { stateCode: 'OK', stateName: 'Oklahoma', count: 28940, percentage: 1.1 },
      {
        stateCode: 'CT',
        stateName: 'Connecticut',
        count: 26410,
        percentage: 1.0,
      },
      { stateCode: 'UT', stateName: 'Utah', count: 24870, percentage: 0.9 },
      { stateCode: 'NV', stateName: 'Nevada', count: 22640, percentage: 0.8 },
      { stateCode: 'AR', stateName: 'Arkansas', count: 19870, percentage: 0.7 },
      {
        stateCode: 'MS',
        stateName: 'Mississippi',
        count: 17630,
        percentage: 0.7,
      },
      { stateCode: 'KS', stateName: 'Kansas', count: 16480, percentage: 0.6 },
      {
        stateCode: 'NM',
        stateName: 'New Mexico',
        count: 14230,
        percentage: 0.5,
      },
      { stateCode: 'NE', stateName: 'Nebraska', count: 13780, percentage: 0.5 },
      {
        stateCode: 'WV',
        stateName: 'West Virginia',
        count: 11240,
        percentage: 0.4,
      },
      { stateCode: 'ID', stateName: 'Idaho', count: 10870, percentage: 0.4 },
      { stateCode: 'HI', stateName: 'Hawaii', count: 9840, percentage: 0.4 },
      {
        stateCode: 'NH',
        stateName: 'New Hampshire',
        count: 9120,
        percentage: 0.3,
      },
      { stateCode: 'ME', stateName: 'Maine', count: 8340, percentage: 0.3 },
      { stateCode: 'MT', stateName: 'Montana', count: 7210, percentage: 0.3 },
      {
        stateCode: 'RI',
        stateName: 'Rhode Island',
        count: 6870,
        percentage: 0.3,
      },
      { stateCode: 'DE', stateName: 'Delaware', count: 6430, percentage: 0.2 },
      {
        stateCode: 'SD',
        stateName: 'South Dakota',
        count: 5920,
        percentage: 0.2,
      },
      {
        stateCode: 'ND',
        stateName: 'North Dakota',
        count: 5340,
        percentage: 0.2,
      },
      { stateCode: 'AK', stateName: 'Alaska', count: 4870, percentage: 0.2 },
      { stateCode: 'VT', stateName: 'Vermont', count: 4210, percentage: 0.2 },
      { stateCode: 'WY', stateName: 'Wyoming', count: 3780, percentage: 0.1 },
      {
        stateCode: 'DC',
        stateName: 'Washington D.C.',
        count: 18430,
        percentage: 0.7,
      },
    ],
    leadCapture: {
      highIntentSessions: 19847,
      preActivationLeads: 31204,
      b2bSalesLeads: 333,
      potentialOpportunities: 31204,
    },
  },
};

/** Endpoint 1 response: export header + lightweight destination stubs. */
export const MOCK_EXPORT_DETAIL: ExportDetail = {
  id: 'exp-a1b2c3d4-e5f6-7890-abcd-ef1234567890',
  name: 'High Value Customers Export',
  audienceId: '4a10aace-ddbb-493d-b9de-b401c6825c74',
  audienceName: 'High Value Customers',
  createdDate: '2026-03-01T10:00:00.000Z',
  initialJobDate: '2026-03-01T10:05:00.000Z',
  destinations: [
    {
      destinationId: '8acc167d-efce-4755-baf0-54dfb203c4c2',
      name: 'exports_dataset_google_bigquery',
      iconId: 'database',
    },
    {
      destinationId: 'dsp-dest-001',
      name: 'exports_dataset_dsp_campaign',
      iconId: 'campaign',
    },
  ],
};

/** Endpoint 2 response mock: BigQuery destination detail with scheduling and versions. */
export const MOCK_EXPORT_DESTINATION_DETAIL_BQ: ExportDestinationDetail = {
  destinationId: '8acc167d-efce-4755-baf0-54dfb203c4c2',
  name: 'exports_dataset_google_bigquery',
  icon: 'database',
  audience: 'High Value Customers',
  scheduling: {
    frequency: ExportFrequency.DAILY,
    time: '09:00 AM',
    startDate: '2026-03-01T09:00:00.000Z',
    endDate: null,
    nextRun: '2026-03-26T09:00:00.000Z',
  },
  versions: [
    {
      version: 3,
      audienceName: 'High Value Customers v3',
      audienceCriteria:
        'Age 25-54 AND Interest = Streaming AND Income = High AND Region = US',
      isLatest: true,
      destinationConfig: {
        type: 'bigquery',
        dataset: 'marketing_data',
        table: 'high_value_customers_v3',
        writePreference: ExportWritePreference.APPEND,
      },
      fields: [
        { name: 'Stirista ID', required: true },
        { name: 'Email', required: false },
        { name: 'Zip Code', required: false },
        { name: 'Age', required: false },
      ],
      jobCount: 15,
    },
    {
      version: 2,
      audienceName: 'High Value Customers',
      audienceCriteria: 'Age 25-54 AND Interest = Streaming AND Income = High',
      isLatest: false,
      destinationConfig: {
        type: 'bigquery',
        dataset: 'marketing_data',
        table: 'high_value_customers',
        writePreference: ExportWritePreference.APPEND,
      },
      fields: [
        { name: 'Stirista ID', required: true },
        { name: 'Email', required: false },
        { name: 'Zip Code', required: false },
        { name: 'Age', required: false },
      ],
      jobCount: 2,
    },
    {
      version: 1,
      audienceName: 'High Value Customers',
      audienceCriteria: 'Age 25-54 AND Interest = Streaming',
      isLatest: false,
      destinationConfig: {
        type: 'bigquery',
        dataset: 'marketing_data',
        table: 'hvc_initial',
        writePreference: ExportWritePreference.TRUNCATE,
      },
      fields: [
        { name: 'Stirista ID', required: true },
        { name: 'Email', required: false },
        { name: 'Zip Code', required: false },
      ],
      jobCount: 2,
    },
  ],
};

/**
 * Endpoint 2 response mock: DSP destination detail.
 * scheduling is null because this was a one-time immediate export.
 * fields is null because DSP destinations do not use exported fields.
 */
export const MOCK_EXPORT_DESTINATION_DETAIL_DSP: ExportDestinationDetail = {
  destinationId: 'dsp-dest-001',
  name: 'exports_dataset_dsp_campaign',
  icon: 'campaign',
  audience: 'High Value Customers',
  scheduling: null,
  versions: [
    {
      version: 1,
      audienceName: 'High Value Customers',
      audienceCriteria: 'Age 25-54 AND Interest = Streaming',
      isLatest: true,
      destinationConfig: {
        type: 'dsp',
        segmentName: 'HVC_Streaming_Q1',
      },
      fields: null,
      jobCount: 1,
    },
  ],
};

/** Endpoint 3 response mock: paginated jobs for BigQuery destination, version 2. */
export const MOCK_EXPORT_VERSION_JOBS_BQ_V2: ExportVersionJobsResponse = {
  jobs: [
    {
      jobId: 'job-bq-003',
      status: JobStatus.COMPLETED,
      started: '2026-03-14T09:00:00.000Z',
      completed: '2026-03-14T09:03:00.000Z',
      duration: '3m',
      records: 24120,
    },
    {
      jobId: 'job-bq-004',
      status: JobStatus.FAILED,
      started: '2026-03-15T09:00:00.000Z',
      completed: null,
      duration: '1m',
      records: null,
    },
  ],
  pagination: {
    page: 1,
    pageSize: 10,
    totalItems: 2,
    totalPages: 1,
    sortBy: null,
    sortOrder: 'desc',
  },
};

/**
 * Endpoint 3 response mock: paginated jobs for BigQuery destination, version 3, page 1.
 * Version 3 has 15 total jobs to exercise the paginator (pageSize 10 → 2 pages).
 */
export const MOCK_EXPORT_VERSION_JOBS_BQ_V3_PAGE1: ExportVersionJobsResponse = {
  jobs: [
    {
      jobId: 'job-bq-v3-001',
      status: JobStatus.COMPLETED,
      started: '2026-04-01T09:00:00.000Z',
      completed: '2026-04-01T09:04:00.000Z',
      duration: '4m',
      records: 31200,
    },
    {
      jobId: 'job-bq-v3-002',
      status: JobStatus.COMPLETED,
      started: '2026-04-02T09:00:00.000Z',
      completed: '2026-04-02T09:05:00.000Z',
      duration: '5m',
      records: 31450,
    },
    {
      jobId: 'job-bq-v3-003',
      status: JobStatus.FAILED,
      started: '2026-04-03T09:00:00.000Z',
      completed: null,
      duration: '1m',
      records: null,
    },
    {
      jobId: 'job-bq-v3-004',
      status: JobStatus.COMPLETED,
      started: '2026-04-04T09:00:00.000Z',
      completed: '2026-04-04T09:03:00.000Z',
      duration: '3m',
      records: 30100,
    },
    {
      jobId: 'job-bq-v3-005',
      status: JobStatus.COMPLETED,
      started: '2026-04-05T09:00:00.000Z',
      completed: '2026-04-05T09:04:00.000Z',
      duration: '4m',
      records: 29800,
    },
    {
      jobId: 'job-bq-v3-006',
      status: JobStatus.RUNNING,
      started: '2026-04-06T09:00:00.000Z',
      completed: null,
      duration: null,
      records: null,
    },
    {
      jobId: 'job-bq-v3-007',
      status: JobStatus.COMPLETED,
      started: '2026-04-07T09:00:00.000Z',
      completed: '2026-04-07T09:06:00.000Z',
      duration: '6m',
      records: 32000,
    },
    {
      jobId: 'job-bq-v3-008',
      status: JobStatus.COMPLETED,
      started: '2026-04-08T09:00:00.000Z',
      completed: '2026-04-08T09:03:00.000Z',
      duration: '3m',
      records: 31700,
    },
    {
      jobId: 'job-bq-v3-009',
      status: JobStatus.FAILED,
      started: '2026-04-09T09:00:00.000Z',
      completed: null,
      duration: '2m',
      records: null,
    },
    {
      jobId: 'job-bq-v3-010',
      status: JobStatus.COMPLETED,
      started: '2026-04-10T09:00:00.000Z',
      completed: '2026-04-10T09:04:00.000Z',
      duration: '4m',
      records: 30900,
    },
  ],
  pagination: {
    page: 1,
    pageSize: 10,
    totalItems: 15,
    totalPages: 2,
    sortBy: null,
    sortOrder: 'desc',
  },
};

/**
 * Endpoint 3 response mock: paginated jobs for BigQuery destination, version 3, page 2.
 * Returns the remaining 5 of 15 total jobs.
 */
export const MOCK_EXPORT_VERSION_JOBS_BQ_V3_PAGE2: ExportVersionJobsResponse = {
  jobs: [
    {
      jobId: 'job-bq-v3-011',
      status: JobStatus.COMPLETED,
      started: '2026-04-11T09:00:00.000Z',
      completed: '2026-04-11T09:05:00.000Z',
      duration: '5m',
      records: 31100,
    },
    {
      jobId: 'job-bq-v3-012',
      status: JobStatus.COMPLETED,
      started: '2026-04-12T09:00:00.000Z',
      completed: '2026-04-12T09:03:00.000Z',
      duration: '3m',
      records: 30500,
    },
    {
      jobId: 'job-bq-v3-013',
      status: JobStatus.FAILED,
      started: '2026-04-13T09:00:00.000Z',
      completed: null,
      duration: '1m',
      records: null,
    },
    {
      jobId: 'job-bq-v3-014',
      status: JobStatus.COMPLETED,
      started: '2026-04-14T09:00:00.000Z',
      completed: '2026-04-14T09:04:00.000Z',
      duration: '4m',
      records: 32400,
    },
    {
      jobId: 'job-bq-v3-015',
      status: JobStatus.SCHEDULED,
      started: '2026-04-15T09:00:00.000Z',
      completed: null,
      duration: null,
      records: null,
    },
  ],
  pagination: {
    page: 2,
    pageSize: 10,
    totalItems: 15,
    totalPages: 2,
    sortBy: null,
    sortOrder: 'desc',
  },
};

export interface AiConfigAgentOverrides {
  prompt?: string | null;
  output_detail?: string;
  temperature?: number;
  max_output_tokens?: number;
}

export interface AiConfigOrgAgentOverrides {
  prompt?: string | null;
}

export interface AiConfigStoreEntry {
  organization: {
    banned_words?: string[];
    agents?: Partial<Record<AiConfigAgentKey, AiConfigOrgAgentOverrides>>;
  };
  tenant: {
    banned_words?: string[];
    agents?: Partial<Record<AiConfigAgentKey, AiConfigAgentOverrides>>;
  };
}

/** In-memory AI config overrides keyed by orgId:tenantId. */
export const MOCK_AI_CONFIG_STORE = new Map<string, AiConfigStoreEntry>();

/** Singleton platform-wide global AI config overrides. */
export interface AiConfigGlobalStoreEntry {
  banned_words?: string[];
  settings?: Record<string, number>;
}

export let MOCK_AI_CONFIG_GLOBAL_STORE: AiConfigGlobalStoreEntry = {};

/**
 * Builds the store key for org/tenant AI config overrides.
 * @param orgId - Organization identifier.
 * @param tenantId - Tenant identifier.
 * @returns Composite store key.
 */
export function getAiConfigStoreKey(orgId: string, tenantId: string): string {
  return `${orgId}:${tenantId}`;
}

/**
 * Merges catalog agent metadata with stored overrides for tenant GET
 * responses (prompt + settings).
 * @param overrides - Persisted per-agent value overrides.
 * @returns Full tenant agent GET objects for all 6 agents.
 */
function buildAgentsFromStore(
  overrides: Partial<Record<AiConfigAgentKey, AiConfigAgentOverrides>> = {},
): AiConfigAgentGet[] {
  return AI_CONFIG_AGENT_CATALOG.map(catalogAgent => {
    const agentOverride = overrides[catalogAgent.key];
    return {
      ...catalogAgent,
      prompt: {
        ...catalogAgent.prompt,
        value: agentOverride?.prompt ?? catalogAgent.prompt.value,
      },
      output_detail: {
        ...catalogAgent.output_detail,
        value: agentOverride?.output_detail
          ? (agentOverride.output_detail as AiConfigAgentGet['output_detail']['value'])
          : catalogAgent.output_detail.value,
      },
      temperature: {
        ...catalogAgent.temperature,
        value: agentOverride?.temperature ?? catalogAgent.temperature.value,
      },
      max_output_tokens: {
        ...catalogAgent.max_output_tokens,
        value:
          agentOverride?.max_output_tokens ??
          catalogAgent.max_output_tokens.value,
      },
    };
  });
}

/**
 * Merges catalog agent metadata with stored overrides for organization GET
 * responses (prompt only).
 * @param overrides - Persisted per-agent prompt overrides.
 * @returns Organization-scope agent GET objects for all 6 agents.
 */
function buildOrgAgentsFromStore(
  overrides: Partial<Record<AiConfigAgentKey, AiConfigOrgAgentOverrides>> = {},
): AiConfigOrgAgentGet[] {
  return AI_CONFIG_AGENT_CATALOG.map(catalogAgent => {
    const agentOverride = overrides[catalogAgent.key];
    return {
      key: catalogAgent.key,
      display_name: catalogAgent.display_name,
      prompt: {
        ...catalogAgent.prompt,
        value: agentOverride?.prompt ?? catalogAgent.prompt.value,
      },
    };
  });
}

/**
 * Builds a scoped GET /ai-config response from catalog + in-memory store.
 * @param options - Requested scopes and IDs.
 * @returns FE-ready AI config for the requested scopes.
 */
export function buildAiConfigGetResponse(options: {
  scopes?: AiConfigScope[];
  orgId?: string;
  tenantId?: string;
}): AiConfigGetResponse {
  const scopes = options.scopes?.length
    ? options.scopes
    : (['global', 'organization', 'tenant'] as AiConfigScope[]);
  const includeGlobal = scopes.includes('global');
  const includeOrg = scopes.includes('organization');
  const includeTenant = scopes.includes('tenant');

  const response: AiConfigGetResponse = {};

  if (includeGlobal) {
    response.global_config = {
      banned_words: {
        value: MOCK_AI_CONFIG_GLOBAL_STORE.banned_words ?? [],
        info: AI_CONFIG_GLOBAL_BANNED_WORDS_INFO,
      },
      setting_categories: cloneAiConfigGlobalSettingCategories(
        MOCK_AI_CONFIG_GLOBAL_STORE.settings ?? {},
      ),
    };
  }

  const stored = resolveAiConfigStoreEntry(options.orgId, options.tenantId);

  if (includeOrg) {
    response.organization_id = options.orgId ?? '';
    response.organization_config = {
      banned_words: {
        value: stored.organization.banned_words ?? [],
        info: AI_CONFIG_ORG_BANNED_WORDS_INFO,
      },
      agents: buildOrgAgentsFromStore(stored.organization.agents),
    };
  }

  if (includeTenant) {
    response.tenant_id = options.tenantId ?? '';
    response.tenant_config = {
      banned_words: {
        value: stored.tenant.banned_words ?? [],
        info: AI_CONFIG_TENANT_BANNED_WORDS_INFO,
      },
      agents: buildAgentsFromStore(stored.tenant.agents),
    };
  }

  return response;
}

/**
 * Resolves the best matching org/tenant store entry for a scoped GET.
 * @param orgId - Optional organization identifier.
 * @param tenantId - Optional tenant identifier.
 * @returns Matching store entry or empty defaults.
 */
function resolveAiConfigStoreEntry(
  orgId?: string,
  tenantId?: string,
): AiConfigStoreEntry {
  if (orgId && tenantId) {
    return (
      MOCK_AI_CONFIG_STORE.get(getAiConfigStoreKey(orgId, tenantId)) ?? {
        organization: {},
        tenant: {},
      }
    );
  }

  for (const [key, entry] of MOCK_AI_CONFIG_STORE.entries()) {
    const [storedOrgId, storedTenantId] = key.split(':');
    if (orgId && storedOrgId === orgId) {
      return entry;
    }
    if (tenantId && storedTenantId === tenantId) {
      return entry;
    }
  }

  return { organization: {}, tenant: {} };
}

/**
 * Validates a global setting PUT item against catalog min/max constraints.
 * @param settingPut - Setting partial update from PUT body.
 * @returns Error message when invalid; null when valid.
 */
function validateGlobalSettingPut(
  settingPut: AiConfigGlobalSettingPut,
): string | null {
  const catalogSetting = AI_CONFIG_GLOBAL_SETTING_CATEGORIES.flatMap(
    category => category.settings,
  ).find(setting => setting.key === settingPut.key);

  if (!catalogSetting) {
    return `Unknown global setting key: ${settingPut.key}`;
  }

  const { min, max } = catalogSetting;
  if (
    min !== undefined &&
    max !== undefined &&
    (settingPut.value < min || settingPut.value > max)
  ) {
    return `${settingPut.key} must be between ${min} and ${max}`;
  }

  return null;
}

/**
 * Validates an agent PUT payload against catalog min/max constraints.
 * @param agentPut - Agent partial update from PUT body.
 * @returns Error message when invalid; null when valid.
 */
function validateAgentPut(agentPut: AiConfigAgentPut): string | null {
  const catalogAgent = AI_CONFIG_AGENT_CATALOG.find(
    a => a.key === agentPut.key,
  );
  if (!catalogAgent) {
    return `Unknown agent key: ${agentPut.key}`;
  }

  if (agentPut.temperature !== undefined) {
    const { min, max } = catalogAgent.temperature;
    if (
      min !== undefined &&
      max !== undefined &&
      (agentPut.temperature < min || agentPut.temperature > max)
    ) {
      return `temperature for ${agentPut.key} must be between ${min} and ${max}`;
    }
  }

  if (agentPut.max_output_tokens !== undefined) {
    const { min, max } = catalogAgent.max_output_tokens;
    if (
      min !== undefined &&
      max !== undefined &&
      (agentPut.max_output_tokens < min || agentPut.max_output_tokens > max)
    ) {
      return `max_output_tokens for ${agentPut.key} must be between ${min} and ${max}`;
    }
  }

  if (
    agentPut.output_detail !== undefined &&
    catalogAgent.output_detail.options &&
    !catalogAgent.output_detail.options.some(
      option => option.key === agentPut.output_detail,
    )
  ) {
    return `Invalid output_detail for ${agentPut.key}`;
  }

  return null;
}

/**
 * Applies a partial PUT /ai-config update to the in-memory store.
 * @param body - Partial PUT request body.
 * @returns Error message when validation fails; null on success.
 */
export function applyAiConfigPut(body: AiConfigPutRequest): string | null {
  if (!body.global_config && !body.organization_config && !body.tenant_config) {
    return 'At least one of global_config, organization_config, or tenant_config is required';
  }

  if (body.global_config) {
    if (body.global_config.banned_words !== undefined) {
      MOCK_AI_CONFIG_GLOBAL_STORE.banned_words =
        body.global_config.banned_words === null
          ? []
          : body.global_config.banned_words;
    }
    if (body.global_config.settings) {
      MOCK_AI_CONFIG_GLOBAL_STORE.settings =
        MOCK_AI_CONFIG_GLOBAL_STORE.settings ?? {};
      for (const settingPut of body.global_config.settings) {
        const validationError = validateGlobalSettingPut(settingPut);
        if (validationError) {
          return validationError;
        }
        MOCK_AI_CONFIG_GLOBAL_STORE.settings[settingPut.key] = settingPut.value;
      }
    }
  }

  const needsOrgTenantStore =
    !!body.organization_config || !!body.tenant_config;
  if (needsOrgTenantStore) {
    const orgId = body.organization_id;
    const tenantId = body.tenant_id;
    if (body.organization_config && !orgId) {
      return 'organization_id is required when organization_config is provided';
    }
    if (body.tenant_config && !tenantId) {
      return 'tenant_id is required when tenant_config is provided';
    }
    if (!orgId || !tenantId) {
      // Org/tenant store is keyed by both; require both when touching either.
      return 'organization_id and tenant_id are required';
    }

    const storeKey = getAiConfigStoreKey(orgId, tenantId);
    const existing = MOCK_AI_CONFIG_STORE.get(storeKey) ?? {
      organization: {},
      tenant: {},
    };

    if (body.organization_config) {
      if (body.organization_config.banned_words !== undefined) {
        existing.organization.banned_words =
          body.organization_config.banned_words === null
            ? []
            : body.organization_config.banned_words;
      }
      if (body.organization_config.agents) {
        existing.organization.agents = existing.organization.agents ?? {};
        for (const orgAgentPut of body.organization_config.agents) {
          const catalogAgent = AI_CONFIG_AGENT_CATALOG.find(
            agent => agent.key === orgAgentPut.key,
          );
          if (!catalogAgent) {
            return `Unknown agent key: ${orgAgentPut.key}`;
          }
          existing.organization.agents[orgAgentPut.key] = {
            prompt: orgAgentPut.prompt,
          };
        }
      }
    }

    if (body.tenant_config) {
      if (body.tenant_config.banned_words !== undefined) {
        existing.tenant.banned_words =
          body.tenant_config.banned_words === null
            ? []
            : body.tenant_config.banned_words;
      }
      if (body.tenant_config.agents) {
        existing.tenant.agents = existing.tenant.agents ?? {};
        for (const agentPut of body.tenant_config.agents) {
          const validationError = validateAgentPut(agentPut);
          if (validationError) {
            return validationError;
          }
          const current = existing.tenant.agents[agentPut.key] ?? {};
          existing.tenant.agents[agentPut.key] = {
            ...current,
            ...(agentPut.prompt !== undefined && { prompt: agentPut.prompt }),
            ...(agentPut.output_detail !== undefined && {
              output_detail: agentPut.output_detail,
            }),
            ...(agentPut.temperature !== undefined && {
              temperature: agentPut.temperature,
            }),
            ...(agentPut.max_output_tokens !== undefined && {
              max_output_tokens: agentPut.max_output_tokens,
            }),
          };
        }
      }
    }

    MOCK_AI_CONFIG_STORE.set(storeKey, existing);
  }

  return null;
}

/**
 * Clears requested AI config overrides from the in-memory store.
 * @param scopes - Scopes to clear.
 * @param orgId - Organization identifier (when clearing organization).
 * @param tenantId - Tenant identifier (when clearing tenant).
 */
export function deleteAiConfigFromStore(
  scopes: AiConfigScope[],
  orgId?: string,
  tenantId?: string,
): void {
  if (scopes.includes('global')) {
    MOCK_AI_CONFIG_GLOBAL_STORE = {};
  }

  const clearOrg = scopes.includes('organization');
  const clearTenant = scopes.includes('tenant');
  if (!clearOrg && !clearTenant) {
    return;
  }

  if (!orgId && !tenantId) {
    MOCK_AI_CONFIG_STORE.clear();
    return;
  }

  for (const key of [...MOCK_AI_CONFIG_STORE.keys()]) {
    const [storedOrgId, storedTenantId] = key.split(':');
    const matchesOrg = !orgId || storedOrgId === orgId;
    const matchesTenant = !tenantId || storedTenantId === tenantId;
    if (!matchesOrg || !matchesTenant) {
      continue;
    }

    const entry = MOCK_AI_CONFIG_STORE.get(key);
    if (!entry) {
      continue;
    }

    if (clearOrg) {
      entry.organization = {};
    }
    if (clearTenant) {
      entry.tenant = {};
    }

    if (
      Object.keys(entry.organization).length === 0 &&
      Object.keys(entry.tenant).length === 0
    ) {
      MOCK_AI_CONFIG_STORE.delete(key);
    } else {
      MOCK_AI_CONFIG_STORE.set(key, entry);
    }
  }
}

const MOCK_AUTH_EXPIRES_IN_SECONDS = 3600;
const MOCK_AUTH_REFRESH_TOKEN = 'mock-refresh-token-for-local-dev';
const MOCK_AUTH_TENANT_ID = 'd86ee4b0-de3d-4021-b3bf-ec3427694f6c';
const MOCK_AUTH_ORG_ID = '123e4567-e89b-42d3-a456-426614174001';

/**
 * Builds a synthetic JWT for local in-memory auth mocking.
 * Tokens are generated at runtime so static scanners do not flag hardcoded secrets.
 * @param exp - JWT `exp` claim in seconds since epoch.
 * @param payload - Additional JWT payload claims.
 * @returns Encoded JWT string for mock API responses.
 */
export function createMockJwtToken(
  exp: number,
  payload: Record<string, unknown> = {},
): string {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const encodedPayload = btoa(
    JSON.stringify({ exp, sub: 'mock-user-id-1', ...payload }),
  );
  return `${header}.${encodedPayload}.mock-signature`;
}

/**
 * Builds a mock auth/token response for the in-memory API.
 * `userData` matches a live super admin: multi-tenant, no bound tenant,
 * access to all tenants.
 * @returns {@link ExchangeCodeResponse} with dynamically generated tokens.
 */
export function buildMockAuthTokenResponse(): ExchangeCodeResponse {
  const nowInSeconds = Math.floor(Date.now() / 1000);
  const accessTokenExp = nowInSeconds + MOCK_AUTH_EXPIRES_IN_SECONDS;
  const idTokenExp = nowInSeconds + MOCK_AUTH_EXPIRES_IN_SECONDS;

  return {
    access_token: createMockJwtToken(accessTokenExp, {
      scope: 'openid accessIdentity offline_access',
      client_id: 'spyglass',
      tenantId: MOCK_AUTH_TENANT_ID,
      orgId: MOCK_AUTH_ORG_ID,
    }),
    id_token: createMockJwtToken(idTokenExp, {
      aud: 'spyglass',
      tenantId: MOCK_AUTH_TENANT_ID,
      orgId: MOCK_AUTH_ORG_ID,
      email: MOCK_USER_1_PROFILE.email,
      name: `${MOCK_USER_1_PROFILE.firstName} ${MOCK_USER_1_PROFILE.lastName}`,
      profileId: MOCK_USER_1_PROFILE.id,
    }),
    expires_in: MOCK_AUTH_EXPIRES_IN_SECONDS,
    token_type: 'Bearer',
    refresh_token: MOCK_AUTH_REFRESH_TOKEN,
    scope: 'openid accessIdentity offline_access',
    userData: {
      id: MOCK_USER_1_PROFILE.id,
      email: MOCK_USER_1_PROFILE.email,
      firstName: MOCK_USER_1_PROFILE.firstName,
      lastName: MOCK_USER_1_PROFILE.lastName,
      role: userRoles.superAdmin.id,
      multitenancy: true,
      tenantAccessMode: 'all',
      status: MOCK_USER_1_PROFILE.status,
      orgId: MOCK_AUTH_ORG_ID,
      createdAt: MOCK_USER_1_PROFILE.createdAt,
      updatedAt: MOCK_USER_1_PROFILE.updatedAt,
    },
  };
}
